/**
 * CANONICAL BOOKING DETAILS - STATE VIEWER + ALLOWED ACTIONS PANEL
 * 
 * THIS COMPONENT OBEYS THE BACKEND STATE MACHINE.
 * THE UI IS NO LONGER ALLOWED TO DECIDE LOGIC.
 * 
 * All behavior is driven ONLY by booking.status
 * No optimistic UI transitions - always refetch after actions.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { HostDirectModeBanner } from '../components/HostDirectModeBanner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { 
  ChevronLeft, Calendar as CalendarIcon, Clock, CheckCircle, CheckCircle2, AlertCircle, CreditCard, X, 
  Camera, MessageCircle, Headphones, Star, Play, AlertTriangle, FileCheck, 
  Clipboard, Upload, Image, Fuel, Key, Info, RefreshCw, Eye, FileText, Download,
  Shield, Loader2, Package, FileJson, FileArchive, Zap, CalendarPlus, Bell, Scale, Check,
  TrendingUp, DollarSign, ArrowUpRight, ArrowDownRight, Wallet, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { getVehiclePrimaryImage, handleVehicleImageError } from '../lib/imageUtils';
import PaymentCountdown from '../components/PaymentCountdown';
import SpecialtyInspectionWizard from '../components/SpecialtyInspectionWizard';
import TripConfirmationModal from '../components/TripConfirmationModal';
import { shouldUseSpecialtyTemplate, usesNonDefaultTemplate, PHASES as INSPECTION_PHASES } from '../lib/inspectionTemplates';

// ============================================================================
// P2: FINANCIAL TIMELINE COMPONENT (Read-only, Platform Owner/Admin view)
// Uses existing endpoint: GET /api/admin/financial/timeline/{booking_id}
// ============================================================================

const FinancialTimelineSection = ({ bookingId, api }) => {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchTimeline = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/financial/timeline/${bookingId}`);
      setTimeline(res.data);
    } catch (err) {
      console.error('Failed to fetch financial timeline:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded && !timeline) {
      fetchTimeline();
    }
  }, [expanded]);

  const getEventIcon = (type) => {
    const icons = {
      booking_payment: <CreditCard className="w-4 h-4 text-green-600" />,
      platform_fee: <TrendingUp className="w-4 h-4 text-purple-600" />,
      host_earning: <Wallet className="w-4 h-4 text-blue-600" />,
      bond_authorized: <Shield className="w-4 h-4 text-amber-600" />,
      bond_capture: <ArrowDownRight className="w-4 h-4 text-red-600" />,
      bond_release: <ArrowUpRight className="w-4 h-4 text-green-600" />,
      posthire_charge: <AlertCircle className="w-4 h-4 text-orange-600" />,
      processing_fee: <DollarSign className="w-4 h-4 text-slate-500" />,
      payout_created: <Clock className="w-4 h-4 text-blue-500" />,
      payout_completed: <CheckCircle className="w-4 h-4 text-green-600" />,
      claim_payment: <Scale className="w-4 h-4 text-purple-600" />,
      refund: <RefreshCw className="w-4 h-4 text-red-500" />
    };
    return icons[type] || <DollarSign className="w-4 h-4 text-slate-400" />;
  };

  const getDirectionBadge = (direction) => {
    const badges = {
      in: <Badge className="bg-green-100 text-green-700 text-xs">IN</Badge>,
      out: <Badge className="bg-blue-100 text-blue-700 text-xs">OUT</Badge>,
      hold: <Badge className="bg-amber-100 text-amber-700 text-xs">HOLD</Badge>,
      pending: <Badge className="bg-slate-100 text-slate-600 text-xs">PENDING</Badge>,
      scheduled: <Badge className="bg-purple-100 text-purple-600 text-xs">SCHEDULED</Badge>
    };
    return badges[direction] || <Badge className="bg-slate-100 text-slate-500 text-xs">{direction}</Badge>;
  };

  return (
    <Card className="border-purple-200 bg-purple-50/30">
      <CardContent className="p-4">
        <button 
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between text-left"
          data-testid="financial-timeline-toggle"
        >
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-purple-900">Financial Timeline</h3>
            <Badge className="bg-purple-100 text-purple-600 text-xs">Admin</Badge>
          </div>
          <ChevronLeft className={`w-5 h-5 text-purple-500 transition-transform ${expanded ? '-rotate-90' : ''}`} />
        </button>

        {expanded && (
          <div className="mt-4 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
              </div>
            ) : timeline ? (
              <>
                {/* Totals Summary */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 bg-white rounded-lg border border-purple-200">
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Renter Paid</p>
                    <p className="font-bold text-green-700">${timeline.totals?.renter_paid?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Host Earned</p>
                    <p className="font-bold text-blue-700">${timeline.totals?.host_earned?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Platform Revenue</p>
                    <p className="font-bold text-purple-700">${timeline.totals?.platform_revenue?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Processing Fees</p>
                    <p className="font-bold text-slate-600">${timeline.totals?.processing_fees?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Bond Captured</p>
                    <p className="font-bold text-red-600">${timeline.totals?.bond_captured?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Bond Returned</p>
                    <p className="font-bold text-green-600">${timeline.totals?.bond_returned?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>

                {/* Timeline Events */}
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {timeline.timeline?.map((event, idx) => (
                    <div 
                      key={event.event_id || idx} 
                      className="flex items-start gap-3 p-2 bg-white rounded border border-slate-100 hover:bg-slate-50"
                    >
                      <div className="flex-shrink-0 mt-1">
                        {getEventIcon(event.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-slate-800">{event.description}</span>
                          {getDirectionBadge(event.direction)}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <span className={`font-mono ${event.direction === 'in' ? 'text-green-600' : event.direction === 'out' ? 'text-blue-600' : 'text-slate-600'}`}>
                            ${(event.amount_cents / 100).toFixed(2)}
                          </span>
                          <span>•</span>
                          <span className="capitalize">{event.affects}</span>
                          {event.timestamp && (
                            <>
                              <span>•</span>
                              <span>{new Date(event.timestamp).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Sources Used */}
                <div className="text-xs text-slate-400 flex flex-wrap gap-2">
                  <span>Sources:</span>
                  {timeline.sources_used?.payment_ledger && <Badge variant="outline" className="text-[10px]">payment_ledger</Badge>}
                  {timeline.sources_used?.bond_ledger && <Badge variant="outline" className="text-[10px]">bond_ledger</Badge>}
                  {timeline.sources_used?.payment_records && <Badge variant="outline" className="text-[10px]">payment_records</Badge>}
                  {timeline.sources_used?.post_hire_charges && <Badge variant="outline" className="text-[10px]">post_hire_charges</Badge>}
                  {timeline.sources_used?.payout_calculation && <Badge variant="outline" className="text-[10px]">payout_calculation</Badge>}
                  {timeline.sources_used?.claims && <Badge variant="outline" className="text-[10px]">claims</Badge>}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">No financial data available</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// CANONICAL STATUS CONFIGURATION - SINGLE SOURCE OF TRUTH
// Host-facing labels with clear descriptions and next-step guidance
// ============================================================================

const CANONICAL_STATUSES = {
  requested: {
    label: 'Awaiting Your Approval',
    color: 'bg-yellow-100 text-yellow-800',
    description: 'Review the request and approve or decline.',
    hostHelperText: 'A renter has requested to book your vehicle.',
    renterHelperText: 'Waiting for the host to review your request.'
  },
  approved_pending_payment: {
    label: 'Awaiting Renter Payment',
    color: 'bg-blue-100 text-blue-800',
    description: 'You approved this booking. Waiting for the renter to pay.',
    hostHelperText: 'No action needed. The renter has been notified to complete payment.',
    renterHelperText: 'Complete payment to confirm your booking.'
  },
  paid: {
    label: 'Payment Received',
    color: 'bg-green-100 text-green-800',
    description: 'Payment complete. Ready for pre-hire inspection.',
    hostHelperText: 'Complete the pre-hire checklist before handing over the vehicle.',
    renterHelperText: 'Payment confirmed. Wait for the host to prepare your vehicle.'
  },
  prehire_in_progress: {
    label: 'Pre-Hire In Progress',
    color: 'bg-amber-100 text-amber-800',
    description: 'Host is completing the handover inspection.',
    hostHelperText: 'Finish documenting the vehicle condition with photos before handover.',
    renterHelperText: 'The host is preparing your vehicle for pickup.'
  },
  prehire_submitted: {
    label: 'Ready for Pickup',
    color: 'bg-indigo-100 text-indigo-800',
    description: 'Pre-hire complete. Vehicle is ready.',
    hostHelperText: 'Hand over the vehicle when the renter arrives. They will start the trip.',
    renterHelperText: 'Vehicle is ready! Start your trip when you pick up.'
  },
  active: {
    label: 'Trip Active',
    color: 'bg-emerald-100 text-emerald-800',
    description: 'Trip is in progress.',
    hostHelperText: 'Complete the return checklist when the vehicle is returned.',
    renterHelperText: 'Enjoy your trip! Return the vehicle on time.'
  },
  trip_active: {
    label: 'Trip Active',
    color: 'bg-emerald-100 text-emerald-800',
    description: 'Trip is in progress.',
    hostHelperText: 'Complete the return checklist when the vehicle is returned.',
    renterHelperText: 'Enjoy your trip! Return the vehicle on time.'
  },
  posthire_in_progress: {
    label: 'Return Checklist In Progress',
    color: 'bg-orange-100 text-orange-800',
    description: 'Host is documenting the vehicle return condition.',
    hostHelperText: 'Complete all return photos to protect your claim eligibility.',
    renterHelperText: 'The host is inspecting the returned vehicle.'
  },
  posthire_submitted: {
    label: 'Review Period Active',
    color: 'bg-purple-100 text-purple-800',
    description: 'Return checklist complete. Review period has started.',
    hostHelperText: 'Claims or disputes can be submitted during this period.',
    renterHelperText: 'Inspection complete. Review period is active.'
  },
  cooldown_window_open: {
    label: 'Review Period Active',
    color: 'bg-purple-100 text-purple-800',
    description: 'Both parties can submit claims during this window.',
    hostHelperText: 'If you found damage, submit a claim before the review period ends.',
    renterHelperText: 'Any issues? You can submit a dispute during this period.'
  },
  claim_submitted: {
    label: 'Claim Under Review',
    color: 'bg-orange-100 text-orange-800',
    description: 'Additional charges submitted for review.',
    hostHelperText: 'The renter has been notified. Waiting for response or admin review.',
    renterHelperText: 'The host has submitted additional charges. Review and respond.'
  },
  disputed: {
    label: 'Dispute Under Review',
    color: 'bg-amber-100 text-amber-800',
    description: 'A dispute is being reviewed by admin.',
    hostHelperText: 'Admin will review the evidence and make a decision.',
    renterHelperText: 'Admin is reviewing the dispute.'
  },
  completed: {
    label: 'Completed',
    color: 'bg-slate-100 text-slate-800',
    description: 'Booking successfully completed.',
    hostHelperText: 'All inspections done. Payout will be processed.',
    renterHelperText: 'Trip completed successfully. Thank you for booking!'
  },
  cancelled: {
    label: 'Cancelled',
    color: 'bg-red-100 text-red-800',
    description: 'This booking was cancelled.',
    hostHelperText: 'No further action required.',
    renterHelperText: 'This booking was cancelled.'
  },
  approval_expired: {
    label: 'Booking Expired',
    color: 'bg-slate-100 text-slate-800',
    description: 'Payment was not completed in time.',
    hostHelperText: 'This booking has expired. Your dates have been released.',
    renterHelperText: 'This booking expired due to non-payment.'
  }
};

// ============================================================================
// "WHAT HAPPENS NEXT" STRIP - UX Clarity Enhancement
// Provides clear guidance on who needs to act and what happens next
// ============================================================================
const WHAT_HAPPENS_NEXT = {
  requested: {
    whoActsNext: 'Host',
    hostAction: 'Review and respond to this booking request.',
    renterAction: 'Waiting for the host to respond.'
  },
  approved_pending_payment: {
    whoActsNext: 'Renter',
    hostAction: 'Waiting for the renter to pay.',
    renterAction: 'Complete payment to confirm your booking.'
  },
  paid: {
    whoActsNext: 'Host',
    hostAction: 'Complete the handover checklist before pickup.',
    renterAction: 'The host is preparing your vehicle.'
  },
  prehire_in_progress: {
    whoActsNext: 'Host',
    hostAction: 'Finish the handover checklist.',
    renterAction: 'Almost ready — the host is documenting the vehicle.'
  },
  prehire_submitted: {
    whoActsNext: 'Renter',
    hostAction: 'Hand over the keys when the renter arrives.',
    renterAction: 'Tap "Start Trip" when you collect the vehicle.'
  },
  active: {
    whoActsNext: 'Host at return',
    hostAction: 'Complete the return checklist when the vehicle comes back.',
    renterAction: 'Enjoy your trip! Return the vehicle on time.'
  },
  trip_active: {
    whoActsNext: 'Host at return',
    hostAction: 'Complete the return checklist when the vehicle comes back.',
    renterAction: 'Enjoy your trip! Return the vehicle on time.'
  },
  posthire_in_progress: {
    whoActsNext: 'Host',
    hostAction: 'Finish the return checklist to complete the booking.',
    renterAction: 'The host is inspecting the returned vehicle.'
  },
  posthire_submitted: {
    whoActsNext: 'Renter or auto',
    hostAction: 'Waiting for the review period to end. Submit a claim if needed.',
    renterAction: 'Review any charges and accept or dispute them.'
  },
  cooldown_window_open: {
    whoActsNext: 'Optional',
    hostAction: 'Submit a claim now if you found any issues.',
    renterAction: 'Review charges if any. The booking will auto-complete soon.'
  },
  claim_submitted: {
    whoActsNext: 'Renter',
    hostAction: 'Waiting for the renter to respond to your claim.',
    renterAction: 'Review the claim and accept or dispute.'
  },
  disputed: {
    whoActsNext: 'HireHub team',
    hostAction: 'We\'re reviewing this dispute. We may contact you.',
    renterAction: 'We\'re reviewing this dispute. We may contact you.'
  },
  completed: {
    whoActsNext: 'Done',
    hostAction: 'All done! Your payout is being processed.',
    renterAction: 'Trip complete! Consider leaving a review.'
  },
  cancelled: {
    whoActsNext: 'Done',
    hostAction: 'This booking was cancelled.',
    renterAction: 'This booking was cancelled.'
  },
  approval_expired: {
    whoActsNext: 'Done',
    hostAction: 'This booking expired. Your dates are available again.',
    renterAction: 'This booking expired. You can submit a new request.'
  }
};

// Legacy status mapping for backwards compatibility (read-only)
const LEGACY_STATUS_MAP = {
  'pending': 'requested',
  'accepted': 'approved_pending_payment',
  'captured': 'paid',
  'confirmed': 'paid',
  'in_progress': 'active',
  'returning': 'posthire_in_progress',
  'declined': 'cancelled',
  'expired': 'approval_expired'  // P1 FIX
};

// ============================================================================
// PHASE 4 — CATEGORY-AWARE WORDING POLISH (DISPLAY-ONLY)
// Local helper that returns category-specific labels used to swap generic
// "trip / vehicle / driver" wording at render time. Cars are the explicit
// baseline (every value matches the existing copy) so the car flow renders
// byte-for-byte unchanged. No backend / DB / status / lifecycle change.
// ============================================================================
const BOOKING_CATEGORY_COPY = {
  car: {
    activity: 'trip', activityCap: 'Trip',
    asset: 'vehicle', assetCap: 'Vehicle',
    operator: 'driver', operatorCap: 'Driver',
    startActionLabel: 'Start Trip',
  },
  motorcycle: {
    activity: 'ride', activityCap: 'Ride',
    asset: 'motorcycle', assetCap: 'Motorcycle',
    operator: 'rider', operatorCap: 'Rider',
    startActionLabel: 'Start Ride',
  },
  trailer: {
    activity: 'tow', activityCap: 'Tow',
    asset: 'trailer', assetCap: 'Trailer',
    operator: 'operator', operatorCap: 'Operator',
    startActionLabel: 'Start Tow',
  },
  boat: {
    activity: 'hire', activityCap: 'Hire',
    asset: 'vessel', assetCap: 'Vessel',
    operator: 'skipper', operatorCap: 'Skipper',
    startActionLabel: 'Start Hire',
  },
  jetski: {
    activity: 'ride', activityCap: 'Ride',
    asset: 'PWC', assetCap: 'PWC',
    operator: 'rider', operatorCap: 'Rider',
    startActionLabel: 'Start Ride',
  },
  ebike: {
    activity: 'ride', activityCap: 'Ride',
    asset: 'eBike', assetCap: 'eBike',
    operator: 'rider', operatorCap: 'Rider',
    startActionLabel: 'Start Ride',
  },
  electric_scooter: {
    activity: 'ride', activityCap: 'Ride',
    asset: 'scooter', assetCap: 'Scooter',
    operator: 'rider', operatorCap: 'Rider',
    startActionLabel: 'Start Ride',
  },
  mobility_scooter: {
    activity: 'hire', activityCap: 'Hire',
    asset: 'scooter', assetCap: 'Scooter',
    operator: 'operator', operatorCap: 'Operator',
    startActionLabel: 'Start Hire',
  },
  specialty_asset: {
    activity: 'hire', activityCap: 'Hire',
    asset: 'asset', assetCap: 'Asset',
    operator: 'operator', operatorCap: 'Operator',
    startActionLabel: 'Start Hire',
  },
};

const getBookingCategoryCopy = (vehicle_category) => {
  return BOOKING_CATEGORY_COPY[vehicle_category] || BOOKING_CATEGORY_COPY.car;
};

// Whole-word substitution. For cars every replacement is identity, so the
// existing wording is preserved byte-for-byte. Pronouns, verbs, and any other
// non-mapped words are untouched.
const applyCategoryCopy = (text, copy) => {
  if (!text || !copy) return text;
  return String(text)
    .replace(/\bTrip\b/g, copy.activityCap)
    .replace(/\btrip\b/g, copy.activity)
    .replace(/\bVehicle\b/g, copy.assetCap)
    .replace(/\bvehicle\b/g, copy.asset);
};

// ============================================================================
// STATUS → ALLOWED ACTIONS MAPPING
// ============================================================================

const getStatusActions = (status, isHost, isRenter, paymentMode = 'platform') => {
  const isHostDirect = paymentMode === 'host_direct';
  const actions = {
    requested: {
      host: ['approve', 'decline'],
      renter: ['cancel']
    },
    approved_pending_payment: {
      // PHASE B: In host-direct mode the host confirms payment manually;
      //         renter has no "Pay Now" button (payment is off-platform).
      host: isHostDirect
        ? ['mark_payment_received', 'cancel_unpaid_hold']
        : ['cancel_unpaid_hold'],
      renter: isHostDirect
        ? ['cancel']
        : ['pay', 'cancel']
    },
    paid: {
      host: ['start_prehire'],
      renter: ['cancel']
    },
    prehire_in_progress: {
      host: ['continue_prehire', 'submit_prehire'],
      renter: []
    },
    prehire_submitted: {
      host: [],
      renter: ['start_trip']
    },
    active: {
      host: ['start_posthire'],
      renter: ['request_extension']
    },
    trip_active: {
      host: ['start_posthire'],
      renter: ['request_extension']
    },
    posthire_in_progress: {
      host: ['continue_posthire', 'submit_posthire'],
      renter: []
    },
    posthire_submitted: {
      host: [],
      renter: ['accept_charges', 'dispute_charges']
    },
    // P0 FIX: Cooldown window allows renter to accept/dispute charges
    cooldown_window_open: {
      host: [],
      renter: ['accept_charges', 'dispute_charges']
    },
    completed: {
      host: [],
      renter: ['leave_review']
    },
    cancelled: {
      host: [],
      renter: []
    },
    approval_expired: {  // P1 FIX: Add expired status
      host: [],
      renter: []
    }
  };
  
  const statusActions = actions[status] || { host: [], renter: [] };
  if (isHost) return statusActions.host;
  if (isRenter) return statusActions.renter;
  return [];
};

// ============================================================================
// PRE-HIRE CHECKLIST WIZARD COMPONENT - WITH REAL S3 UPLOADS
// ============================================================================

const PreHireChecklistWizard = React.forwardRef(({ booking, onComplete, onCancel }, ref) => {
  const { api } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [renterInfo, setRenterInfo] = useState(null);
  const [renterVerification, setRenterVerification] = useState(null);
  const [verificationImageModal, setVerificationImageModal] = useState({ open: false, url: null, type: null });
  const [savingDraft, setSavingDraft] = useState(false);
  
  // P0 CRITICAL FIX: Track initial data loading to prevent autosave overwriting server data
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  
  // P0 CANONICAL FIX: Store thumbnail URLs for ALL photo types for immediate display
  const [photoThumbnails, setPhotoThumbnails] = useState({
    exterior: [],      // Array of {s3_key, thumbnail_url, localPreview?}
    interior: [],
    odometer: null,    // Single {s3_key, thumbnail_url}
    fuel: null,
    damage: [],
    licence_front: null,
    licence_back: null,
    licence_selfie: null,
    international_permit: null
  });
  
  const [data, setData] = useState({
    exterior_photos: [],
    interior_photos: [],
    odometer_photo: '',
    odometer_value: '',
    fuel_photo: '',
    fuel_level: 'full',
    keys_count: '2',
    damage_items: [],
    notes: '',
    // P0 EXTENSION: prehire_meta for new steps 5-17
    prehire_meta: {
      renter_present: false,
      identity_confirmed: false,
      licence_front: '',
      licence_back: '',
      licence_selfie: '',
      additional_drivers: [],
      has_additional_drivers: null,
      has_international_licence: null,
      international_docs: [],
      licence_class: '',
      vehicle_explained: false,
      fuel_type: '',
      fuel_policy: '',
      km_allowance: '',
      extra_km_rate: '',
      toll_policy: '',
      handover_acknowledgements: {
        height_restrictions: false,
        emergency_equipment: false,
        servicing_equipment: false,
        dashboard_warnings: false,
        breakdown_procedure: false,
        accident_procedure: false,
        cleaning_expectations: false,
        smoking_policy: false
      },
      final_declaration: false
    }
  });
  const [errors, setErrors] = useState({});

  const FUEL_LEVELS = ['empty', '1/4', '1/2', '3/4', 'full'];
  const REQUIRED_EXTERIOR = 6;
  const REQUIRED_INTERIOR = 6;
  const TOTAL_STEPS = 17;

  // ========================================================
  // P0 FIX 3: SAFE ERROR NORMALIZER - Never render objects
  // ========================================================
  // ========================================================
  // P0 FIX: Improved error normalization with deduplication
  // Prevents "Input should be a valid string" spam toasts
  // ========================================================
  const normalizeApiError = (err) => {
    // If it's already a string, return it
    if (typeof err === 'string') return err;
    
    // Handle axios error response
    if (err?.response?.data) {
      const data = err.response.data;
      
      // If data is a string, return it
      if (typeof data === 'string') return data;
      
      // If data.detail exists
      if (data.detail !== undefined) {
        const detail = data.detail;
        
        // If detail is a string, return it
        if (typeof detail === 'string') return detail;
        
        // If detail is an array (Pydantic validation errors)
        if (Array.isArray(detail)) {
          // Extract unique messages only
          const uniqueMessages = new Set();
          detail.forEach(item => {
            let msg = '';
            if (typeof item === 'string') {
              msg = item;
            } else if (item?.msg) {
              msg = item.msg;
            } else if (item?.message) {
              msg = item.message;
            }
            if (msg) uniqueMessages.add(msg);
          });
          
          // If there are many identical errors, show a clean summary
          if (uniqueMessages.size === 1 && detail.length > 1) {
            const singleMsg = Array.from(uniqueMessages)[0];
            // Log full error for debugging
            return `Validation error: ${singleMsg}`;
          }
          
          // If more than 3 unique errors, summarize
          if (uniqueMessages.size > 3) {
            return 'Some fields are invalid. Please review highlighted items.';
          }
          
          return Array.from(uniqueMessages).join('; ');
        }
        
        // If detail is an object, stringify it
        if (typeof detail === 'object') {
          return JSON.stringify(detail);
        }
      }
      
      // Fallback: stringify the data
      return JSON.stringify(data);
    }
    
    // Handle standard Error object
    if (err?.message) return err.message;
    
    // Fallback
    return 'Something went wrong';
  };

  // ========================================================
  // P0 FIX: SAFE STRING HELPER - Prevents crash on undefined
  // ========================================================
  const safeStartsWith = (value, prefix) => 
    (typeof value === 'string') ? value.startsWith(prefix) : false;

  // ========================================================
  // P0 FIX 1: PARALLEL UPLOAD WITH LOCAL PREVIEWS
  // ========================================================
  // Concurrent upload pool with limit
  const uploadWithLimit = async (files, limit, uploadFn) => {
    const results = [];
    const executing = [];
    
    for (const file of files) {
      const promise = uploadFn(file).then(result => {
        executing.splice(executing.indexOf(promise), 1);
        return { status: 'fulfilled', value: result, file };
      }).catch(error => {
        executing.splice(executing.indexOf(promise), 1);
        return { status: 'rejected', reason: error, file };
      });
      
      results.push(promise);
      executing.push(promise);
      
      if (executing.length >= limit) {
        await Promise.race(executing);
      }
    }
    
    return Promise.all(results);
  };

  // P0 CANONICAL: Fetch signed URL for thumbnail
  const fetchSignedUrl = async (s3Key) => {
    if (!s3Key || !booking?.booking_id) return null;
    try {
      const encodedKey = encodeURIComponent(s3Key);
      const response = await api.get(`/uploads/checklists/${booking.booking_id}/${encodedKey}`);
      return response.data?.url || null;
    } catch (error) {
      return null;
    }
  };

  // ========================================================
  // P0 FIX 2: LOAD EXISTING DATA ON MOUNT (RESUME SUPPORT)
  // ========================================================
  useEffect(() => {
    const loadExistingData = async () => {
      if (!booking?.booking_id) return;
      
      try {
        // Load existing prehire checklist data (for resume)
        const checklistRes = await api.get(`/bookings/${booking.booking_id}/prehire`);
        const existingChecklist = checklistRes.data?.checklist;
        
        if (existingChecklist) {
          
          // P0 LEGAL RECORD: Resume at saved step if available
          if (existingChecklist.current_step && existingChecklist.current_step > 1) {
            setStep(existingChecklist.current_step);
          }
          
          // Hydrate data state with saved values
          setData(prev => {
            const serverMeta = existingChecklist.prehire_meta || {};
            // Deep merge handover_acknowledgements to prevent null crash
            const mergedAcks = {
              ...prev.prehire_meta.handover_acknowledgements,
              ...(serverMeta.handover_acknowledgements || {})
            };
            // P0 FIX: Filter out null/invalid values from photo arrays
            const safeExterior = (existingChecklist.exterior_photos || [])
              .filter(k => typeof k === 'string' && k.length > 0);
            const safeInterior = (existingChecklist.interior_photos || [])
              .filter(k => typeof k === 'string' && k.length > 0);
            const safeTyrePhotos = (existingChecklist.tyre_photos || [])
              .filter(k => typeof k === 'string' && k.length > 0);
            return {
              ...prev,
              odometer_value: existingChecklist.odometer_value?.toString() || '',
              fuel_level: existingChecklist.fuel_level || 'full',
              keys_count: existingChecklist.keys_count?.toString() || '2',
              notes: existingChecklist.notes || '',
              // Also hydrate photo s3_keys if they exist in checklist - WITH NULL FILTERING
              odometer_photo: existingChecklist.odometer_photo || prev.odometer_photo || '',
              fuel_photo: existingChecklist.fuel_photo || prev.fuel_photo || '',
              exterior_photos: safeExterior.length > 0 ? safeExterior : prev.exterior_photos,
              interior_photos: safeInterior.length > 0 ? safeInterior : prev.interior_photos,
              // P0 LEGAL RECORD: Hydrate tyre photos
              tyre_photos: safeTyrePhotos.length > 0 ? safeTyrePhotos : prev.tyre_photos || [],
              // Hydrate prehire_meta with deep merge for nested objects
              prehire_meta: {
                ...prev.prehire_meta,
                ...serverMeta,
                handover_acknowledgements: mergedAcks
              }
            };
          });
        }
      } catch (error) {
        // No existing checklist, that's fine
      }
      
      // Load existing photos - FETCH ALL PHOTO TYPES FROM SERVER
      try {
        const response = await api.get(`/uploads/checklists/${booking.booking_id}/photos`, {
          params: { checklist_type: 'prehire' }
        });
        
        const photos = response.data?.photos || [];
        
        // Group photos by type - capture ALL types dynamically
        const photosByType = {};
        photos.forEach(p => {
          const t = p.photo_type || 'unknown';
          if (!photosByType[t]) photosByType[t] = [];
          photosByType[t].push(p);
        });
        
        // Extract specific types with proper defaults
        const exterior = photosByType['exterior'] || [];
        const interior = photosByType['interior'] || [];
        const odometer = photosByType['odometer']?.[0] || null;
        const fuel = photosByType['fuel']?.[0] || null;
        const damage = photosByType['damage'] || [];
        const licenceFront = photosByType['licence_front']?.[0] || null;
        const licenceBack = photosByType['licence_back']?.[0] || null;
        const licenceSelfie = photosByType['licence_selfie']?.[0] || null;
        const internationalPermit = photosByType['international_permit']?.[0] || null;
        
        // Update data with s3_keys - ONLY if checklist didn't have them
        // P0 FIX: Photos endpoint returns ALL uploaded photos, but we should only use
        // the photos that are actually part of the checklist (from first setData call)
        // Only update if the checklist didn't have photo arrays (fallback for legacy data)
        setData(prev => {
          // If checklist already has photos, DON'T overwrite with photos endpoint data
          const useExteriorFromPhotos = !prev.exterior_photos || prev.exterior_photos.length === 0;
          const useInteriorFromPhotos = !prev.interior_photos || prev.interior_photos.length === 0;
          
          return {
            ...prev,
            exterior_photos: useExteriorFromPhotos && exterior.length > 0 
              ? exterior.map(p => p.s3_key).filter(k => typeof k === 'string' && k.length > 0) 
              : prev.exterior_photos,
            interior_photos: useInteriorFromPhotos && interior.length > 0 
              ? interior.map(p => p.s3_key).filter(k => typeof k === 'string' && k.length > 0) 
              : prev.interior_photos,
            odometer_photo: odometer?.s3_key || prev.odometer_photo || '',
            fuel_photo: fuel?.s3_key || prev.fuel_photo || '',
            prehire_meta: {
              ...prev.prehire_meta,
              licence_front: licenceFront?.s3_key || prev.prehire_meta?.licence_front || '',
              licence_back: licenceBack?.s3_key || prev.prehire_meta?.licence_back || '',
              licence_selfie: licenceSelfie?.s3_key || prev.prehire_meta?.licence_selfie || '',
              international_permit: internationalPermit?.s3_key || prev.prehire_meta?.international_permit || ''
            }
          };
        });
        
        // Update thumbnails - preserve existing and add new from server
        setPhotoThumbnails(prev => ({
          ...prev,
          exterior: exterior.length > 0 ? exterior.map(p => ({ s3_key: p.s3_key, thumbnail_url: p.thumbnail_url })) : prev.exterior,
          interior: interior.length > 0 ? interior.map(p => ({ s3_key: p.s3_key, thumbnail_url: p.thumbnail_url })) : prev.interior,
          odometer: odometer ? { s3_key: odometer.s3_key, thumbnail_url: odometer.thumbnail_url } : prev.odometer,
          fuel: fuel ? { s3_key: fuel.s3_key, thumbnail_url: fuel.thumbnail_url } : prev.fuel,
          damage: damage.length > 0 ? damage.map(p => ({ s3_key: p.s3_key, thumbnail_url: p.thumbnail_url })) : prev.damage,
          licence_front: licenceFront ? { s3_key: licenceFront.s3_key, thumbnail_url: licenceFront.thumbnail_url } : prev.licence_front,
          licence_back: licenceBack ? { s3_key: licenceBack.s3_key, thumbnail_url: licenceBack.thumbnail_url } : prev.licence_back,
          licence_selfie: licenceSelfie ? { s3_key: licenceSelfie.s3_key, thumbnail_url: licenceSelfie.thumbnail_url } : prev.licence_selfie,
          international_permit: internationalPermit ? { s3_key: internationalPermit.s3_key, thumbnail_url: internationalPermit.thumbnail_url } : prev.international_permit
        }));
        
      } catch (error) {
      }
      
      // P0 CRITICAL: Mark initial load complete AFTER all data is loaded
      // This prevents autosave from overwriting server data with empty arrays
      setIsInitialLoadComplete(true);
    };
    
    loadExistingData();
  }, [booking?.booking_id, api]);

  // Fetch renter info and verification images on mount
  useEffect(() => {
    const fetchRenterData = async () => {
      try {
        // Fetch renter identity via the new secure endpoint
        const identityRes = await api.get(`/bookings/${booking.booking_id}/renter-identity`);
        setRenterInfo(identityRes.data);
        
        // Fetch renter verification images (presigned URLs)
        const verificationRes = await api.get(`/bookings/${booking.booking_id}/renter-verification`);
        setRenterVerification(verificationRes.data);
      } catch (error) {
        console.error('Failed to fetch renter data:', error);
        // Fallback to basic user info if new endpoints fail
        try {
          const fallbackRes = await api.get(`/users/${booking.customer_id}`);
          setRenterInfo({
            renter_full_name: fallbackRes.data.full_name,
            renter_email: fallbackRes.data.email,
            renter_phone: fallbackRes.data.phone,
            verification_status: fallbackRes.data.verification_status
          });
        } catch (fallbackError) {
          console.error('Fallback renter fetch also failed:', fallbackError);
        }
      }
    };
    if (booking?.booking_id) {
      fetchRenterData();
    }
  }, [booking?.booking_id, api]);

  // Calculate progress percentage
  const progressPercent = Math.round((step / TOTAL_STEPS) * 100);

  // P0 CANONICAL FIX: Real S3 upload via backend proxy with thumbnail support
  // Returns {s3_key, thumbnail_key, thumbnail_url, photo_id} for immediate thumbnail display
  const uploadFileToS3 = async (file, photoType) => {
    try {
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('booking_id', booking.booking_id);
      formData.append('checklist_type', 'prehire');
      formData.append('photo_type', photoType);
      
      const uploadResponse = await api.post('/uploads/checklists/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      const { s3_key, thumbnail_key, photo_id, file_size, original_size } = uploadResponse.data;
      
      // P0 CANONICAL: Immediately fetch signed URL for thumbnail
      let thumbnail_url = null;
      if (thumbnail_key) {
        thumbnail_url = await fetchSignedUrl(thumbnail_key);
      }
      if (!thumbnail_url && s3_key) {
        thumbnail_url = await fetchSignedUrl(s3_key);
      }
      
      return { s3_key, thumbnail_key, thumbnail_url, photo_id };
    } catch (error) {
      console.error('[S3 Upload] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const detail = error.response?.data?.detail || error.message;
      if (detail.includes('Empty file')) {
        throw new Error('Photo capture failed - please try again');
      } else if (detail.includes('HEIC') || detail.includes('heif')) {
        throw new Error('iPhone photo format not supported - try PNG or JPG');
      } else if (detail.includes('too large')) {
        throw new Error('Photo too large (max 50MB)');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Upload timed out - check your connection');
      }
      
      throw error;
    }
  };

  // P0 FIX 1: Handle multiple photo uploads with PARALLEL uploads + LOCAL PREVIEWS
  const handlePhotoUpload = async (files, type) => {
    const photoType = type === 'exterior_photos' ? 'exterior' : 'interior';
    const thumbKey = photoType;
    const fileArray = Array.from(files);
    
    if (fileArray.length === 0) return;
    
    // Step 1: IMMEDIATELY show local previews for all files
    const localPreviews = fileArray.map((file, idx) => ({
      s3_key: `pending_${Date.now()}_${idx}`,
      thumbnail_url: URL.createObjectURL(file),
      isLocalPreview: true,
      isUploading: true,
      file
    }));
    
    // Add local previews to thumbnails immediately
    setPhotoThumbnails(prev => ({
      ...prev,
      [thumbKey]: [...prev[thumbKey], ...localPreviews]
    }));
    
    // Add placeholder s3_keys to data
    setData(prev => ({
      ...prev,
      [type]: [...prev[type], ...localPreviews.map(p => p.s3_key)]
    }));
    
    setLoading(true);
    
    // Step 2: Upload files in parallel with concurrency limit of 3
    const uploadSingleFile = async (file) => {
      const result = await uploadFileToS3(file, photoType);
      return result;
    };
    
    const results = await uploadWithLimit(fileArray, 3, uploadSingleFile);
    
    // Step 3: Process results and update state
    const successful = [];
    const failedIndices = [];
    
    results.forEach((result, idx) => {
      if (result.status === 'fulfilled') {
        successful.push({ ...result.value, originalIndex: idx });
      } else {
        failedIndices.push(idx);
        const errorMsg = normalizeApiError(result.reason);
        toast.error(`Failed to upload ${fileArray[idx].name}: ${errorMsg}`);
      }
    });
    
    // Clean up local preview URLs for successful uploads
    localPreviews.forEach(preview => {
      if (preview.thumbnail_url && preview.isLocalPreview) {
        URL.revokeObjectURL(preview.thumbnail_url);
      }
    });
    
    // Update data with real s3_keys (remove pending, add real ones)
    setData(prev => {
      const existingPhotos = (prev[type] || []).filter(key => !safeStartsWith(key, 'pending_'));
      return {
        ...prev,
        [type]: [...existingPhotos, ...successful.map(s => s.s3_key)]
      };
    });
    
    // Update thumbnails with real URLs (remove pending, add real ones)
    setPhotoThumbnails(prev => {
      const existingThumbs = prev[thumbKey].filter(t => !t.isLocalPreview);
      return {
        ...prev,
        [thumbKey]: [...existingThumbs, ...successful.map(s => ({ 
          s3_key: s.s3_key, 
          thumbnail_url: s.thumbnail_url 
        }))]
      };
    });
    
    setLoading(false);
    
    if (successful.length > 0) {
      toast.success(`Uploaded ${successful.length} photo(s)`);
    }
  };

  // P0 CANONICAL: Handle single photo upload with thumbnail (odometer, fuel)
  const handleSinglePhotoUpload = async (file, field) => {
    const photoType = field.replace('_photo', '');
    
    setLoading(true);
    setUploadProgress(prev => ({ ...prev, [field]: 'uploading' }));
    
    try {
      const result = await uploadFileToS3(file, photoType);
      setData(prev => ({ ...prev, [field]: result.s3_key }));
      
      // P0 CANONICAL: Update thumbnail state
      setPhotoThumbnails(prev => ({
        ...prev,
        [photoType]: { s3_key: result.s3_key, thumbnail_url: result.thumbnail_url }
      }));
      
      setUploadProgress(prev => ({ ...prev, [field]: 'done' }));
      toast.success(`${photoType} photo uploaded`);
    } catch (error) {
      setUploadProgress(prev => ({ ...prev, [field]: 'error' }));
      const errorMsg = normalizeApiError(error);
      toast.error(`Failed to upload ${photoType} photo: ${errorMsg}`);
      console.error(`[Upload] Failed for ${photoType}:`, error);
    } finally {
      setLoading(false);
    }
  };

  // File input ref for triggering file picker
  const exteriorInputRef = React.useRef(null);
  const interiorInputRef = React.useRef(null);
  const odometerInputRef = React.useRef(null);
  const fuelInputRef = React.useRef(null);
  // P0 EXTENSION: Additional refs for licence uploads
  const licenceFrontRef = React.useRef(null);
  const licenceBackRef = React.useRef(null);
  const licenceSelfieRef = React.useRef(null);
  const internationalDocRef = React.useRef(null);
  const additionalDriverRefs = React.useRef({});

  // P0 CANONICAL: Handle licence photo upload with thumbnail preview
  const handleLicenceUpload = async (file, field) => {
    setLoading(true);
    setUploadProgress(prev => ({ ...prev, [field]: 'uploading' }));
    
    try {
      const result = await uploadFileToS3(file, field);
      
      // Store the s3_key in data
      setData(prev => ({
        ...prev,
        prehire_meta: { ...prev.prehire_meta, [field]: result.s3_key }
      }));
      
      // P0 CANONICAL: Update thumbnail state
      setPhotoThumbnails(prev => ({
        ...prev,
        [field]: { s3_key: result.s3_key, thumbnail_url: result.thumbnail_url }
      }));
      
      setUploadProgress(prev => ({ ...prev, [field]: 'done' }));
      toast.success(`${field.replace(/_/g, ' ')} uploaded`);
    } catch (error) {
      setUploadProgress(prev => ({ ...prev, [field]: 'error' }));
      toast.error(`Failed to upload: ${normalizeApiError(error)}`);
    } finally {
      setLoading(false);
    }
  };

  // P0 EXTENSION: Handle additional driver licence upload
  const handleAdditionalDriverUpload = async (file, driverIndex, field) => {
    const key = `driver_${driverIndex}_${field}`;
    setLoading(true);
    setUploadProgress(prev => ({ ...prev, [key]: 'uploading' }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('booking_id', booking.booking_id);
      formData.append('checklist_type', 'prehire');
      formData.append('photo_type', `additional_driver_${driverIndex}_${field}`);
      
      const response = await api.post('/uploads/checklists/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      
      const { s3_key } = response.data;
      setData(prev => {
        const drivers = [...prev.prehire_meta.additional_drivers];
        if (!drivers[driverIndex]) {
          drivers[driverIndex] = { name: '', licence_front_key: '', licence_back_key: '' };
        }
        drivers[driverIndex][field === 'front' ? 'licence_front_key' : 'licence_back_key'] = s3_key;
        return {
          ...prev,
          prehire_meta: { ...prev.prehire_meta, additional_drivers: drivers }
        };
      });
      setUploadProgress(prev => ({ ...prev, [key]: 'done' }));
      toast.success('Licence photo uploaded');
    } catch (error) {
      setUploadProgress(prev => ({ ...prev, [key]: 'error' }));
      toast.error(`Failed to upload: ${normalizeApiError(error)}`);
    } finally {
      setLoading(false);
    }
  };

  // P0 EXTENSION: Handle international doc upload
  const handleInternationalDocUpload = async (file) => {
    setLoading(true);
    setUploadProgress(prev => ({ ...prev, international_doc: 'uploading' }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('booking_id', booking.booking_id);
      formData.append('checklist_type', 'prehire');
      formData.append('photo_type', 'international_permit');
      
      const response = await api.post('/uploads/checklists/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      
      const { s3_key, thumbnail_url } = response.data;
      setData(prev => ({
        ...prev,
        prehire_meta: {
          ...prev.prehire_meta,
          international_docs: [...(prev.prehire_meta?.international_docs || []), s3_key],
          international_permit: s3_key
        }
      }));
      // Update thumbnail for display
      setPhotoThumbnails(prev => ({
        ...prev,
        international_permit: { s3_key, thumbnail_url }
      }));
      setUploadProgress(prev => ({ ...prev, international_doc: 'done' }));
      toast.success('International document uploaded');
    } catch (error) {
      setUploadProgress(prev => ({ ...prev, international_doc: 'error' }));
      toast.error(`Failed to upload: ${normalizeApiError(error)}`);
    } finally {
      setLoading(false);
    }
  };

  // ========================================================
  // P0 FIX 2: INCREMENTAL SAVE DRAFT (auto-save on step change)
  // ========================================================
  // P0 BUGFIX: Numeric field normalization to prevent 422 errors
  // Converts empty strings to null, valid strings to numbers
  // ========================================================
  const normalizeNumber = (value, isInteger = false) => {
    if (value === "" || value === undefined || value === null) return null;
    const parsed = isInteger ? parseInt(value, 10) : parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  };

  // P0 BUGFIX: Convert empty strings to null for optional string fields
  const normalizeString = (value) => {
    if (value === "" || value === undefined) return null;
    return value;
  };

  // P0 BUGFIX: Filter out incomplete additional drivers to prevent validation errors
  const normalizeAdditionalDrivers = (drivers) => {
    if (!drivers || !Array.isArray(drivers)) return null;
    // Filter out drivers that don't have all required fields filled
    const validDrivers = drivers.filter(driver => 
      driver && 
      driver.name && driver.name.trim() !== '' &&
      driver.licence_front_key && driver.licence_front_key.trim() !== '' &&
      driver.licence_back_key && driver.licence_back_key.trim() !== ''
    );
    return validDrivers.length > 0 ? validDrivers : null;
  };

  // P0 BUGFIX: Filter out empty strings AND null from list fields
  const normalizeStringList = (list) => {
    if (!list || !Array.isArray(list)) return null;
    const validItems = list.filter(item => typeof item === 'string' && item.trim() !== '');
    return validItems.length > 0 ? validItems : null;
  };

  // Normalize prehire_meta fields before sending to backend
  // This prevents 422 validation errors from empty strings
  const normalizePreHireMeta = (meta) => {
    if (!meta) return null;
    return {
      ...meta,
      // Normalize string fields
      licence_front: normalizeString(meta.licence_front),
      licence_back: normalizeString(meta.licence_back),
      licence_selfie: normalizeString(meta.licence_selfie),
      licence_class: normalizeString(meta.licence_class),
      fuel_type: normalizeString(meta.fuel_type),
      fuel_policy: normalizeString(meta.fuel_policy),
      toll_policy: normalizeString(meta.toll_policy),
      // Normalize numeric fields
      km_allowance: normalizeNumber(meta.km_allowance, true),
      extra_km_rate: normalizeNumber(meta.extra_km_rate, false),
      // Normalize list fields - filter out empty entries
      additional_drivers: normalizeAdditionalDrivers(meta.additional_drivers),
      international_docs: normalizeStringList(meta.international_docs)
    };
  };

  // ========================================================
  // P0 CRITICAL FIX: Filter photo arrays to only valid strings (not null, not pending)
  // This prevents 422 "Input should be a valid string" validation errors
  const filterValidPhotoKeys = (photos) => {
    if (!photos || !Array.isArray(photos)) return [];
    return photos.filter(k => 
      typeof k === 'string' && 
      k.length > 0 && 
      !k.startsWith('pending_')
    );
  };

  const saveDraftSilently = async (currentData) => {
    try {
      setSavingDraft(true);
      
      // P0 FIX: Prepare data for API - filter out null/undefined and pending photos
      const exteriorPhotos = filterValidPhotoKeys(currentData.exterior_photos);
      const interiorPhotos = filterValidPhotoKeys(currentData.interior_photos);
      
      const savePayload = {
        // P0 BUGFIX: Send null instead of empty arrays
        exterior_photos: exteriorPhotos.length > 0 ? exteriorPhotos : null,
        interior_photos: interiorPhotos.length > 0 ? interiorPhotos : null,
        odometer_photo: normalizeString(currentData.odometer_photo),
        // P0 BUGFIX: Use normalizeNumber to prevent 422 on empty strings
        odometer_value: normalizeNumber(currentData.odometer_value, true),
        fuel_photo: normalizeString(currentData.fuel_photo),
        fuel_level: normalizeString(currentData.fuel_level),
        keys_count: normalizeNumber(currentData.keys_count, true),
        notes: normalizeString(currentData.notes),
        // P0 BUGFIX: Normalize prehire_meta fields
        prehire_meta: normalizePreHireMeta(currentData.prehire_meta),
        // P0 LEGAL RECORD: Save current step for resume
        current_step: step,
        // P0 LEGAL RECORD: Save tyre photos
        tyre_photos: normalizeStringList(currentData.tyre_photos)
      };
      
      await api.post(`/bookings/${booking.booking_id}/prehire/save`, savePayload);
    } catch (error) {
    } finally {
      setSavingDraft(false);
    }
  };

  // P0 FIX 1 (Resume Bug): Expose save method to parent via ref
  // This allows the Dialog's onOpenChange to save before closing
  React.useImperativeHandle(ref, () => ({
    saveDraftBeforeClose: () => {
      // Fire-and-forget save when modal closes (click outside / X button)
      saveDraftSilently(data);
    }
  }), [data]);

  // P0 FIX: Save on browser back / beforeunload (best effort)
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      // Sync attempt - may or may not complete
      saveDraftSilently(data);
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [data]);

  // P0 FIX 2: Debounced autosave (700ms) for ALL prehire_meta changes
  const autosaveTimeoutRef = React.useRef(null);
  const lastSavedDataRef = React.useRef(null);
  
  React.useEffect(() => {
    // P0 CRITICAL FIX: Skip autosave until initial data load is complete
    // This prevents the autosave from sending empty arrays to the server
    // when the component first mounts and data hasn't loaded yet
    if (!isInitialLoadComplete) {
      return;
    }
    
    // Initialize lastSavedDataRef after initial load
    if (lastSavedDataRef.current === null) {
      lastSavedDataRef.current = JSON.stringify(data);
      return;
    }
    
    // Skip when data hasn't meaningfully changed
    const currentDataStr = JSON.stringify(data);
    if (lastSavedDataRef.current === currentDataStr) return;
    
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    
    autosaveTimeoutRef.current = setTimeout(() => {
      saveDraftSilently(data);
      lastSavedDataRef.current = currentDataStr;
    }, 700);
    
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [data, isInitialLoadComplete]);

  // Save on step navigation (with debounce built-in via timeout)
  const saveTimeoutRef = React.useRef(null);
  const triggerAutoSave = React.useCallback((currentData) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveDraftSilently(currentData);
    }, 500);
  }, []);

  // Navigate to next step with auto-save
  const goToNextStep = (nextStep) => {
    triggerAutoSave(data);
    setStep(nextStep);
  };

  // Navigate to previous step with auto-save
  const goToPrevStep = (prevStep) => {
    triggerAutoSave(data);
    setStep(prevStep);
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      // P0 FIX: Use filterValidPhotoKeys to prevent 422 errors from null values
      const exteriorPhotos = filterValidPhotoKeys(data.exterior_photos);
      const interiorPhotos = filterValidPhotoKeys(data.interior_photos);
      
      const savePayload = {
        // P0 BUGFIX: Send null instead of empty arrays
        exterior_photos: exteriorPhotos.length > 0 ? exteriorPhotos : null,
        interior_photos: interiorPhotos.length > 0 ? interiorPhotos : null,
        odometer_photo: normalizeString(data.odometer_photo),
        // P0 BUGFIX: Use normalizeNumber to prevent 422 on empty strings
        odometer_value: normalizeNumber(data.odometer_value, true),
        fuel_photo: normalizeString(data.fuel_photo),
        fuel_level: normalizeString(data.fuel_level),
        keys_count: normalizeNumber(data.keys_count, true),
        notes: normalizeString(data.notes),
        // P0 BUGFIX: Normalize prehire_meta fields
        prehire_meta: normalizePreHireMeta(data.prehire_meta)
      };
      await api.post(`/bookings/${booking.booking_id}/prehire/save`, savePayload);
      toast.success('Draft saved');
    } catch (error) {
      toast.error(normalizeApiError(error) || 'Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  // P0 FIX 2: Handle cancel with auto-save
  const handleCancel = async () => {
    // Fire-and-forget save before closing
    saveDraftSilently(data);
    onCancel();
  };

  // ========================================================
  // P0 FIX 3: SUBMIT WITH SAFE ERROR HANDLING
  // ========================================================
  const handleSubmit = async () => {
    // Validate before submit - use filterValidPhotoKeys for accurate count
    const newErrors = {};
    if (filterValidPhotoKeys(data.exterior_photos).length < REQUIRED_EXTERIOR) {
      newErrors.exterior = `Minimum ${REQUIRED_EXTERIOR} exterior photos required`;
    }
    if (filterValidPhotoKeys(data.interior_photos).length < REQUIRED_INTERIOR) {
      newErrors.interior = `Minimum ${REQUIRED_INTERIOR} interior photos required`;
    }
    if (!data.odometer_photo) newErrors.odometer_photo = 'Odometer photo required';
    if (!data.odometer_value || parseInt(data.odometer_value) <= 0) {
      newErrors.odometer_value = 'Valid odometer reading required';
    }
    if (!data.fuel_photo) newErrors.fuel_photo = 'Fuel gauge photo required';
    if (!data.keys_count || parseInt(data.keys_count) < 1) {
      newErrors.keys_count = 'Keys count required';
    }

    // P0 EXTENSION: Validate new steps
    if (!data.prehire_meta.renter_present) {
      newErrors.renter_present = 'Renter presence confirmation required';
    }
    if (!data.prehire_meta.identity_confirmed) {
      newErrors.identity_confirmed = 'Identity confirmation required';
    }
    if (!data.prehire_meta.licence_front || !data.prehire_meta.licence_back) {
      newErrors.licence = 'Licence front and back photos required';
    }
    if (!data.prehire_meta.licence_class) {
      newErrors.licence_class = 'Licence class required';
    }
    if (!data.prehire_meta.vehicle_explained) {
      newErrors.vehicle_explained = 'Vehicle handover explanation required';
    }
    if (!data.prehire_meta.fuel_type) {
      newErrors.fuel_type = 'Fuel type required';
    }
    if (!data.prehire_meta.fuel_policy) {
      newErrors.fuel_policy = 'Fuel policy required';
    }
    if (!data.prehire_meta.toll_policy) {
      newErrors.toll_policy = 'Toll policy required';
    }
    // P0 FIX: Safe access to handover_acknowledgements
    const acks = data.prehire_meta?.handover_acknowledgements || {};
    if (!Object.values(acks).every(v => v === true)) {
      newErrors.acknowledgements = 'All handover acknowledgements required';
    }
    if (!data.prehire_meta.final_declaration) {
      newErrors.final_declaration = 'Final declaration required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please complete all required fields');
      return;
    }

    setLoading(true);
    try {
      // Filter out any pending/null photo keys using the centralized helper
      const cleanExterior = filterValidPhotoKeys(data.exterior_photos);
      const cleanInterior = filterValidPhotoKeys(data.interior_photos);
      
      const submitData = {
        ...data,
        exterior_photos: cleanExterior,
        interior_photos: cleanInterior,
        odometer_value: parseInt(data.odometer_value),
        keys_count: parseInt(data.keys_count),
        prehire_meta: {
          ...data.prehire_meta,
          km_allowance: data.prehire_meta.km_allowance ? parseInt(data.prehire_meta.km_allowance) : null,
          extra_km_rate: data.prehire_meta.extra_km_rate ? parseFloat(data.prehire_meta.extra_km_rate) : null
        }
      };
      
      const response = await api.post(`/bookings/${booking.booking_id}/prehire/submit`, submitData);
      // Iter117 P0 fix: prehire/submit now lands at 'prehire_submitted'; the renter
      // must sign the Start Trip confirmation to actually start the trip.
      toast.success('Pre-hire checklist submitted. Renter needs to confirm Start Trip to begin the hire.');
      onComplete();
    } catch (error) {
      // P0 LEGAL RECORD: Handle structured validation errors from backend
      console.error('[PreHireWizard] Submit failed:', {
        status: error.response?.status,
        data: error.response?.data
      });
      
      const responseData = error.response?.data?.detail;
      
      // Check if backend returned structured missing_fields
      if (responseData && responseData.missing_fields && Array.isArray(responseData.missing_fields)) {
        const missingFields = responseData.missing_fields;
        
        // Build errors object from backend response
        const newErrors = {};
        missingFields.forEach(mf => {
          newErrors[mf.field] = mf.message;
        });
        setErrors(newErrors);
        
        // Navigate to the first missing step
        const firstMissing = missingFields[0];
        if (firstMissing && firstMissing.step) {
          toast.error(`Step ${firstMissing.step}: ${firstMissing.message}`);
          setStep(firstMissing.step);
        } else {
          toast.error(responseData.message || 'Please complete all required fields');
        }
      } else {
        const errorMsg = normalizeApiError(error);
        toast.error(errorMsg || 'Submission failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // P0 CANONICAL: Remove photo and its thumbnail
  const removePhoto = (type, index) => {
    const thumbKey = type === 'exterior_photos' ? 'exterior' : 'interior';
    
    setData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
    
    // Also remove from thumbnails
    setPhotoThumbnails(prev => ({
      ...prev,
      [thumbKey]: prev[thumbKey].filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-6">
      {/* Hidden file inputs - P0: Added capture="environment" for mobile camera */}
      <input
        type="file"
        ref={exteriorInputRef}
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => handlePhotoUpload(e.target.files, 'exterior_photos')}
      />
      <input
        type="file"
        ref={interiorInputRef}
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => handlePhotoUpload(e.target.files, 'interior_photos')}
      />
      <input
        type="file"
        ref={odometerInputRef}
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => e.target.files[0] && handleSinglePhotoUpload(e.target.files[0], 'odometer_photo')}
      />
      <input
        type="file"
        ref={fuelInputRef}
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => e.target.files[0] && handleSinglePhotoUpload(e.target.files[0], 'fuel_photo')}
      />

      {/* P0 EXTENSION: Hidden file inputs for licence uploads */}
      <input
        type="file"
        ref={licenceFrontRef}
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => e.target.files[0] && handleLicenceUpload(e.target.files[0], 'licence_front')}
      />
      <input
        type="file"
        ref={licenceBackRef}
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => e.target.files[0] && handleLicenceUpload(e.target.files[0], 'licence_back')}
      />
      <input
        type="file"
        ref={licenceSelfieRef}
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={(e) => e.target.files[0] && handleLicenceUpload(e.target.files[0], 'licence_selfie')}
      />
      <input
        type="file"
        ref={internationalDocRef}
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => e.target.files[0] && handleInternationalDocUpload(e.target.files[0])}
      />

      {/* Progress Bar (Percentage) */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Step {step} of {TOTAL_STEPS}</span>
          <span className="text-sm font-medium text-[#004B8D]">{progressPercent}%</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2">
          <div 
            className="bg-[#004B8D] h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Step 1: Exterior Photos - P0 CANONICAL: Now shows thumbnails */}
      {step === 1 && (
        <div>
          <h3 className="font-semibold text-lg mb-2">Exterior Photos</h3>
          <p className="text-sm text-slate-600 mb-2">
            Take photos of all 4 sides + front + rear (minimum {REQUIRED_EXTERIOR} photos)
          </p>
          {/* HireHub Protection Micro Warning */}
          <p className="text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-md mb-4 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Clear exterior photos strengthen your damage claims</span>
          </p>
          {errors.exterior && <p className="text-red-600 text-sm mb-2">{errors.exterior}</p>}
          
          <div className="grid grid-cols-3 gap-2 mb-4">
            {data.exterior_photos.map((s3Key, idx) => {
              // P0 FIX: Find thumbnail by s3_key, not by array index
              const thumbData = photoThumbnails.exterior.find(t => t?.s3_key === s3Key);
              return (
                <div key={s3Key || idx} className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative group">
                  {thumbData?.thumbnail_url ? (
                    <img 
                      src={thumbData.thumbnail_url} 
                      alt={`Exterior ${idx + 1}`}
                      className="w-full h-full object-cover"
                      data-testid={`exterior-thumbnail-${idx}`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                  )}
                  <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5 opacity-80">
                    <CheckCircle className="w-3 h-3 text-white" />
                  </div>
                  <button
                    onClick={() => removePhoto('exterior_photos', idx)}
                    className="absolute top-1 left-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
            <Camera className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-600 mb-2">
              {data.exterior_photos.length}/{REQUIRED_EXTERIOR} photos uploaded
              {data.exterior_photos.length >= REQUIRED_EXTERIOR && <span className="text-green-600 ml-1">✓</span>}
            </p>
            <Button 
              onClick={() => exteriorInputRef.current?.click()}
              variant="outline"
              size="sm"
              data-testid="upload-exterior-btn"
            >
              {Object.values(uploadProgress).some(v => v === 'uploading') ? (
                <>Uploading...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" />Add Photos</>
              )}
            </Button>
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={handleCancel} className="flex-1">Cancel</Button>
            <Button 
              onClick={() => setStep(2)} 
              className="flex-1 bg-[#004B8D]"
              disabled={data.exterior_photos.length < REQUIRED_EXTERIOR}
            >
              Next ({data.exterior_photos.length}/{REQUIRED_EXTERIOR})
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Interior Photos - P0 CANONICAL: Now shows thumbnails */}
      {step === 2 && (
        <div>
          <h3 className="font-semibold text-lg mb-2">Interior Photos</h3>
          <p className="text-sm text-slate-600 mb-2">
            Dashboard, seats, boot/trunk, steering wheel, etc. (minimum {REQUIRED_INTERIOR} photos)
          </p>
          {/* HireHub Protection Micro Warning */}
          <p className="text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-md mb-4 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Interior photos help document pre-existing condition</span>
          </p>
          {errors.interior && <p className="text-red-600 text-sm mb-2">{errors.interior}</p>}
          
          <div className="grid grid-cols-3 gap-2 mb-4">
            {data.interior_photos.map((s3Key, idx) => {
              // P0 FIX: Find thumbnail by s3_key, not by array index
              const thumbData = photoThumbnails.interior.find(t => t?.s3_key === s3Key);
              return (
                <div key={s3Key || idx} className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative group">
                  {thumbData?.thumbnail_url ? (
                    <img 
                      src={thumbData.thumbnail_url} 
                      alt={`Interior ${idx + 1}`}
                      className="w-full h-full object-cover"
                      data-testid={`interior-thumbnail-${idx}`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                  )}
                  <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5 opacity-80">
                    <CheckCircle className="w-3 h-3 text-white" />
                  </div>
                  <button
                    onClick={() => removePhoto('interior_photos', idx)}
                    className="absolute top-1 left-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
            <Camera className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-600 mb-2">
              {data.interior_photos.length}/{REQUIRED_INTERIOR} photos uploaded
              {data.interior_photos.length >= REQUIRED_INTERIOR && <span className="text-green-600 ml-1">✓</span>}
            </p>
            <Button 
              onClick={() => interiorInputRef.current?.click()}
              variant="outline"
              size="sm"
              data-testid="upload-interior-btn"
            >
              {Object.values(uploadProgress).some(v => v === 'uploading') ? (
                <>Uploading...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" />Add Photos</>
              )}
            </Button>
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(3)} 
              className="flex-1 bg-[#004B8D]"
              disabled={data.interior_photos.length < REQUIRED_INTERIOR}
            >
              Next ({data.interior_photos.length}/{REQUIRED_INTERIOR})
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Odometer & Fuel - P0 CANONICAL: Now shows thumbnails */}
      {step === 3 && (
        <div>
          <h3 className="font-semibold text-lg mb-2">Odometer & Fuel Level</h3>
          {/* HireHub Protection Micro Warning */}
          <p className="text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-md mb-4 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Odometer and fuel records are essential for excess KM and fuel claims</span>
          </p>
          
          <div className="space-y-4">
            {/* Odometer Photo */}
            <div>
              <label className="block text-sm font-medium mb-2">Odometer Photo *</label>
              {errors.odometer_photo && <p className="text-red-600 text-xs mb-1">{errors.odometer_photo}</p>}
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                {data.odometer_photo ? (
                  <div className="flex flex-col items-center gap-2">
                    {photoThumbnails.odometer?.thumbnail_url ? (
                      <div className="relative">
                        <img 
                          src={photoThumbnails.odometer.thumbnail_url} 
                          alt="Odometer"
                          className="w-32 h-24 object-cover rounded-lg border border-green-300"
                          data-testid="odometer-thumbnail"
                        />
                        <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-green-700">Photo uploaded</span>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setData(prev => ({ ...prev, odometer_photo: '' }));
                        setPhotoThumbnails(prev => ({ ...prev, odometer: null }));
                      }}
                      className="text-red-500 text-sm hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <Button 
                    onClick={() => odometerInputRef.current?.click()}
                    variant="outline"
                    size="sm"
                    data-testid="upload-odometer-btn"
                  >
                    {uploadProgress.odometer_photo === 'uploading' ? (
                      <>Uploading...</>
                    ) : (
                      <><Camera className="w-4 h-4 mr-2" />Take Photo</>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Odometer Value */}
            <div>
              <label className="block text-sm font-medium mb-2">Odometer Reading (km) *</label>
              {errors.odometer_value && <p className="text-red-600 text-xs mb-1">{errors.odometer_value}</p>}
              <input
                type="number"
                value={data.odometer_value}
                onChange={e => setData(prev => ({ ...prev, odometer_value: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="e.g., 45000"
                data-testid="odometer-input"
              />
            </div>

            {/* Fuel Photo */}
            <div>
              <label className="block text-sm font-medium mb-2">Fuel Gauge Photo *</label>
              {errors.fuel_photo && <p className="text-red-600 text-xs mb-1">{errors.fuel_photo}</p>}
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                {data.fuel_photo ? (
                  <div className="flex flex-col items-center gap-2">
                    {photoThumbnails.fuel?.thumbnail_url ? (
                      <div className="relative">
                        <img 
                          src={photoThumbnails.fuel.thumbnail_url} 
                          alt="Fuel gauge"
                          className="w-32 h-24 object-cover rounded-lg border border-green-300"
                          data-testid="fuel-thumbnail"
                        />
                        <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-green-700">Photo uploaded</span>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setData(prev => ({ ...prev, fuel_photo: '' }));
                        setPhotoThumbnails(prev => ({ ...prev, fuel: null }));
                      }}
                      className="text-red-500 text-sm hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <Button 
                    onClick={() => fuelInputRef.current?.click()}
                    variant="outline"
                    size="sm"
                    data-testid="upload-fuel-btn"
                  >
                    {uploadProgress.fuel_photo === 'uploading' ? (
                      <>Uploading...</>
                    ) : (
                      <><Fuel className="w-4 h-4 mr-2" />Take Photo</>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Fuel Level */}
            <div>
              <label className="block text-sm font-medium mb-2">Fuel Level *</label>
              <div className="flex gap-2">
                {FUEL_LEVELS.map(level => (
                  <button
                    key={level}
                    onClick={() => setData(prev => ({ ...prev, fuel_level: level }))}
                    className={`flex-1 py-2 px-1 text-xs rounded-lg border transition ${
                      data.fuel_level === level 
                        ? 'bg-[#004B8D] text-white border-[#004B8D]' 
                        : 'bg-white border-slate-300 hover:border-[#004B8D]'
                    }`}
                    data-testid={`fuel-level-${level}`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(2)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(4)} 
              className="flex-1 bg-[#004B8D]"
              disabled={!data.odometer_photo || !data.odometer_value || parseInt(data.odometer_value) <= 0 || !data.fuel_photo || !data.fuel_level}
              data-testid="step3-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Keys & Confirmation */}
      {step === 4 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Keys & Photo Confirmation</h3>
          
          <div className="space-y-4">
            {/* Keys Count */}
            <div>
              <label className="block text-sm font-medium mb-2">Number of Keys *</label>
              {errors.keys_count && <p className="text-red-600 text-xs mb-1">{errors.keys_count}</p>}
              <div className="flex gap-2">
                {[1, 2, 3].map(num => (
                  <button
                    key={num}
                    onClick={() => setData(prev => ({ ...prev, keys_count: num.toString() }))}
                    className={`flex-1 py-3 rounded-lg border transition flex items-center justify-center gap-2 ${
                      data.keys_count === num.toString()
                        ? 'bg-[#004B8D] text-white border-[#004B8D]' 
                        : 'bg-white border-slate-300 hover:border-[#004B8D]'
                    }`}
                    data-testid={`keys-${num}`}
                  >
                    <Key className="w-4 h-4" />
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-2">Additional Notes (Optional)</label>
              <Textarea
                value={data.notes}
                onChange={e => setData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any existing damage, scratches, or notes for the renter..."
                rows={3}
                data-testid="prehire-notes"
              />
            </div>

            {/* Summary */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-sm mb-2">Photo Summary</h4>
              <div className="text-xs space-y-1">
                <div className="flex justify-between">
                  <span>Exterior Photos:</span>
                  <span className={data.exterior_photos.length >= REQUIRED_EXTERIOR ? 'text-green-600' : 'text-red-600'}>
                    {data.exterior_photos.length}/{REQUIRED_EXTERIOR} ✓
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Interior Photos:</span>
                  <span className={data.interior_photos.length >= REQUIRED_INTERIOR ? 'text-green-600' : 'text-red-600'}>
                    {data.interior_photos.length}/{REQUIRED_INTERIOR} ✓
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Odometer Photo:</span>
                  <span className={data.odometer_photo ? 'text-green-600' : 'text-red-600'}>
                    {data.odometer_photo ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Odometer Reading:</span>
                  <span>{data.odometer_value || '-'} km</span>
                </div>
                <div className="flex justify-between">
                  <span>Fuel Photo:</span>
                  <span className={data.fuel_photo ? 'text-green-600' : 'text-red-600'}>
                    {data.fuel_photo ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Fuel Level:</span>
                  <span>{data.fuel_level}</span>
                </div>
                <div className="flex justify-between">
                  <span>Keys:</span>
                  <span>{data.keys_count}</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 font-medium">Have you taken enough photos?</p>
              <p className="text-xs text-blue-600 mt-1">
                Please ensure all vehicle photos are clear and show any existing damage before continuing.
              </p>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(3)} className="flex-1">Back</Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={loading}>
              Save Draft
            </Button>
            <Button 
              onClick={() => setStep(5)} 
              className="flex-1 bg-[#004B8D]"
              disabled={data.exterior_photos.length < REQUIRED_EXTERIOR || data.interior_photos.length < REQUIRED_INTERIOR || !data.odometer_photo || !data.fuel_photo}
              data-testid="step4-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/* P0 EXTENSION: NEW STEPS 5-17 */}
      {/* ================================================================== */}

      {/* Step 5: Renter Present Confirmation */}
      {step === 5 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Renter Present Confirmation</h3>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800 font-medium">
              Are you physically with the renter at the vehicle for handover?
            </p>
          </div>

          {data.prehire_meta.renter_present === false && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-700 font-medium">
                Pre-hire inspection must be completed in person with the renter present.
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant={data.prehire_meta.renter_present === true ? 'default' : 'outline'}
              onClick={() => setData(prev => ({
                ...prev,
                prehire_meta: { ...prev.prehire_meta, renter_present: true }
              }))}
              className={`flex-1 ${data.prehire_meta.renter_present === true ? 'bg-green-600' : ''}`}
              data-testid="renter-present-yes"
            >
              Yes – Continue
            </Button>
            <Button
              variant={data.prehire_meta.renter_present === false ? 'default' : 'outline'}
              onClick={() => setData(prev => ({
                ...prev,
                prehire_meta: { ...prev.prehire_meta, renter_present: false }
              }))}
              className={`flex-1 ${data.prehire_meta.renter_present === false ? 'bg-red-600' : ''}`}
              data-testid="renter-present-no"
            >
              No
            </Button>
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(4)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(6)} 
              className="flex-1 bg-[#004B8D]"
              disabled={data.prehire_meta.renter_present !== true}
              data-testid="step5-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 6: Confirm Renter Identity - P0 FIX: Now shows verification photos */}
      {step === 6 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Confirm Renter Identity</h3>
          
          {/* Renter Details Section */}
          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#004B8D]" />
              Renter Details
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Full Name:</span>
                <span className="font-medium" data-testid="renter-full-name">
                  {renterInfo?.renter_full_name || renterInfo?.full_name || 'Loading...'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Email:</span>
                <span className="font-medium" data-testid="renter-email">
                  {renterInfo?.renter_email || renterInfo?.email || booking?.customer_email || 'Not provided'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Phone:</span>
                <span className="font-medium" data-testid="renter-phone">
                  {renterInfo?.renter_phone || renterInfo?.phone || 'Not provided'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Verification Status:</span>
                <Badge 
                  className={`text-xs ${
                    renterInfo?.verification_status === 'verified' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-amber-100 text-amber-800'
                  }`}
                  data-testid="renter-verification-status"
                >
                  {renterInfo?.verification_status || 'Unknown'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Renter Verification Photos Section */}
          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
              <Image className="w-4 h-4 text-[#004B8D]" />
              Renter Verification Photos
            </h4>
            
            {renterVerification?.has_verification ? (
              <div className="grid grid-cols-2 gap-3">
                {/* Selfie Thumbnail */}
                {renterVerification.selfie_url ? (
                  <div 
                    className="relative border rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#004B8D] transition-all"
                    onClick={() => setVerificationImageModal({ open: true, url: renterVerification.selfie_url, type: 'Selfie' })}
                    data-testid="renter-selfie-thumbnail"
                  >
                    <img 
                      src={renterVerification.selfie_url} 
                      alt="Renter Selfie" 
                      className="w-full h-24 object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs py-1 px-2 text-center">
                      Selfie - Tap to view
                    </div>
                  </div>
                ) : (
                  <div className="border rounded-lg p-4 text-center bg-slate-100 h-24 flex items-center justify-center">
                    <span className="text-xs text-slate-500">No selfie on file</span>
                  </div>
                )}
                
                {/* Licence Front Thumbnail */}
                {renterVerification.licence_front_url ? (
                  <div 
                    className="relative border rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#004B8D] transition-all"
                    onClick={() => setVerificationImageModal({ open: true, url: renterVerification.licence_front_url, type: 'Licence Front' })}
                    data-testid="renter-licence-thumbnail"
                  >
                    <img 
                      src={renterVerification.licence_front_url} 
                      alt="Licence Front" 
                      className="w-full h-24 object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs py-1 px-2 text-center">
                      Licence - Tap to view
                    </div>
                  </div>
                ) : (
                  <div className="border rounded-lg p-4 text-center bg-slate-100 h-24 flex items-center justify-center">
                    <span className="text-xs text-slate-500">No licence on file</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">No Verification Images on File</p>
                    <p className="text-xs text-amber-700 mt-1">
                      This renter has not uploaded verification photos. Please request ID manually and proceed with caution.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Important:</strong> Confirm that the person present matches the booking details and verification photos above. 
              This is a legal requirement before handing over the vehicle.
            </p>
          </div>

          <Button
            onClick={() => setData(prev => ({
              ...prev,
              prehire_meta: { ...prev.prehire_meta, identity_confirmed: true }
            }))}
            className={`w-full ${data.prehire_meta.identity_confirmed ? 'bg-green-600' : 'bg-[#004B8D]'}`}
            data-testid="confirm-identity-btn"
          >
            {data.prehire_meta.identity_confirmed ? '✓ Identity Confirmed' : 'Identity Matches – Confirm'}
          </Button>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(5)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(7)} 
              className="flex-1 bg-[#004B8D]"
              disabled={!data.prehire_meta.identity_confirmed}
              data-testid="step6-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Verification Image Modal */}
      <Dialog open={verificationImageModal.open} onOpenChange={(open) => setVerificationImageModal({ ...verificationImageModal, open })}>
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#004B8D]" />
              {verificationImageModal.type}
            </DialogTitle>
          </DialogHeader>
          {verificationImageModal.url && (
            <div className="p-2">
              <img 
                src={verificationImageModal.url} 
                alt={verificationImageModal.type}
                className="w-full max-h-[60vh] object-contain rounded"
                data-testid="verification-image-full"
              />
            </div>
          )}
          <DialogFooter className="p-4 border-t">
            <Button onClick={() => setVerificationImageModal({ open: false, url: null, type: null })} className="w-full">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 7: Driver Licence Capture - P0 FIX: Now shows thumbnails after upload */}
      {step === 7 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Primary Driver Licence</h3>
          
          <div className="space-y-4">
            {/* Licence Front */}
            <div>
              <label className="block text-sm font-medium mb-2">Licence Front *</label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                {(data.prehire_meta?.licence_front || photoThumbnails.licence_front?.thumbnail_url) ? (
                  <div className="flex flex-col items-center gap-2">
                    {/* P0 CANONICAL: Show thumbnail image if available */}
                    {photoThumbnails.licence_front?.thumbnail_url ? (
                      <div className="relative w-32 h-24 rounded-lg overflow-hidden border border-green-300 shadow-sm">
                        <img 
                          src={photoThumbnails.licence_front.thumbnail_url} 
                          alt="Licence Front" 
                          className="w-full h-full object-cover"
                          data-testid="licence-front-thumbnail"
                        />
                        <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-green-700">Photo uploaded</span>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setData(prev => ({
                          ...prev,
                          prehire_meta: { ...prev.prehire_meta, licence_front: '' }
                        }));
                        setPhotoThumbnails(prev => ({ ...prev, licence_front: null }));
                      }}
                      className="text-red-500 text-sm hover:text-red-700"
                      data-testid="remove-licence-front-btn"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <Button 
                    onClick={() => licenceFrontRef.current?.click()}
                    variant="outline"
                    size="sm"
                    data-testid="upload-licence-front-btn"
                  >
                    {uploadProgress.licence_front === 'uploading' ? (
                      <>Uploading...</>
                    ) : (
                      <><Camera className="w-4 h-4 mr-2" />Take Photo</>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Licence Back */}
            <div>
              <label className="block text-sm font-medium mb-2">Licence Back *</label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                {(data.prehire_meta?.licence_back || photoThumbnails.licence_back?.thumbnail_url) ? (
                  <div className="flex flex-col items-center gap-2">
                    {/* P0 CANONICAL: Show thumbnail image if available */}
                    {photoThumbnails.licence_back?.thumbnail_url ? (
                      <div className="relative w-32 h-24 rounded-lg overflow-hidden border border-green-300 shadow-sm">
                        <img 
                          src={photoThumbnails.licence_back.thumbnail_url} 
                          alt="Licence Back" 
                          className="w-full h-full object-cover"
                          data-testid="licence-back-thumbnail"
                        />
                        <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-green-700">Photo uploaded</span>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setData(prev => ({
                          ...prev,
                          prehire_meta: { ...prev.prehire_meta, licence_back: '' }
                        }));
                        setPhotoThumbnails(prev => ({ ...prev, licence_back: null }));
                      }}
                      className="text-red-500 text-sm hover:text-red-700"
                      data-testid="remove-licence-back-btn"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <Button 
                    onClick={() => licenceBackRef.current?.click()}
                    variant="outline"
                    size="sm"
                    data-testid="upload-licence-back-btn"
                  >
                    {uploadProgress.licence_back === 'uploading' ? (
                      <>Uploading...</>
                    ) : (
                      <><Camera className="w-4 h-4 mr-2" />Take Photo</>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Selfie (Optional) */}
            <div>
              <label className="block text-sm font-medium mb-2">Selfie Photo (Optional)</label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                {(data.prehire_meta?.licence_selfie || photoThumbnails.licence_selfie?.thumbnail_url) ? (
                  <div className="flex flex-col items-center gap-2">
                    {/* P0 CANONICAL: Show thumbnail image if available */}
                    {photoThumbnails.licence_selfie?.thumbnail_url ? (
                      <div className="relative w-24 h-24 rounded-full overflow-hidden border border-green-300 shadow-sm">
                        <img 
                          src={photoThumbnails.licence_selfie.thumbnail_url} 
                          alt="Selfie" 
                          className="w-full h-full object-cover"
                          data-testid="licence-selfie-thumbnail"
                        />
                        <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-green-700">Selfie uploaded</span>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setData(prev => ({
                          ...prev,
                          prehire_meta: { ...prev.prehire_meta, licence_selfie: '' }
                        }));
                        setPhotoThumbnails(prev => ({ ...prev, licence_selfie: null }));
                      }}
                      className="text-red-500 text-sm hover:text-red-700"
                      data-testid="remove-licence-selfie-btn"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <Button 
                    onClick={() => licenceSelfieRef.current?.click()}
                    variant="outline"
                    size="sm"
                    data-testid="upload-licence-selfie-btn"
                  >
                    {uploadProgress.licence_selfie === 'uploading' ? (
                      <>Uploading...</>
                    ) : (
                      <><Camera className="w-4 h-4 mr-2" />Take Selfie</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(6)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(8)} 
              className="flex-1 bg-[#004B8D]"
              disabled={!(data.prehire_meta?.licence_front || photoThumbnails.licence_front) || !(data.prehire_meta?.licence_back || photoThumbnails.licence_back)}
              data-testid="step7-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 8: Additional Drivers */}
      {step === 8 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Additional Drivers</h3>
          
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-slate-700">
              Are there additional approved drivers for this booking?
            </p>
          </div>

          <div className="flex gap-3 mb-6">
            <Button
              variant={data.prehire_meta.has_additional_drivers === true ? 'default' : 'outline'}
              onClick={() => {
                setData(prev => ({
                  ...prev,
                  prehire_meta: { 
                    ...prev.prehire_meta, 
                    has_additional_drivers: true,
                    additional_drivers: prev.prehire_meta.additional_drivers.length === 0 
                      ? [{ name: '', licence_front_key: '', licence_back_key: '' }]
                      : prev.prehire_meta.additional_drivers
                  }
                }));
              }}
              className={`flex-1 ${data.prehire_meta.has_additional_drivers === true ? 'bg-[#004B8D]' : ''}`}
              data-testid="additional-drivers-yes"
            >
              Yes
            </Button>
            <Button
              variant={data.prehire_meta.has_additional_drivers === false ? 'default' : 'outline'}
              onClick={() => setData(prev => ({
                ...prev,
                prehire_meta: { ...prev.prehire_meta, has_additional_drivers: false, additional_drivers: [] }
              }))}
              className={`flex-1 ${data.prehire_meta.has_additional_drivers === false ? 'bg-[#004B8D]' : ''}`}
              data-testid="additional-drivers-no"
            >
              No
            </Button>
          </div>

          {data.prehire_meta.has_additional_drivers === true && (
            <div className="space-y-4">
              {data.prehire_meta.additional_drivers.map((driver, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium text-sm">Additional Driver {idx + 1}</h4>
                    {idx > 0 && (
                      <button
                        onClick={() => setData(prev => ({
                          ...prev,
                          prehire_meta: {
                            ...prev.prehire_meta,
                            additional_drivers: prev.prehire_meta.additional_drivers.filter((_, i) => i !== idx)
                          }
                        }))}
                        className="text-red-500 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Full Name *</label>
                      <input
                        type="text"
                        value={driver.name}
                        onChange={e => setData(prev => {
                          const drivers = [...prev.prehire_meta.additional_drivers];
                          drivers[idx].name = e.target.value;
                          return { ...prev, prehire_meta: { ...prev.prehire_meta, additional_drivers: drivers }};
                        })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                        placeholder="Full name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Licence Front *</label>
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 text-center">
                          {driver.licence_front_key ? (
                            <span className="text-xs text-green-600">✓ Uploaded</span>
                          ) : (
                            <>
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                ref={el => additionalDriverRefs.current[`${idx}_front`] = el}
                                onChange={(e) => e.target.files[0] && handleAdditionalDriverUpload(e.target.files[0], idx, 'front')}
                              />
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => additionalDriverRefs.current[`${idx}_front`]?.click()}
                              >
                                <Camera className="w-3 h-3 mr-1" />
                                Photo
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Licence Back *</label>
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 text-center">
                          {driver.licence_back_key ? (
                            <span className="text-xs text-green-600">✓ Uploaded</span>
                          ) : (
                            <>
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                ref={el => additionalDriverRefs.current[`${idx}_back`] = el}
                                onChange={(e) => e.target.files[0] && handleAdditionalDriverUpload(e.target.files[0], idx, 'back')}
                              />
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => additionalDriverRefs.current[`${idx}_back`]?.click()}
                              >
                                <Camera className="w-3 h-3 mr-1" />
                                Photo
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              <Button
                variant="outline"
                onClick={() => setData(prev => ({
                  ...prev,
                  prehire_meta: {
                    ...prev.prehire_meta,
                    additional_drivers: [...prev.prehire_meta.additional_drivers, { name: '', licence_front_key: '', licence_back_key: '' }]
                  }
                }))}
                className="w-full"
              >
                + Add Another Driver
              </Button>
            </div>
          )}

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(7)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(9)} 
              className="flex-1 bg-[#004B8D]"
              disabled={data.prehire_meta.has_additional_drivers === null || 
                (data.prehire_meta.has_additional_drivers === true && 
                  data.prehire_meta.additional_drivers.some(d => !d.name || !d.licence_front_key || !d.licence_back_key))}
              data-testid="step8-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 9: International Driver Check */}
      {step === 9 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Overseas Licence Check</h3>
          
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-slate-700">
              Are any approved drivers using an overseas licence?
            </p>
          </div>

          <div className="flex gap-3 mb-6">
            <Button
              variant={data.prehire_meta.has_international_licence === true ? 'default' : 'outline'}
              onClick={() => setData(prev => ({
                ...prev,
                prehire_meta: { ...prev.prehire_meta, has_international_licence: true }
              }))}
              className={`flex-1 ${data.prehire_meta.has_international_licence === true ? 'bg-[#004B8D]' : ''}`}
              data-testid="international-yes"
            >
              Yes
            </Button>
            <Button
              variant={data.prehire_meta.has_international_licence === false ? 'default' : 'outline'}
              onClick={() => setData(prev => ({
                ...prev,
                prehire_meta: { ...prev.prehire_meta, has_international_licence: false, international_docs: [] }
              }))}
              className={`flex-1 ${data.prehire_meta.has_international_licence === false ? 'bg-[#004B8D]' : ''}`}
              data-testid="international-no"
            >
              No
            </Button>
          </div>

          {data.prehire_meta.has_international_licence === true && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">International Permit / Supporting Document *</label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                  {data.prehire_meta.international_docs?.length > 0 || photoThumbnails.international_permit?.thumbnail_url ? (
                    <div className="flex flex-col items-center gap-2">
                      {photoThumbnails.international_permit?.thumbnail_url && (
                        <img 
                          src={photoThumbnails.international_permit.thumbnail_url} 
                          alt="International Permit" 
                          className="w-24 h-16 object-cover rounded-lg border border-green-300"
                        />
                      )}
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-green-700">{data.prehire_meta.international_docs?.length || 1} document(s) uploaded</span>
                      </div>
                    </div>
                  ) : (
                    <Button 
                      onClick={() => internationalDocRef.current?.click()}
                      variant="outline"
                      size="sm"
                      data-testid="upload-international-doc-btn"
                    >
                      {uploadProgress.international_doc === 'uploading' ? (
                        <>Uploading...</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" />Upload Document</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(8)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(10)} 
              className="flex-1 bg-[#004B8D]"
              disabled={data.prehire_meta.has_international_licence === null ||
                (data.prehire_meta.has_international_licence === true && (data.prehire_meta.international_docs?.length || 0) === 0 && !photoThumbnails.international_permit)}
              data-testid="step9-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 10: Licence Class Confirmation */}
      {step === 10 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Licence Class</h3>
          
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-slate-700">
              Select the primary driver's licence class.
            </p>
          </div>

          <div className="space-y-2">
            {['Full', 'Light Rigid', 'Medium Rigid', 'Heavy Rigid'].map(cls => (
              <button
                key={cls}
                onClick={() => setData(prev => ({
                  ...prev,
                  prehire_meta: { ...prev.prehire_meta, licence_class: cls }
                }))}
                className={`w-full py-3 px-4 text-left rounded-lg border transition ${
                  data.prehire_meta.licence_class === cls
                    ? 'bg-[#004B8D] text-white border-[#004B8D]'
                    : 'bg-white border-slate-300 hover:border-[#004B8D]'
                }`}
                data-testid={`licence-class-${cls.toLowerCase().replace(' ', '-')}`}
              >
                {cls}
              </button>
            ))}
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(9)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(11)} 
              className="flex-1 bg-[#004B8D]"
              disabled={!data.prehire_meta.licence_class}
              data-testid="step10-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 11: Vehicle Handover Explanation */}
      {step === 11 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Vehicle Handover Overview</h3>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              Please explain how the vehicle operates, any special features, cleanliness expectations, and safe operation requirements.
            </p>
          </div>

          <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              checked={data.prehire_meta.vehicle_explained}
              onChange={e => setData(prev => ({
                ...prev,
                prehire_meta: { ...prev.prehire_meta, vehicle_explained: e.target.checked }
              }))}
              className="mt-1 w-5 h-5"
              data-testid="vehicle-explained-checkbox"
            />
            <span className="text-sm text-slate-700">
              I have explained the vehicle features and expectations to the renter.
            </span>
          </label>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(10)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(12)} 
              className="flex-1 bg-[#004B8D]"
              disabled={!data.prehire_meta.vehicle_explained}
              data-testid="step11-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 12: Fuel Type */}
      {step === 12 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Fuel Type</h3>
          
          <div className="space-y-2">
            {['Unleaded', 'E10', 'Diesel', 'Electric'].map(fuel => (
              <button
                key={fuel}
                onClick={() => setData(prev => ({
                  ...prev,
                  prehire_meta: { ...prev.prehire_meta, fuel_type: fuel }
                }))}
                className={`w-full py-3 px-4 text-left rounded-lg border transition ${
                  data.prehire_meta.fuel_type === fuel
                    ? 'bg-[#004B8D] text-white border-[#004B8D]'
                    : 'bg-white border-slate-300 hover:border-[#004B8D]'
                }`}
                data-testid={`fuel-type-${fuel.toLowerCase()}`}
              >
                {fuel}
              </button>
            ))}
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(11)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(13)} 
              className="flex-1 bg-[#004B8D]"
              disabled={!data.prehire_meta.fuel_type}
              data-testid="step12-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 13: Fuel Policy */}
      {step === 13 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Fuel Policy</h3>
          
          <div className="space-y-2">
            {[
              'Return with full tank',
              'Return with same level',
              'Fuel included in hire',
              'Electric charging instructions provided'
            ].map(policy => (
              <button
                key={policy}
                onClick={() => setData(prev => ({
                  ...prev,
                  prehire_meta: { ...prev.prehire_meta, fuel_policy: policy }
                }))}
                className={`w-full py-3 px-4 text-left rounded-lg border transition ${
                  data.prehire_meta.fuel_policy === policy
                    ? 'bg-[#004B8D] text-white border-[#004B8D]'
                    : 'bg-white border-slate-300 hover:border-[#004B8D]'
                }`}
                data-testid={`fuel-policy-${policy.slice(0, 10).toLowerCase().replace(/ /g, '-')}`}
              >
                {policy}
              </button>
            ))}
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(12)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(14)} 
              className="flex-1 bg-[#004B8D]"
              disabled={!data.prehire_meta.fuel_policy}
              data-testid="step13-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 14: Kilometre Allowance */}
      {step === 14 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Kilometre Allowance</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Allowed Kilometres</label>
              <input
                type="number"
                value={data.prehire_meta.km_allowance}
                onChange={e => setData(prev => ({
                  ...prev,
                  prehire_meta: { ...prev.prehire_meta, km_allowance: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="e.g., 500 (leave blank for unlimited)"
                data-testid="km-allowance-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Additional Charge per km ($)</label>
              <input
                type="number"
                step="0.01"
                value={data.prehire_meta.extra_km_rate}
                onChange={e => setData(prev => ({
                  ...prev,
                  prehire_meta: { ...prev.prehire_meta, extra_km_rate: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="e.g., 0.35"
                data-testid="extra-km-rate-input"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(13)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(15)} 
              className="flex-1 bg-[#004B8D]"
              data-testid="step14-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 15: Toll Policy */}
      {step === 15 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Toll Policy</h3>
          
          <div className="space-y-2">
            {['Included in hire', 'Charged after trip'].map(policy => (
              <button
                key={policy}
                onClick={() => setData(prev => ({
                  ...prev,
                  prehire_meta: { ...prev.prehire_meta, toll_policy: policy }
                }))}
                className={`w-full py-3 px-4 text-left rounded-lg border transition ${
                  data.prehire_meta.toll_policy === policy
                    ? 'bg-[#004B8D] text-white border-[#004B8D]'
                    : 'bg-white border-slate-300 hover:border-[#004B8D]'
                }`}
                data-testid={`toll-policy-${policy.slice(0, 8).toLowerCase().replace(/ /g, '-')}`}
              >
                {policy}
              </button>
            ))}
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(14)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(16)} 
              className="flex-1 bg-[#004B8D]"
              disabled={!data.prehire_meta.toll_policy}
              data-testid="step15-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 16: Driving Features Checklist */}
      {step === 16 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Handover Acknowledgements</h3>
          
          <p className="text-sm text-slate-600 mb-4">
            Please confirm you have explained the following to the renter:
          </p>

          <div className="space-y-2">
            {[
              { key: 'height_restrictions', label: 'Height restrictions explained' },
              { key: 'emergency_equipment', label: 'Emergency equipment location shown' },
              { key: 'servicing_equipment', label: 'Servicing equipment location shown' },
              { key: 'dashboard_warnings', label: 'Dashboard warning light procedure explained' },
              { key: 'breakdown_procedure', label: 'Breakdown procedure explained' },
              { key: 'accident_procedure', label: 'Accident procedure explained' },
              { key: 'cleaning_expectations', label: 'Cleaning expectations explained' },
              { key: 'smoking_policy', label: 'Smoking policy explained' }
            ].map(item => {
              // P0 FIX: Safe access to handover_acknowledgements
              const acks = data.prehire_meta?.handover_acknowledgements || {};
              return (
                <label key={item.key} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={acks[item.key] || false}
                    onChange={e => setData(prev => ({
                      ...prev,
                      prehire_meta: {
                        ...prev.prehire_meta,
                        handover_acknowledgements: {
                          ...(prev.prehire_meta?.handover_acknowledgements || {}),
                          [item.key]: e.target.checked
                        }
                      }
                    }))}
                    className="w-5 h-5"
                    data-testid={`ack-${item.key}`}
                  />
                  <span className="text-sm text-slate-700">{item.label}</span>
                </label>
              );
            })}
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => setStep(15)} className="flex-1">Back</Button>
            <Button 
              onClick={() => setStep(17)} 
              className="flex-1 bg-[#004B8D]"
              disabled={!Object.values(data.prehire_meta?.handover_acknowledgements || {}).every(v => v === true)}
              data-testid="step16-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 17: Final Declaration & Submit - P0 FIX: Mobile-friendly scrollable layout */}
      {step === 17 && (
        <div className="flex flex-col h-full">
          <h3 className="font-semibold text-lg mb-4">Final Declaration</h3>
          
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto max-h-[55vh] sm:max-h-[60vh] pr-1 pb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-green-800 font-medium">
                Almost done! Please confirm the final declaration.
              </p>
            </div>

            <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 mb-4">
              <input
                type="checkbox"
                checked={data.prehire_meta.final_declaration}
                onChange={e => setData(prev => ({
                  ...prev,
                  prehire_meta: { ...prev.prehire_meta, final_declaration: e.target.checked }
                }))}
                className="mt-1 w-5 h-5 flex-shrink-0"
                data-testid="final-declaration-checkbox"
              />
              <span className="text-sm text-slate-700">
                I confirm that all information recorded in this pre-hire inspection is accurate at the time of handover.
              </span>
            </label>

            {/* Final Summary - Wrapped for mobile */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-sm mb-2">Checklist Summary</h4>
              <div className="text-xs space-y-1">
                <div className="flex justify-between flex-wrap gap-1">
                  <span className="text-slate-600">Exterior Photos:</span>
                  <span className="text-green-600 font-medium">{data.exterior_photos.length} ✓</span>
                </div>
                <div className="flex justify-between flex-wrap gap-1">
                  <span className="text-slate-600">Interior Photos:</span>
                  <span className="text-green-600 font-medium">{data.interior_photos.length} ✓</span>
                </div>
                <div className="flex justify-between flex-wrap gap-1">
                  <span className="text-slate-600">Odometer:</span>
                  <span className="font-medium">{data.odometer_value} km</span>
                </div>
                <div className="flex justify-between flex-wrap gap-1">
                  <span className="text-slate-600">Fuel Level:</span>
                  <span className="font-medium">{data.fuel_level}</span>
                </div>
                <div className="flex justify-between flex-wrap gap-1">
                  <span className="text-slate-600">Keys:</span>
                  <span className="font-medium">{data.keys_count}</span>
                </div>
                <div className="flex justify-between flex-wrap gap-1">
                  <span className="text-slate-600">Renter Identity:</span>
                  <span className="text-green-600 font-medium">Confirmed ✓</span>
                </div>
                <div className="flex justify-between flex-wrap gap-1">
                  <span className="text-slate-600">Licence:</span>
                  <span className="text-green-600 font-medium">Captured ✓</span>
                </div>
                <div className="flex justify-between flex-wrap gap-1">
                  <span className="text-slate-600">Licence Class:</span>
                  <span className="font-medium">{data.prehire_meta.licence_class || '-'}</span>
                </div>
                <div className="flex justify-between flex-wrap gap-1">
                  <span className="text-slate-600">Fuel Type:</span>
                  <span className="font-medium">{data.prehire_meta.fuel_type || '-'}</span>
                </div>
                <div className="flex justify-between flex-wrap gap-1">
                  <span className="text-slate-600">Fuel Policy:</span>
                  <span className="font-medium truncate max-w-[140px]">{data.prehire_meta.fuel_policy || '-'}</span>
                </div>
                <div className="flex justify-between flex-wrap gap-1">
                  <span className="text-slate-600">Toll Policy:</span>
                  <span className="font-medium truncate max-w-[140px]">{data.prehire_meta.toll_policy || '-'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky action buttons at bottom */}
          <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-slate-200 mt-2 bg-white">
            <Button variant="outline" onClick={() => setStep(16)} className="flex-1 h-10">
              Back
            </Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={loading} className="h-10">
              Save Draft
            </Button>
            <Button 
              onClick={handleSubmit} 
              className="flex-1 bg-green-600 hover:bg-green-700 h-10"
              disabled={loading || !data.prehire_meta.final_declaration}
              data-testid="submit-prehire-btn"
            >
              {loading ? 'Submitting...' : 'Submit Checklist'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

// ============================================================================
// POST-HIRE CHECKLIST WIZARD COMPONENT
// ============================================================================

const PostHireChecklistWizard = React.forwardRef(({ booking, prehireData, onComplete, onCancel }, ref) => {
  const { api } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [savingDraft, setSavingDraft] = useState(false);
  
  // Total steps in the canonical 6-step wizard
  const TOTAL_STEPS = 6;
  
  // P0 CANONICAL: Store thumbnail URLs for all photo types
  const [photoThumbnails, setPhotoThumbnails] = useState({
    return_exterior: [],
    return_interior: [],
    return_odometer: null,
    return_fuel: null,
    fuel_receipt: null,
    damage_photos: [],
    return_issue: []
  });
  
  const [data, setData] = useState({
    // Step 1 & 2: Photos
    return_exterior_photos: [],
    return_interior_photos: [],
    // Step 3: Odometer & Fuel
    return_odometer_photo: '',
    return_odometer_value: '',
    return_fuel_photo: '',
    return_fuel_level: 'full',
    host_refuelled: false,
    fuel_charge_amount: 0,
    fuel_receipt_photo: '',
    // Step 4: Damage
    new_damage_observed: false,
    damage_description: '',
    damage_photos: [],
    // Step 5: Return Confirmation
    keys_retrieved: true,
    vehicle_returned_to_location: true,
    vehicle_abandoned: false,
    return_issue_explanation: '',
    return_issue_photos: [],
    // Step 6: Charges
    cleaning_required: false,
    cleaning_fee: 0,
    toll_charges: 0,
    late_fee: 0,
    notes: ''
  });
  const [errors, setErrors] = useState({});

  // P0 CRITICAL FIX: Use ref to track latest data for immediate saves after state updates
  const dataRef = React.useRef(data);
  React.useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const FUEL_LEVELS = ['empty', '1/4', '1/2', '3/4', 'full'];
  const REQUIRED_EXTERIOR = 6;
  const REQUIRED_INTERIOR = 6;

  // P0 FIX: Safe string helper to prevent crashes
  const safeStartsWith = (str, prefix) => {
    if (typeof str !== 'string') return false;
    return str.startsWith(prefix);
  };
  
  // P0 BUGFIX: Safe string normalizer
  const normalizeString = (val) => {
    if (typeof val === 'string' && val.trim()) return val;
    return null;
  };
  
  // P0 BUGFIX: Filter out pending uploads
  const filterPendingPhotos = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.filter(k => typeof k === 'string' && !safeStartsWith(k, 'pending_'));
  };

  // P0 CRITICAL FIX: Save draft to backend on every step
  const saveDraftSilently = async (currentData) => {
    if (!booking?.booking_id) return;
    
    try {
      setSavingDraft(true);
      
      // Prepare save payload - include ALL data including photos
      const savePayload = {
        return_exterior_photos: filterPendingPhotos(currentData.return_exterior_photos),
        return_interior_photos: filterPendingPhotos(currentData.return_interior_photos),
        return_odometer_photo: normalizeString(currentData.return_odometer_photo),
        return_odometer_value: currentData.return_odometer_value ? parseInt(currentData.return_odometer_value) : null,
        return_fuel_photo: normalizeString(currentData.return_fuel_photo),
        return_fuel_level: normalizeString(currentData.return_fuel_level),
        host_refuelled: !!currentData.host_refuelled,
        fuel_charge_amount: parseFloat(currentData.fuel_charge_amount) || null,
        fuel_receipt_photo: normalizeString(currentData.fuel_receipt_photo),
        new_damage_observed: !!currentData.new_damage_observed,
        damage_description: normalizeString(currentData.damage_description),
        damage_photos: filterPendingPhotos(currentData.damage_photos),
        keys_retrieved: currentData.keys_retrieved !== false,
        vehicle_returned_to_location: currentData.vehicle_returned_to_location !== false,
        vehicle_abandoned: !!currentData.vehicle_abandoned,
        return_issue_explanation: normalizeString(currentData.return_issue_explanation),
        return_issue_photos: filterPendingPhotos(currentData.return_issue_photos),
        cleaning_required: !!currentData.cleaning_required,
        cleaning_fee: parseFloat(currentData.cleaning_fee) || null,
        toll_charges: parseFloat(currentData.toll_charges) || null,
        late_fee: parseFloat(currentData.late_fee) || null,
        notes: normalizeString(currentData.notes)
      };
      
      await api.post(`/bookings/${booking.booking_id}/posthire/save`, savePayload);
    } catch (error) {
    } finally {
      setSavingDraft(false);
    }
  };

  // P0 FIX: Normalize API errors to strings only with deduplication
  const normalizeApiError = (err) => {
    if (typeof err === 'string') return err;
    if (err?.response?.data) {
      const data = err.response.data;
      if (typeof data === 'string') return data;
      if (data.detail !== undefined) {
        const detail = data.detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) {
          // Dedupe messages
          const uniqueMessages = new Set();
          detail.forEach(item => {
            const msg = typeof item === 'string' ? item : item?.msg || '';
            if (msg) uniqueMessages.add(msg);
          });
          if (uniqueMessages.size === 1 && detail.length > 1) {
            return `Validation error: ${Array.from(uniqueMessages)[0]}`;
          }
          if (uniqueMessages.size > 3) {
            return 'Some fields are invalid. Please review highlighted items.';
          }
          return Array.from(uniqueMessages).join('; ');
        }
        if (typeof detail === 'object') return JSON.stringify(detail);
      }
      return JSON.stringify(data);
    }
    if (err?.message) return err.message;
    return 'Something went wrong';
  };

  // P0 UNIFIED: Same upload concurrency limiter as Pre-Hire
  const uploadWithLimit = async (files, limit, uploadFn) => {
    const results = [];
    const executing = [];
    
    for (const file of files) {
      const promise = uploadFn(file).then(result => {
        executing.splice(executing.indexOf(promise), 1);
        return { status: 'fulfilled', value: result, file };
      }).catch(error => {
        executing.splice(executing.indexOf(promise), 1);
        return { status: 'rejected', reason: error, file };
      });
      
      results.push(promise);
      executing.push(promise);
      
      if (executing.length >= limit) {
        await Promise.race(executing);
      }
    }
    
    return Promise.all(results);
  };

  // P0 CANONICAL: Fetch signed URL for thumbnail
  const fetchSignedUrl = async (s3Key) => {
    if (!s3Key || !booking?.booking_id) return null;
    try {
      const encodedKey = encodeURIComponent(s3Key);
      const response = await api.get(`/uploads/checklists/${booking.booking_id}/${encodedKey}`);
      return response.data?.url || null;
    } catch (error) {
      return null;
    }
  };

  // P0 CANONICAL: Load existing photos on mount (resume support)
  useEffect(() => {
    const loadExistingPhotos = async () => {
      if (!booking?.booking_id) return;
      
      try {
        const response = await api.get(`/uploads/checklists/${booking.booking_id}/photos`, {
          params: { checklist_type: 'posthire' }
        });
        
        const photos = response.data?.photos || [];
        
        // Group photos by type
        // P0 FIX: Use canonical photo_types - both prehire and posthire use same types
        // Differentiated by checklist_type only
        const exterior = photos.filter(p => p.photo_type === 'exterior');
        const interior = photos.filter(p => p.photo_type === 'interior');
        const odometer = photos.find(p => p.photo_type === 'odometer');
        const fuel = photos.find(p => p.photo_type === 'fuel');
        const fuelReceipt = photos.find(p => p.photo_type === 'fuel_receipt');
        const damage = photos.filter(p => p.photo_type === 'damage');
        const returnIssue = photos.filter(p => p.photo_type === 'return_issue');
        
        setData(prev => ({
          ...prev,
          return_exterior_photos: exterior.map(p => p.s3_key),
          return_interior_photos: interior.map(p => p.s3_key),
          return_odometer_photo: odometer?.s3_key || '',
          return_fuel_photo: fuel?.s3_key || '',
          fuel_receipt_photo: fuelReceipt?.s3_key || '',
          damage_photos: damage.map(p => p.s3_key),
          return_issue_photos: returnIssue.map(p => p.s3_key)
        }));
        
        setPhotoThumbnails({
          return_exterior: exterior.map(p => ({ s3_key: p.s3_key, thumbnail_url: p.thumbnail_url })),
          return_interior: interior.map(p => ({ s3_key: p.s3_key, thumbnail_url: p.thumbnail_url })),
          return_odometer: odometer ? { s3_key: odometer.s3_key, thumbnail_url: odometer.thumbnail_url } : null,
          return_fuel: fuel ? { s3_key: fuel.s3_key, thumbnail_url: fuel.thumbnail_url } : null,
          fuel_receipt: fuelReceipt ? { s3_key: fuelReceipt.s3_key, thumbnail_url: fuelReceipt.thumbnail_url } : null,
          damage_photos: damage.map(p => ({ s3_key: p.s3_key, thumbnail_url: p.thumbnail_url })),
          return_issue: returnIssue.map(p => ({ s3_key: p.s3_key, thumbnail_url: p.thumbnail_url }))
        });
        
      } catch (error) {
      }
    };
    
    // Also load saved draft data from report
    const loadDraftData = async () => {
      if (!booking?.booking_id) return;
      try {
        const response = await api.get(`/bookings/${booking.booking_id}/posthire`);
        const report = response.data?.report;
        if (report && report.status === 'draft') {
          setData(prev => ({
            ...prev,
            // P0 BUGFIX: Also load photo arrays from saved draft
            return_exterior_photos: report.return_exterior_photos?.length > 0 
              ? report.return_exterior_photos 
              : prev.return_exterior_photos,
            return_interior_photos: report.return_interior_photos?.length > 0 
              ? report.return_interior_photos 
              : prev.return_interior_photos,
            return_odometer_photo: report.return_odometer_photo || prev.return_odometer_photo,
            return_fuel_photo: report.return_fuel_photo || prev.return_fuel_photo,
            fuel_receipt_photo: report.fuel_receipt_photo || prev.fuel_receipt_photo,
            damage_photos: report.damage_photos?.length > 0 
              ? report.damage_photos 
              : prev.damage_photos,
            return_issue_photos: report.return_issue_photos?.length > 0 
              ? report.return_issue_photos 
              : prev.return_issue_photos,
            // Non-photo fields
            return_odometer_value: report.return_odometer_value || '',
            return_fuel_level: report.return_fuel_level || 'full',
            host_refuelled: report.host_refuelled || false,
            fuel_charge_amount: report.fuel_charge_amount || 0,
            new_damage_observed: report.new_damage_observed || false,
            damage_description: report.damage_description || '',
            keys_retrieved: report.keys_retrieved !== false,
            vehicle_returned_to_location: report.vehicle_returned_to_location !== false,
            vehicle_abandoned: report.vehicle_abandoned || false,
            return_issue_explanation: report.return_issue_explanation || '',
            cleaning_required: report.cleaning_required || false,
            cleaning_fee: report.cleaning_fee || 0,
            toll_charges: report.toll_charges || 0,
            late_fee: report.late_fee || 0,
            notes: report.notes || ''
          }));
          
          // P0 BUGFIX: Load thumbnails from draft photos - MUST return objects with { s3_key, thumbnail_url }
          const fetchThumbnailsFromKeys = async (keys) => {
            if (!keys || keys.length === 0) return [];
            const results = await Promise.all(keys.map(async (key) => {
              try {
                const url = await fetchSignedUrl(key);
                // P0 FIX: Return object with s3_key and thumbnail_url, not just URL string
                return url ? { s3_key: key, thumbnail_url: url } : null;
              } catch {
                return null;
              }
            }));
            return results.filter(Boolean);
          };
          
          // P0 FIX: Helper for single photos - returns object { s3_key, thumbnail_url }
          const fetchSingleThumbnail = async (key) => {
            if (!key) return null;
            try {
              const url = await fetchSignedUrl(key);
              return url ? { s3_key: key, thumbnail_url: url } : null;
            } catch {
              return null;
            }
          };
          
          // Load thumbnails for all photo arrays from draft - use proper object structure
          if (report.return_exterior_photos?.length > 0) {
            const thumbnails = await fetchThumbnailsFromKeys(report.return_exterior_photos);
            if (thumbnails.length > 0) {
              setPhotoThumbnails(prev => ({ ...prev, return_exterior: thumbnails }));
            }
          }
          if (report.return_interior_photos?.length > 0) {
            const thumbnails = await fetchThumbnailsFromKeys(report.return_interior_photos);
            if (thumbnails.length > 0) {
              setPhotoThumbnails(prev => ({ ...prev, return_interior: thumbnails }));
            }
          }
          // P0 FIX: Single photos must also be objects with { s3_key, thumbnail_url }
          if (report.return_odometer_photo) {
            const thumb = await fetchSingleThumbnail(report.return_odometer_photo);
            if (thumb) setPhotoThumbnails(prev => ({ ...prev, return_odometer: thumb }));
          }
          if (report.return_fuel_photo) {
            const thumb = await fetchSingleThumbnail(report.return_fuel_photo);
            if (thumb) setPhotoThumbnails(prev => ({ ...prev, return_fuel: thumb }));
          }
          if (report.fuel_receipt_photo) {
            const thumb = await fetchSingleThumbnail(report.fuel_receipt_photo);
            if (thumb) setPhotoThumbnails(prev => ({ ...prev, fuel_receipt: thumb }));
          }
          if (report.damage_photos?.length > 0) {
            const thumbnails = await fetchThumbnailsFromKeys(report.damage_photos);
            if (thumbnails.length > 0) {
              setPhotoThumbnails(prev => ({ ...prev, damage_photos: thumbnails }));
            }
          }
          if (report.return_issue_photos?.length > 0) {
            const thumbnails = await fetchThumbnailsFromKeys(report.return_issue_photos);
            if (thumbnails.length > 0) {
              setPhotoThumbnails(prev => ({ ...prev, return_issue: thumbnails }));
            }
          }
          
        }
      } catch (error) {
      }
    };
    
    // P0 FIX: Sequential loading - loadDraftData must run AFTER loadExistingPhotos completes
    // to avoid race condition where draft loading reads prev state before photos are loaded
    const loadAll = async () => {
      await loadExistingPhotos();
      await loadDraftData();
    };
    loadAll();
  }, [booking?.booking_id, api]);

  // P0 UNIFIED: Upload with thumbnail support - same error handling as Pre-Hire
  const uploadFileToS3 = async (file, photoType) => {
    try {
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('booking_id', booking.booking_id);
      formData.append('checklist_type', 'posthire');
      formData.append('photo_type', photoType);

      const uploadResponse = await api.post('/uploads/checklists/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      const { s3_key, thumbnail_key, photo_id, file_size, original_size } = uploadResponse.data;
      
      // P0 CANONICAL: Immediately fetch signed URL for thumbnail
      let thumbnail_url = null;
      if (thumbnail_key) {
        thumbnail_url = await fetchSignedUrl(thumbnail_key);
      }
      if (!thumbnail_url && s3_key) {
        thumbnail_url = await fetchSignedUrl(s3_key);
      }
      
      return { s3_key, thumbnail_key, thumbnail_url, photo_id };
    } catch (error) {
      console.error('[PostHire S3 Upload] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const detail = error.response?.data?.detail || error.message;
      if (typeof detail === 'string') {
        if (detail.includes('Empty file')) {
          throw new Error('Photo capture failed - please try again');
        } else if (detail.includes('HEIC') || detail.includes('heif')) {
          throw new Error('iPhone photo format not supported - try PNG or JPG');
        } else if (detail.includes('too large')) {
          throw new Error('Photo too large (max 50MB)');
        }
      }
      if (error.code === 'ECONNABORTED') {
        throw new Error('Upload timed out - check your connection');
      }
      
      throw error;
    }
  };

  // P0 UNIFIED: Handle multiple photo uploads with PARALLEL uploads + LOCAL PREVIEWS (same as Pre-Hire)
  const handlePhotoUpload = async (files, type, photoType, thumbKey) => {
    const fileArray = Array.from(files);
    
    if (fileArray.length === 0) return;
    
    // Step 1: IMMEDIATELY show local previews for all files
    const localPreviews = fileArray.map((file, idx) => ({
      s3_key: `pending_${Date.now()}_${idx}`,
      thumbnail_url: URL.createObjectURL(file),
      isLocalPreview: true,
      isUploading: true,
      file
    }));
    
    // Add local previews to thumbnails immediately
    setPhotoThumbnails(prev => ({
      ...prev,
      [thumbKey]: [...prev[thumbKey], ...localPreviews]
    }));
    
    // Add placeholder s3_keys to data
    setData(prev => ({
      ...prev,
      [type]: [...prev[type], ...localPreviews.map(p => p.s3_key)]
    }));
    
    setLoading(true);
    
    // Step 2: Upload files in parallel with concurrency limit of 3
    const uploadSingleFile = async (file) => {
      const result = await uploadFileToS3(file, photoType);
      return result;
    };
    
    const results = await uploadWithLimit(fileArray, 3, uploadSingleFile);
    
    // Step 3: Process results and update state
    const successful = [];
    const failedIndices = [];
    
    results.forEach((result, idx) => {
      if (result.status === 'fulfilled') {
        successful.push({ ...result.value, originalIndex: idx });
      } else {
        failedIndices.push(idx);
        const errorMsg = normalizeApiError(result.reason);
        toast.error(`Failed to upload ${fileArray[idx].name}: ${errorMsg}`);
      }
    });
    
    // Clean up local preview URLs for successful uploads
    localPreviews.forEach(preview => {
      if (preview.thumbnail_url && preview.isLocalPreview) {
        URL.revokeObjectURL(preview.thumbnail_url);
      }
    });
    
    // Update data with real s3_keys (remove pending, add real ones)
    setData(prev => {
      const existingPhotos = (prev[type] || []).filter(key => !safeStartsWith(key, 'pending_'));
      return {
        ...prev,
        [type]: [...existingPhotos, ...successful.map(s => s.s3_key)]
      };
    });
    
    // Update thumbnails with real URLs (remove pending, add real ones)
    setPhotoThumbnails(prev => {
      const existingThumbs = prev[thumbKey].filter(t => !t.isLocalPreview);
      return {
        ...prev,
        [thumbKey]: [...existingThumbs, ...successful.map(s => ({ 
          s3_key: s.s3_key, 
          thumbnail_url: s.thumbnail_url 
        }))]
      };
    });
    
    setLoading(false);
    
    if (successful.length > 0) {
      toast.success(`Uploaded ${successful.length} photo(s)`);
      
      // P0 CRITICAL FIX: Immediately save draft after successful uploads
      // This ensures photos are persisted before user navigates or submits
      // Use setTimeout to allow state to settle, then use ref for latest data
      setTimeout(() => {
        saveDraftSilently(dataRef.current);
      }, 200);
    }
  };

  // P0 UNIFIED: Handle single photo upload with proper error handling (same as Pre-Hire)
  const handleSinglePhotoUpload = async (file, field, photoType, thumbKey) => {
    setLoading(true);
    setUploadProgress(prev => ({ ...prev, [field]: 'uploading' }));
    
    try {
      const result = await uploadFileToS3(file, photoType);
      setData(prev => ({ ...prev, [field]: result.s3_key }));
      
      // Update thumbnail
      setPhotoThumbnails(prev => ({
        ...prev,
        [thumbKey]: { s3_key: result.s3_key, thumbnail_url: result.thumbnail_url }
      }));
      
      setUploadProgress(prev => ({ ...prev, [field]: 'done' }));
      toast.success(`${photoType.replace(/_/g, ' ')} photo uploaded`);
      
      // P0 CRITICAL FIX: Immediately save draft after successful upload
      setTimeout(() => saveDraftSilently(dataRef.current), 200);
    } catch (error) {
      setUploadProgress(prev => ({ ...prev, [field]: 'error' }));
      const errorMsg = normalizeApiError(error);
      toast.error(`Failed to upload ${photoType.replace(/_/g, ' ')} photo: ${errorMsg}`);
      console.error(`[PostHire Upload] Failed for ${photoType}:`, error);
    } finally {
      setLoading(false);
    }
  };

  // P0 CANONICAL: Remove photo and its thumbnail
  const removePhoto = (type, thumbKey, index) => {
    setData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
    
    setPhotoThumbnails(prev => ({
      ...prev,
      [thumbKey]: prev[thumbKey].filter((_, i) => i !== index)
    }));
  };

  const removeSinglePhoto = (field, thumbKey) => {
    setData(prev => ({ ...prev, [field]: '' }));
    setPhotoThumbnails(prev => ({ ...prev, [thumbKey]: null }));
  };

  // File input refs
  const exteriorInputRef = React.useRef(null);
  const interiorInputRef = React.useRef(null);
  const odometerInputRef = React.useRef(null);
  const fuelInputRef = React.useRef(null);
  const fuelReceiptInputRef = React.useRef(null);
  const damageInputRef = React.useRef(null);
  const returnIssueInputRef = React.useRef(null);

  // P0 FIX: Handle cancel - just close
  const handleCancel = () => {
    onCancel();
  };

  // P0 FIX: Expose method to parent via ref - ACTUALLY SAVE DRAFT
  React.useImperativeHandle(ref, () => ({
    saveDraftBeforeClose: () => {
      saveDraftSilently(data);
    }
  }), [data]);
  
  // P0 FIX: Autosave on data changes (debounced 700ms)
  const autosaveTimeoutRef = React.useRef(null);
  const lastSavedDataRef = React.useRef(null);
  const isFirstRenderRef = React.useRef(true);
  
  React.useEffect(() => {
    // Skip the first render to prevent saving on initial load
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      lastSavedDataRef.current = JSON.stringify(data);
      return;
    }
    
    // Skip when data hasn't meaningfully changed
    const currentDataStr = JSON.stringify(data);
    if (lastSavedDataRef.current === currentDataStr) return;
    
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    
    autosaveTimeoutRef.current = setTimeout(() => {
      saveDraftSilently(data);
      lastSavedDataRef.current = currentDataStr;
    }, 700);
    
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [data]);
  
  // P0 FIX: Save on step navigation
  const goToNextStep = (nextStep) => {
    saveDraftSilently(data);
    setStep(nextStep);
  };
  
  const goToPrevStep = (prevStep) => {
    saveDraftSilently(data);
    setStep(prevStep);
  };

  // Calculate fuel difference
  const getFuelDifference = () => {
    if (!prehireData?.fuel_level) return null;
    const fuelValues = { 'empty': 0, '1/4': 25, '1/2': 50, '3/4': 75, 'full': 100 };
    const startFuel = fuelValues[prehireData.fuel_level] || 100;
    const returnFuel = fuelValues[data.return_fuel_level] || 100;
    return startFuel - returnFuel;
  };

  const fuelDiff = getFuelDifference();
  const showFuelCharge = fuelDiff !== null && fuelDiff > 0;

  // Check if return confirmation has issues
  const hasReturnIssues = !data.keys_retrieved || !data.vehicle_returned_to_location || data.vehicle_abandoned;

  // Calculate excess KMs
  const getExcessKms = () => {
    const startOdo = prehireData?.odometer_value || 0;
    const returnOdo = parseInt(data.return_odometer_value) || 0;
    const totalKms = Math.max(0, returnOdo - startOdo);
    const kmsIncluded = (booking?.total_days || 1) * 200; // Default 200km/day
    return Math.max(0, totalKms - kmsIncluded);
  };

  const handleSubmit = async () => {
    // P0 DEV LOGGING - Task A: Prove photo state before submit
    if (process.env.NODE_ENV === 'development') {
    }
    
    const newErrors = {};
    if (data.return_exterior_photos.length < REQUIRED_EXTERIOR) {
      newErrors.exterior = `Minimum ${REQUIRED_EXTERIOR} photos required`;
    }
    if (data.return_interior_photos.length < REQUIRED_INTERIOR) {
      newErrors.interior = `Minimum ${REQUIRED_INTERIOR} photos required`;
    }
    if (!data.return_odometer_photo) newErrors.odometer_photo = 'Required';
    if (!data.return_odometer_value || parseInt(data.return_odometer_value) <= 0) {
      newErrors.odometer_value = 'Required';
    }
    if (!data.return_fuel_photo) newErrors.fuel_photo = 'Required';
    
    // Validate fuel charge if host refuelled
    if (data.host_refuelled && (!data.fuel_charge_amount || data.fuel_charge_amount <= 0)) {
      newErrors.fuel_charge = 'Fuel charge amount required';
    }
    if (data.host_refuelled && !data.fuel_receipt_photo) {
      newErrors.fuel_receipt = 'Fuel receipt photo required';
    }
    
    // Validate damage if observed
    if (data.new_damage_observed && !data.damage_description) {
      newErrors.damage_description = 'Damage description required';
    }
    if (data.new_damage_observed && data.damage_photos.length === 0) {
      newErrors.damage_photos = 'At least one damage photo required';
    }
    
    // Validate return issues
    if (hasReturnIssues && !data.return_issue_explanation) {
      newErrors.return_issue = 'Explanation required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please complete all required fields');
      return;
    }

    setLoading(true);
    try {
      // P0 TASK B: Save draft first, then fetch saved draft as source of truth
      await saveDraftSilently(data);
      
      // Fetch the saved draft from backend to ensure we use persisted data
      let draftData = null;
      try {
        const draftResponse = await api.get(`/bookings/${booking.booking_id}/posthire`);
        draftData = draftResponse.data?.report;
        
        if (process.env.NODE_ENV === 'development') {
        }
      } catch (err) {
      }
      
      // Use draft data if available, otherwise fall back to local state
      const sourceData = (draftData && draftData.return_exterior_photos?.length > 0) ? draftData : data;
      
      // P0 FIX: Filter out any pending local preview items (start with 'pending_')
      const filterPending = (arr) => (arr || []).filter(key => typeof key === 'string' && !key.startsWith('pending_'));
      
      // P0 FIX: Ensure all required string fields have valid values (prevents "Input should be a valid string" errors)
      const safeString = (val, fallback = '') => (typeof val === 'string' && val.trim()) ? val : fallback;
      
      const submitData = {
        return_exterior_photos: filterPending(sourceData.return_exterior_photos),
        return_interior_photos: filterPending(sourceData.return_interior_photos),
        return_odometer_photo: safeString(sourceData.return_odometer_photo),
        return_odometer_value: parseInt(sourceData.return_odometer_value) || 0,
        return_fuel_photo: safeString(sourceData.return_fuel_photo),
        return_fuel_level: safeString(sourceData.return_fuel_level, 'full'),
        host_refuelled: !!sourceData.host_refuelled,
        fuel_charge_amount: parseFloat(sourceData.fuel_charge_amount) || 0,
        fuel_receipt_photo: safeString(sourceData.fuel_receipt_photo) || null,
        new_damage_observed: !!sourceData.new_damage_observed,
        damage_description: safeString(sourceData.damage_description) || null,
        damage_photos: filterPending(sourceData.damage_photos),
        keys_retrieved: sourceData.keys_retrieved !== false,
        vehicle_returned_to_location: sourceData.vehicle_returned_to_location !== false,
        vehicle_abandoned: !!sourceData.vehicle_abandoned,
        return_issue_explanation: safeString(sourceData.return_issue_explanation) || null,
        return_issue_photos: filterPending(sourceData.return_issue_photos),
        cleaning_required: !!sourceData.cleaning_required,
        cleaning_fee: parseFloat(sourceData.cleaning_fee) || 0,
        toll_charges: parseFloat(sourceData.toll_charges) || 0,
        late_fee: parseFloat(sourceData.late_fee) || 0,
        notes: safeString(sourceData.notes) || null
      };
      
      // P0 DEV LOGGING - Task A: Log exact payload
      if (process.env.NODE_ENV === 'development') {
      }
      
      await api.post(`/bookings/${booking.booking_id}/posthire/submit`, submitData);
      toast.success('Post-hire report submitted! The renter now has 48 hours to review any charges.');
      onComplete();
    } catch (error) {
      console.error('[PostHire Submit] Error:', error.response?.data);
      toast.error(normalizeApiError(error) || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  // Render photo thumbnail grid
  const renderPhotoGrid = (photos, thumbnails, type, thumbKey, onRemove) => (
    <div className="grid grid-cols-3 gap-2 mb-4">
      {photos.map((photo, idx) => {
        const thumbData = thumbnails[idx];
        return (
          <div key={idx} className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative group">
            {thumbData?.thumbnail_url ? (
              <img 
                src={thumbData.thumbnail_url} 
                alt={`Photo ${idx + 1}`}
                className="w-full h-full object-cover"
                data-testid={`${type}-thumbnail-${idx}`}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            )}
            <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5 opacity-80">
              <CheckCircle className="w-3 h-3 text-white" />
            </div>
            <button
              onClick={() => onRemove(type, thumbKey, idx)}
              className="absolute top-1 left-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center text-xs"
            >×</button>
          </div>
        );
      })}
    </div>
  );

  // Render single photo upload area
  const renderSinglePhotoUpload = (field, thumbKey, label, inputRef, photoType) => (
    <div>
      <label className="block text-sm font-medium mb-2">{label} *</label>
      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
        {data[field] ? (
          <div className="flex flex-col items-center gap-2">
            {photoThumbnails[thumbKey]?.thumbnail_url ? (
              <div className="relative">
                <img 
                  src={photoThumbnails[thumbKey].thumbnail_url} 
                  alt={label}
                  className="w-32 h-24 object-cover rounded-lg border border-green-300"
                  data-testid={`${thumbKey}-thumbnail`}
                />
                <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700">Photo uploaded</span>
              </div>
            )}
            <button 
              onClick={() => removeSinglePhoto(field, thumbKey)} 
              className="text-red-500 text-sm hover:text-red-700"
            >
              Remove
            </button>
          </div>
        ) : (
          <Button 
            onClick={() => inputRef.current?.click()}
            variant="outline"
            size="sm"
          >
            {uploadProgress[field] === 'uploading' ? 'Uploading...' : <><Camera className="w-4 h-4 mr-2" />Take Photo</>}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* P0 DEV PROOF BANNER - Platform Owner Only */}
      {booking?.is_test && (
        <div className="bg-yellow-100 border border-yellow-400 rounded p-2 text-xs font-mono" data-testid="dev-viewport-banner">
          <div>Viewport: {typeof window !== 'undefined' ? `${window.innerWidth}×${window.innerHeight}` : 'SSR'}</div>
          <div>Step: {step}/{TOTAL_STEPS}</div>
        </div>
      )}
      
      {/* Hidden file inputs - P0 UNIFIED: Added capture="environment" for mobile camera (same as Pre-Hire) */}
      {/* P0 FIX: Use canonical photo_types (exterior, interior, odometer, fuel, damage) - NOT post_* variants */}
      <input type="file" ref={exteriorInputRef} accept="image/*" capture="environment" multiple className="hidden"
        onChange={(e) => handlePhotoUpload(e.target.files, 'return_exterior_photos', 'exterior', 'return_exterior')} />
      <input type="file" ref={interiorInputRef} accept="image/*" capture="environment" multiple className="hidden"
        onChange={(e) => handlePhotoUpload(e.target.files, 'return_interior_photos', 'interior', 'return_interior')} />
      <input type="file" ref={odometerInputRef} accept="image/*" capture="environment" className="hidden"
        onChange={(e) => e.target.files[0] && handleSinglePhotoUpload(e.target.files[0], 'return_odometer_photo', 'odometer', 'return_odometer')} />
      <input type="file" ref={fuelInputRef} accept="image/*" capture="environment" className="hidden"
        onChange={(e) => e.target.files[0] && handleSinglePhotoUpload(e.target.files[0], 'return_fuel_photo', 'fuel', 'return_fuel')} />
      <input type="file" ref={fuelReceiptInputRef} accept="image/*" capture="environment" className="hidden"
        onChange={(e) => e.target.files[0] && handleSinglePhotoUpload(e.target.files[0], 'fuel_receipt_photo', 'fuel_receipt', 'fuel_receipt')} />
      <input type="file" ref={damageInputRef} accept="image/*" capture="environment" multiple className="hidden"
        onChange={(e) => handlePhotoUpload(e.target.files, 'damage_photos', 'damage', 'damage_photos')} />
      <input type="file" ref={returnIssueInputRef} accept="image/*" capture="environment" multiple className="hidden"
        onChange={(e) => handlePhotoUpload(e.target.files, 'return_issue_photos', 'return_issue', 'return_issue')} />

      {/* Step Progress Bar */}
      <div className="flex items-center justify-between mb-6">
        {[1, 2, 3, 4, 5, 6].map(s => (
          <div key={s} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              s <= step ? 'bg-orange-600 text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              {s}
            </div>
            {s < TOTAL_STEPS && <div className={`w-8 h-1 mx-1 ${s < step ? 'bg-orange-600' : 'bg-slate-200'}`} />}
          </div>
        ))}
      </div>

      {/* Pre-Hire Reference (shown on all steps) */}
      {prehireData && (
        <div className="bg-blue-50 rounded-lg p-3 mb-4">
          <h4 className="font-medium text-sm mb-1 flex items-center gap-2">
            <Info className="w-4 h-4" />
            Pre-Hire Reference
          </h4>
          <div className="text-xs text-slate-600 grid grid-cols-2 gap-2">
            <span>Start Odometer: {prehireData.odometer_value} km</span>
            <span>Start Fuel: {prehireData.fuel_level}</span>
          </div>
        </div>
      )}

      {/* STEP 1: Return Exterior Photos */}
      {step === 1 && (
        <div>
          <h3 className="font-semibold text-lg mb-2">Step 1: Return Exterior Photos</h3>
          <p className="text-sm text-slate-600 mb-2">Document vehicle exterior condition on return (minimum {REQUIRED_EXTERIOR})</p>
          {/* HireHub Protection Micro Warning */}
          <p className="text-xs text-orange-600 bg-orange-50 px-3 py-1.5 rounded-md mb-4 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Post-hire photos are essential for damage claim evidence</span>
          </p>
          
          {renderPhotoGrid(data.return_exterior_photos, photoThumbnails.return_exterior, 'return_exterior_photos', 'return_exterior', removePhoto)}

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
            <Camera className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-600 mb-2">
              {data.return_exterior_photos.length}/{REQUIRED_EXTERIOR} photos
              {data.return_exterior_photos.length >= REQUIRED_EXTERIOR && <span className="text-green-600 ml-1">✓</span>}
            </p>
            <Button onClick={() => exteriorInputRef.current?.click()} variant="outline" size="sm" data-testid="upload-return-exterior-btn">
              {loading ? 'Uploading...' : <><Upload className="w-4 h-4 mr-2" />Add Photos</>}
            </Button>
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={handleCancel} className="flex-1">Cancel</Button>
            <Button onClick={() => goToNextStep(2)} className="flex-1 bg-orange-600 hover:bg-orange-700"
              disabled={data.return_exterior_photos.length < REQUIRED_EXTERIOR}>
              Next ({data.return_exterior_photos.length}/{REQUIRED_EXTERIOR})
            </Button>
          </div>
        </div>
      )}

      {/* STEP 2: Return Interior Photos */}
      {step === 2 && (
        <div>
          <h3 className="font-semibold text-lg mb-2">Step 2: Return Interior Photos</h3>
          <p className="text-sm text-slate-600 mb-4">Minimum {REQUIRED_INTERIOR} photos</p>
          
          {renderPhotoGrid(data.return_interior_photos, photoThumbnails.return_interior, 'return_interior_photos', 'return_interior', removePhoto)}

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
            <Camera className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-600 mb-2">
              {data.return_interior_photos.length}/{REQUIRED_INTERIOR} photos
              {data.return_interior_photos.length >= REQUIRED_INTERIOR && <span className="text-green-600 ml-1">✓</span>}
            </p>
            <Button onClick={() => interiorInputRef.current?.click()} variant="outline" size="sm" data-testid="upload-return-interior-btn">
              {loading ? 'Uploading...' : <><Upload className="w-4 h-4 mr-2" />Add Photos</>}
            </Button>
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => goToPrevStep(1)} className="flex-1">Back</Button>
            <Button onClick={() => goToNextStep(3)} className="flex-1 bg-orange-600 hover:bg-orange-700"
              disabled={data.return_interior_photos.length < REQUIRED_INTERIOR}>
              Next ({data.return_interior_photos.length}/{REQUIRED_INTERIOR})
            </Button>
          </div>
        </div>
      )}

      {/* STEP 3: Return Odometer & Fuel */}
      {step === 3 && (
        <div>
          <h3 className="font-semibold text-lg mb-2">Step 3: Return Odometer & Fuel</h3>
          {/* HireHub Protection Micro Warning */}
          <p className="text-xs text-orange-600 bg-orange-50 px-3 py-1.5 rounded-md mb-4 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Accurate odometer & fuel data supports excess KM and fuel claims</span>
          </p>
          
          <div className="space-y-4">
            {renderSinglePhotoUpload('return_odometer_photo', 'return_odometer', 'Return Odometer Photo', odometerInputRef, 'odometer')}

            <div>
              <label className="block text-sm font-medium mb-2">Return Odometer Reading (km) *</label>
              <input
                type="number"
                value={data.return_odometer_value}
                onChange={e => setData(prev => ({ ...prev, return_odometer_value: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="e.g., 45500"
              />
              {prehireData?.odometer_value && data.return_odometer_value && (
                <p className="text-xs text-slate-500 mt-1">
                  Total KMs: {parseInt(data.return_odometer_value) - prehireData.odometer_value}
                </p>
              )}
            </div>

            {renderSinglePhotoUpload('return_fuel_photo', 'return_fuel', 'Return Fuel Gauge Photo', fuelInputRef, 'fuel')}

            <div>
              <label className="block text-sm font-medium mb-2">Return Fuel Level *</label>
              <div className="flex gap-2">
                {FUEL_LEVELS.map(level => (
                  <button
                    key={level}
                    onClick={() => setData(prev => ({ ...prev, return_fuel_level: level }))}
                    className={`flex-1 py-2 px-1 text-xs rounded-lg border transition ${
                      data.return_fuel_level === level ? 'bg-orange-600 text-white border-orange-600' : 'bg-white border-slate-300'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            {/* Fuel Shortfall Warning & Charge */}
            {showFuelCharge && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-amber-800 mb-3">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Fuel level is lower than at pickup ({prehireData?.fuel_level} → {data.return_fuel_level})
                </p>
                
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="host_refuelled"
                    checked={data.host_refuelled}
                    onChange={e => setData(prev => ({ ...prev, host_refuelled: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <label htmlFor="host_refuelled" className="text-sm font-medium">Host refuelled vehicle?</label>
                </div>

                {data.host_refuelled && (
                  <div className="space-y-3 ml-7">
                    <div>
                      <label className="block text-sm font-medium mb-1">Fuel Charge Amount ($) *</label>
                      <input
                        type="number"
                        value={data.fuel_charge_amount}
                        onChange={e => setData(prev => ({ ...prev, fuel_charge_amount: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                        placeholder="0.00"
                      />
                      {errors.fuel_charge && <p className="text-red-500 text-xs mt-1">{errors.fuel_charge}</p>}
                    </div>
                    
                    {renderSinglePhotoUpload('fuel_receipt_photo', 'fuel_receipt', 'Fuel Receipt Photo', fuelReceiptInputRef, 'fuel_receipt')}
                    {errors.fuel_receipt && <p className="text-red-500 text-xs">{errors.fuel_receipt}</p>}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => goToPrevStep(2)} className="flex-1">Back</Button>
            <Button onClick={() => goToNextStep(4)} className="flex-1 bg-orange-600 hover:bg-orange-700"
              disabled={!data.return_odometer_photo || !data.return_fuel_photo || !data.return_odometer_value}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* STEP 4: Damage Assessment */}
      {step === 4 && (
        <div>
          <h3 className="font-semibold text-lg mb-2">Step 4: Damage Assessment</h3>
          {/* HireHub Protection Micro Warning */}
          <p className="text-xs text-orange-600 bg-orange-50 px-3 py-1.5 rounded-md mb-4 flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Documenting damage with photos is critical for successful claims</span>
          </p>
          
          {/* Pre-Hire Damage Reference (Read-Only) */}
          {prehireData?.damage_items?.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Pre-Hire Damage Reference (Read-Only)
              </h4>
              <div className="text-xs text-slate-600">
                {prehireData.damage_items.map((item, idx) => (
                  <div key={idx} className="mb-1">• {item.area}: {item.description}</div>
                ))}
              </div>
              {prehireData.notes && (
                <p className="text-xs text-slate-500 mt-2">Notes: {prehireData.notes}</p>
              )}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Any new damage observed? *</label>
              <div className="flex gap-4">
                <button
                  onClick={() => setData(prev => ({ ...prev, new_damage_observed: false }))}
                  className={`flex-1 py-3 rounded-lg border-2 transition ${
                    !data.new_damage_observed ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-300'
                  }`}
                >
                  No
                </button>
                <button
                  onClick={() => setData(prev => ({ ...prev, new_damage_observed: true }))}
                  className={`flex-1 py-3 rounded-lg border-2 transition ${
                    data.new_damage_observed ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-300'
                  }`}
                >
                  Yes
                </button>
              </div>
            </div>

            {data.new_damage_observed && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Damage Description *</label>
                  <Textarea
                    value={data.damage_description}
                    onChange={e => setData(prev => ({ ...prev, damage_description: e.target.value }))}
                    rows={3}
                    placeholder="Describe the new damage..."
                  />
                  {errors.damage_description && <p className="text-red-500 text-xs mt-1">{errors.damage_description}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Damage Photos (minimum 1) *</label>
                  
                  {data.damage_photos.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {data.damage_photos.map((photo, idx) => {
                        const thumbData = photoThumbnails.damage_photos[idx];
                        return (
                          <div key={idx} className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative group">
                            {thumbData?.thumbnail_url ? (
                              <img src={thumbData.thumbnail_url} alt={`Damage ${idx + 1}`} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                              </div>
                            )}
                            <button
                              onClick={() => removePhoto('damage_photos', 'damage_photos', idx)}
                              className="absolute top-1 left-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center text-xs"
                            >×</button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <Button onClick={() => damageInputRef.current?.click()} variant="outline" size="sm">
                    {loading ? 'Uploading...' : <><Camera className="w-4 h-4 mr-2" />Add Damage Photos</>}
                  </Button>
                  {errors.damage_photos && <p className="text-red-500 text-xs mt-1">{errors.damage_photos}</p>}
                </div>
              </>
            )}
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => goToPrevStep(3)} className="flex-1">Back</Button>
            <Button onClick={() => goToNextStep(5)} className="flex-1 bg-orange-600 hover:bg-orange-700">
              Next
            </Button>
          </div>
        </div>
      )}

      {/* STEP 5: Vehicle Return Confirmation */}
      {step === 5 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Step 5: Vehicle Return Confirmation</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Keys Retrieved? *</label>
              <div className="flex gap-4">
                <button
                  onClick={() => setData(prev => ({ ...prev, keys_retrieved: true }))}
                  className={`flex-1 py-3 rounded-lg border-2 transition ${
                    data.keys_retrieved ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-300'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => setData(prev => ({ ...prev, keys_retrieved: false }))}
                  className={`flex-1 py-3 rounded-lg border-2 transition ${
                    !data.keys_retrieved ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-300'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Vehicle returned to agreed location? *</label>
              <div className="flex gap-4">
                <button
                  onClick={() => setData(prev => ({ ...prev, vehicle_returned_to_location: true }))}
                  className={`flex-1 py-3 rounded-lg border-2 transition ${
                    data.vehicle_returned_to_location ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-300'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => setData(prev => ({ ...prev, vehicle_returned_to_location: false }))}
                  className={`flex-1 py-3 rounded-lg border-2 transition ${
                    !data.vehicle_returned_to_location ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-300'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Vehicle abandoned/unattended? *</label>
              <div className="flex gap-4">
                <button
                  onClick={() => setData(prev => ({ ...prev, vehicle_abandoned: false }))}
                  className={`flex-1 py-3 rounded-lg border-2 transition ${
                    !data.vehicle_abandoned ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-300'
                  }`}
                >
                  No
                </button>
                <button
                  onClick={() => setData(prev => ({ ...prev, vehicle_abandoned: true }))}
                  className={`flex-1 py-3 rounded-lg border-2 transition ${
                    data.vehicle_abandoned ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-300'
                  }`}
                >
                  Yes
                </button>
              </div>
            </div>

            {/* Issue explanation & photos if any problem */}
            {hasReturnIssues && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-red-800 mb-3">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Please provide details about the return issue
                </p>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Explanation *</label>
                    <Textarea
                      value={data.return_issue_explanation}
                      onChange={e => setData(prev => ({ ...prev, return_issue_explanation: e.target.value }))}
                      rows={3}
                      placeholder="Explain the issue..."
                    />
                    {errors.return_issue && <p className="text-red-500 text-xs mt-1">{errors.return_issue}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Photo Evidence</label>
                    
                    {data.return_issue_photos.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {data.return_issue_photos.map((photo, idx) => {
                          const thumbData = photoThumbnails.return_issue[idx];
                          return (
                            <div key={idx} className="aspect-square bg-slate-100 rounded-lg overflow-hidden relative group">
                              {thumbData?.thumbnail_url ? (
                                <img src={thumbData.thumbnail_url} alt={`Issue ${idx + 1}`} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <CheckCircle className="w-6 h-6 text-green-600" />
                                </div>
                              )}
                              <button
                                onClick={() => removePhoto('return_issue_photos', 'return_issue', idx)}
                                className="absolute top-1 left-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center text-xs"
                              >×</button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <Button onClick={() => returnIssueInputRef.current?.click()} variant="outline" size="sm">
                      {loading ? 'Uploading...' : <><Camera className="w-4 h-4 mr-2" />Add Photos</>}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6 pb-16 sm:pb-4">
            <Button variant="outline" onClick={() => goToPrevStep(4)} className="flex-1">Back</Button>
            <Button onClick={() => goToNextStep(6)} className="flex-1 bg-orange-600 hover:bg-orange-700">
              Next
            </Button>
          </div>
        </div>
      )}

      {/* STEP 6: Additional Charges & Submit */}
      {step === 6 && (
        <div>
          <h3 className="font-semibold text-lg mb-4">Step 6: Additional Charges & Submit</h3>
          
          <div className="space-y-4">
            {/* Auto-calculated charges summary */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-sm mb-3">Charge Summary</h4>
              <div className="space-y-2 text-sm">
                {getExcessKms() > 0 && (
                  <div className="flex justify-between">
                    <span>Excess KMs ({getExcessKms()} km @ $0.35/km)</span>
                    <span className="text-amber-700">+${(getExcessKms() * 0.35).toFixed(2)}</span>
                  </div>
                )}
                {data.host_refuelled && data.fuel_charge_amount > 0 && (
                  <div className="flex justify-between">
                    <span>Fuel Charge (host refuelled)</span>
                    <span className="text-amber-700">+${parseFloat(data.fuel_charge_amount).toFixed(2)}</span>
                  </div>
                )}
                {!data.host_refuelled && showFuelCharge && (
                  <div className="flex justify-between">
                    <span>Fuel Shortfall</span>
                    <span className="text-amber-700">+${((fuelDiff / 100) * 60 * 2.50).toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="cleaning_required"
                checked={data.cleaning_required}
                onChange={e => setData(prev => ({ ...prev, cleaning_required: e.target.checked }))}
                className="w-4 h-4"
              />
              <label htmlFor="cleaning_required" className="text-sm">Cleaning Required</label>
            </div>

            {data.cleaning_required && (
              <div>
                <label className="block text-sm font-medium mb-2">Cleaning Fee ($)</label>
                <input
                  type="number"
                  value={data.cleaning_fee}
                  onChange={e => setData(prev => ({ ...prev, cleaning_fee: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="0.00"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Toll Charges ($)</label>
              <input
                type="number"
                value={data.toll_charges}
                onChange={e => setData(prev => ({ ...prev, toll_charges: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Late Return Fee ($)</label>
              <input
                type="number"
                value={data.late_fee}
                onChange={e => setData(prev => ({ ...prev, late_fee: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Notes</label>
              <Textarea
                value={data.notes}
                onChange={e => setData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                placeholder="Any additional notes..."
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6 pb-20 sm:pb-4">
            <Button variant="outline" onClick={() => goToPrevStep(5)} className="flex-1">Back</Button>
            <Button 
              onClick={handleSubmit}
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={loading}
              data-testid="submit-posthire-btn"
            >
              {loading ? 'Submitting...' : 'Submit Report'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

// ============================================================================
// MAIN BOOKING DETAILS COMPONENT
// ============================================================================

// ============================================================================
// P0 CLAIMS ENGINE: ClaimWizard Component
// A simple wizard for hosts to create claims with evidence upload
// ============================================================================
const ClaimWizard = ({ bookingId, api, onSuccess, onCancel, reviewPeriodEnds }) => {
  const [step, setStep] = useState(0); // Start at step 0 (guidance)
  const [claimType, setClaimType] = useState('');
  const [amountDollars, setAmountDollars] = useState('');
  const [description, setDescription] = useState('');
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [claimId, setClaimId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  const CLAIM_TYPES = [
    { value: 'fuel', label: 'Fuel Shortage', icon: '⛽', desc: 'Fuel reimbursement (requires receipt)' },
    { value: 'tolls', label: 'Toll Fees', icon: '🛣️', desc: 'Toll reimbursement (requires receipt)' },
    { value: 'cleaning', label: 'Cleaning Fee', icon: '🧹', desc: 'Cleaning penalty (requires 2+ photos)' },
    { value: 'damage', label: 'Damage', icon: '💥', desc: 'Damage penalty (requires 2+ photos + quote)' },
    { value: 'late_return', label: 'Late Return', icon: '⏰', desc: 'Late return penalty' },
    { value: 'excess_km', label: 'Excess Kilometres', icon: '📏', desc: 'Distance limit exceeded' },
    { value: 'other', label: 'Other Issue', icon: '📋', desc: 'Other charges (requires description)' },
  ];

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', 'claim_evidence');
        
        const response = await api.post('/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data?.url || response.data?.key) {
          setEvidenceFiles(prev => [...prev, {
            key: response.data.key,
            url: response.data.url,
            filename: file.name
          }]);
        }
      }
    } catch (error) {
      toast.error('Failed to upload evidence');
    } finally {
      setUploading(false);
    }
  };

  const removeEvidence = (index) => {
    setEvidenceFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateAndSubmit = async () => {
    if (!claimType || !amountDollars) {
      toast.error('Please select a claim type and enter an amount');
      return;
    }

    setSubmitting(true);
    try {
      // Step 1: Create claim
      const createResponse = await api.post(`/host/bookings/${bookingId}/claims`, {
        claim_type: claimType,
        amount_cents: Math.round(parseFloat(amountDollars) * 100),
        description: description,
        evidence_files: evidenceFiles.map(f => f.key)
      });

      const newClaimId = createResponse.data?.claim?.claim_id;
      if (!newClaimId) throw new Error('Failed to create claim');
      setClaimId(newClaimId);

      // Step 2: Submit claim
      await api.post(`/host/claims/${newClaimId}/submit`);
      
      setSubmitted(true);
      setStep(4); // Go to confirmation step
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Step 0: Guidance Before Submission */}
      {step === 0 && (
        <>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-amber-800 mb-2">Before You Submit a Claim</h4>
                <ul className="space-y-1.5 text-sm text-amber-700">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    <span>Only submit a claim if damage, fuel issues, excess KM, cleaning, or other charges are required</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    <span>Claims must be supported by pre-hire and post-hire evidence (photos, receipts)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">•</span>
                    <span>Claims must be submitted before the review period ends</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">•</span>
                    <span className="text-amber-800"><strong>Note:</strong> Only submit a claim if necessary — this may result in charges to the renter</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          {reviewPeriodEnds && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-600" />
              <span className="text-sm text-purple-700">
                Review period ends: <strong>{new Date(reviewPeriodEnds).toLocaleString()}</strong>
              </span>
            </div>
          )}
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-blue-600 mt-0.5" />
              <p className="text-sm text-blue-700">
                <strong>Evidence matters:</strong> Strong photo evidence significantly increases claim success. 
                Incomplete inspections may limit claim outcomes.
              </p>
            </div>
          </div>
          
          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button onClick={() => setStep(1)} data-testid="claim-start-btn">
              I Understand, Continue
            </Button>
          </div>
        </>
      )}

      {/* Step 1: Select Claim Type */}
      {step === 1 && (
        <>
          <div className="grid grid-cols-2 gap-2">
            {CLAIM_TYPES.map((type) => (
              <button
                key={type.value}
                onClick={() => {
                  setClaimType(type.value);
                  setStep(2);
                }}
                className={`p-3 rounded-lg border text-left transition-colors hover:bg-slate-50 ${
                  claimType === type.value ? 'border-red-500 bg-red-50' : 'border-slate-200'
                }`}
                data-testid={`claim-type-${type.value}`}
              >
                <div className="text-lg mb-1">{type.icon}</div>
                <div className="font-medium text-sm">{type.label}</div>
                <div className="text-xs text-slate-500">{type.desc}</div>
              </button>
            ))}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </>
      )}

      {/* Step 2: Amount & Description */}
      {step === 2 && (
        <>
          <div className="bg-slate-50 p-3 rounded flex items-center gap-2">
            <span className="text-lg">{CLAIM_TYPES.find(t => t.value === claimType)?.icon}</span>
            <span className="font-medium">{CLAIM_TYPES.find(t => t.value === claimType)?.label}</span>
            <Button size="sm" variant="ghost" onClick={() => setStep(1)} className="ml-auto">
              Change
            </Button>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Claim Amount ($)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={amountDollars}
                onChange={(e) => setAmountDollars(e.target.value)}
                placeholder="0.00"
                className="pl-7"
                data-testid="claim-amount-input"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue..."
              rows={3}
              data-testid="claim-description-input"
            />
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
            <Button 
              onClick={() => setStep(3)}
              disabled={!amountDollars || parseFloat(amountDollars) <= 0}
              data-testid="claim-next-evidence"
            >
              Next: Add Evidence
            </Button>
          </div>
        </>
      )}

      {/* Step 3: Evidence Upload */}
      {step === 3 && (
        <>
          <div className="bg-slate-50 p-3 rounded">
            <div className="flex justify-between text-sm">
              <span>{CLAIM_TYPES.find(t => t.value === claimType)?.label}</span>
              <span className="font-bold text-green-600">${parseFloat(amountDollars).toFixed(2)}</span>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Evidence (optional but recommended)</label>
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="evidence-file-input"
              />
              {uploading ? (
                <div className="text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Uploading...
                </div>
              ) : (
                <div className="text-slate-500">
                  <Upload className="w-6 h-6 mx-auto mb-2" />
                  Click to upload photos
                </div>
              )}
            </div>

            {evidenceFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {evidenceFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="truncate max-w-[200px]">{file.filename}</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => removeEvidence(idx)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
            <Button 
              onClick={handleCreateAndSubmit}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="submit-claim-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit Claim'
              )}
            </Button>
          </div>
        </>
      )}

      {/* Step 4: Confirmation After Submission */}
      {step === 4 && submitted && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h4 className="font-semibold text-green-800 mb-2">Claim Submitted Successfully</h4>
            <p className="text-sm text-green-700 mb-4">
              Your claim for <strong>${parseFloat(amountDollars).toFixed(2)}</strong> has been submitted.
            </p>
          </div>
          
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <h5 className="font-medium text-slate-700 text-sm">What happens next:</h5>
            <ul className="space-y-1.5 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>The renter has been notified</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <span>They can accept or dispute the claim during the review period</span>
              </li>
              <li className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <span>Our team may review this if disputed</span>
              </li>
              <li className="flex items-start gap-2">
                <Bell className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <span>You will be notified of any updates</span>
              </li>
            </ul>
          </div>
          
          <div className="flex justify-center pt-2">
            <Button onClick={() => { onSuccess?.(); }} data-testid="claim-done-btn">
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


const WHAT_CONFIRMED_LABEL_MAP = {
  // Phase 2 keys → human-readable summaries (mirror of CATEGORY_DECLARATIONS in
  // VehicleDetails.js, kept inline here to avoid coupling — display-only).
  motorcycle_licence: 'Holds a valid motorcycle licence appropriate for this motorcycle',
  marine_licence: 'Holds a valid marine/boat licence where required',
  pwc_licence: 'Holds a valid Personal Watercraft (jet ski) licence',
  tow_capability: 'Tow vehicle is capable of safely towing this trailer',
  safe_operation: 'Will operate this eBike safely and in accordance with local laws',
  scooter_rules: 'Understands and will follow local e-scooter laws and restrictions',
  safe_use: 'Is able to safely operate this mobility scooter',
};

// Display-only "What was confirmed at booking" card. Reads existing fields
// already returned by GET /api/bookings/{id} and submitted by the renter at
// booking time (Phase 2 category_declarations + specialty supply_confirmation
// + the four licence_declaration booleans). Renders nothing when no audit
// fields exist (older bookings or simple car bookings without specialty /
// category-specific confirmations). Issues no requests, mutates no state, and
// never exposes IP address or user-agent regardless of viewer role.
const WhatWasConfirmedCard = ({ booking }) => {
  if (!booking) return null;
  const lic = booking.licence_declaration || {};
  const cd = booking.category_declarations || {};
  const sc = booking.supply_confirmation || {};

  const hasLicence = !!(
    lic && (
      lic.declared_licence_valid ||
      lic.declared_not_suspended ||
      lic.declared_legally_permitted ||
      lic.declared_licence_belongs_to_me ||
      lic.declared_at
    )
  );
  const cdValues = (cd && cd.values && typeof cd.values === 'object') ? cd.values : {};
  const cdLines = Object.keys(cdValues).filter((k) => cdValues[k] === true);
  const hasCategoryDecls = cdLines.length > 0;
  const hasSupply = !!(sc && sc.confirmed && (sc.confirmed_text || '').trim());

  if (!hasLicence && !hasCategoryDecls && !hasSupply) return null;

  const fmtTs = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return null;
    }
  };

  return (
    <Card className="border-slate-200" data-testid="what-was-confirmed-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <h3 className="text-sm font-semibold text-slate-800">What was confirmed at booking</h3>
        </div>

        <div className="space-y-3">
          {hasLicence && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="confirmed-licence-block">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">Licence declaration</p>
              <p className="text-xs text-slate-700">Driver licence declarations confirmed</p>
              {fmtTs(lic.declared_at) && (
                <p className="text-[11px] text-slate-500 mt-1">Confirmed {fmtTs(lic.declared_at)}</p>
              )}
            </div>
          )}

          {hasCategoryDecls && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="confirmed-category-decl-block">
              <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">Operator responsibilities</p>
              <ul className="text-xs text-slate-700 space-y-0.5">
                {cdLines.map((k) => (
                  <li key={k} className="flex items-start gap-1.5">
                    <Check className="w-3 h-3 mt-0.5 flex-shrink-0 text-emerald-600" />
                    <span>{WHAT_CONFIRMED_LABEL_MAP[k] || k.replace(/_/g, ' ')}</span>
                  </li>
                ))}
              </ul>
              {fmtTs(cd.confirmed_at) && (
                <p className="text-[11px] text-slate-500 mt-2">Confirmed {fmtTs(cd.confirmed_at)}</p>
              )}
            </div>
          )}

          {hasSupply && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3" data-testid="confirmed-supply-block">
              <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-1">Renter supply responsibilities</p>
              <p className="text-xs text-slate-700 whitespace-pre-wrap">{sc.confirmed_text}</p>
              {fmtTs(sc.confirmed_at) && (
                <p className="text-[11px] text-slate-500 mt-2">Confirmed {fmtTs(sc.confirmed_at)}</p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};


const BookingDetails = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, api, isAdmin, isSuperAdmin, token } = useAuth();
  
  // Deep link focus param for auto-scrolling to sections
  const focusSection = searchParams.get('focus');
  
  // Section refs for auto-scroll
  const prehireSectionRef = useRef(null);
  const posthireSectionRef = useRef(null);
  const extraChargesSectionRef = useRef(null);
  const messagesSectionRef = useRef(null);
  
  // Core state - only what's needed
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [lifecycle, setLifecycle] = useState(null);
  const [prehireData, setPrehireData] = useState(null);
  const [posthireData, setPosthireData] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  // PHASE B: Platform payment mode ('platform' | 'host_direct')
  const [paymentMode, setPaymentMode] = useState('platform');
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [markPaidMethod, setMarkPaidMethod] = useState('');
  // PHASE B4: Manual bond recording
  const [showRecordBondModal, setShowRecordBondModal] = useState(false);
  const [recordBondLoading, setRecordBondLoading] = useState(false);
  const [recordBondAmount, setRecordBondAmount] = useState('');
  const [recordBondMethod, setRecordBondMethod] = useState('payid');
  
  // Modal state
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showPrehireWizard, setShowPrehireWizard] = useState(false);
  const [showPosthireWizard, setShowPosthireWizard] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showTripConfirmModal, setShowTripConfirmModal] = useState(false);
  const [tripConfirmLoading, setTripConfirmLoading] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  
  // P2 FIX: Cancel unpaid approved booking modal
  const [showCancelUnpaidModal, setShowCancelUnpaidModal] = useState(false);
  const [cancelUnpaidReason, setCancelUnpaidReason] = useState('');
  const [cancelUnpaidLoading, setCancelUnpaidLoading] = useState(false);
  
  // P0/P1: Dispute Modal state
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeCategory, setDisputeCategory] = useState('fuel');
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [disputeEvidence, setDisputeEvidence] = useState([]);
  const [disputeConfirmed, setDisputeConfirmed] = useState(false);
  const [disputeViewMode, setDisputeViewMode] = useState(false); // For viewing filed dispute
  const [existingDispute, setExistingDispute] = useState(null);
  
  // P0 UI Polish: Checklist viewer modals
  const [showPrehireViewer, setShowPrehireViewer] = useState(false);
  const [showPosthireViewer, setShowPosthireViewer] = useState(false);
  const [checklistPhotos, setChecklistPhotos] = useState({ prehire: [], posthire: [] });
  
  // P0 LEGAL RECORD: Evidence Pack state
  const [evidencePackStatus, setEvidencePackStatus] = useState({ prehire: null, posthire: null });
  const [showEvidencePackModal, setShowEvidencePackModal] = useState({ open: false, type: null });
  const [evidencePackLoading, setEvidencePackLoading] = useState(false);
  
  // P0: Platform Owner Reset Pre-Hire modal
  const [showResetPrehireModal, setShowResetPrehireModal] = useState(false);
  const [resetPrehireLoading, setResetPrehireLoading] = useState(false);
  
  // PART B: Platform Owner Test Advance to Post-Hire
  const [showAdvanceToPosthireModal, setShowAdvanceToPosthireModal] = useState(false);
  const [advanceToPosthireLoading, setAdvanceToPosthireLoading] = useState(false);
  
  // P0: Platform Owner Fast-Track
  const [showFastTrackModal, setShowFastTrackModal] = useState(false);
  const [fastTrackLoading, setFastTrackLoading] = useState(false);
  const [fastTrackTarget, setFastTrackTarget] = useState(null);
  
  // Agreement download state
  const [agreementLoading, setAgreementLoading] = useState(false);
  
  // P0: Booking documents (original agreement + extension addendums)
  const [bookingDocuments, setBookingDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  
  // BOND v1: Bond payment state
  const [bondLoading, setBondLoading] = useState(false);
  
  // P0 Claims Engine: Host claim creation state
  const [showClaimWizard, setShowClaimWizard] = useState(false);
  const [claims, setClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [showClaimDetail, setShowClaimDetail] = useState(null);
  const [claimDisputing, setClaimDisputing] = useState(false);
  const [disputeReasonInput, setDisputeReasonInput] = useState('');
  
  // P1 Extension System: Booking extensions state
  const [extensions, setExtensions] = useState([]);
  const [extensionsLoading, setExtensionsLoading] = useState(false);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [extensionDate, setExtensionDate] = useState('');
  const [extensionLoading, setExtensionLoading] = useState(false);
  const [extensionPricing, setExtensionPricing] = useState(null);
  const [extensionConfirmed, setExtensionConfirmed] = useState(false);
  
  // P0 EXTENSION WORKFLOW: New renter-required fields
  const [extensionOdometer, setExtensionOdometer] = useState('');
  const [extensionOdometerPhoto, setExtensionOdometerPhoto] = useState(null);
  const [extensionOdometerPhotoUrl, setExtensionOdometerPhotoUrl] = useState('');
  const [extensionLicenceValid, setExtensionLicenceValid] = useState(false);
  const [extensionPhotoUploading, setExtensionPhotoUploading] = useState(false);
  const [extensionPaymentLoading, setExtensionPaymentLoading] = useState(false);  // P0 FIX: Payment loading state
  
  // P0 FIX: Extension availability state
  const [extensionAvailability, setExtensionAvailability] = useState([]);
  const [extensionAvailabilityLoading, setExtensionAvailabilityLoading] = useState(false);
  const [extensionNoAvailableDates, setExtensionNoAvailableDates] = useState(false);
  // P0 FIX: Ref to track if availability has been fetched for current modal session
  const extensionAvailabilityFetchedRef = useRef(false);
  
  // Polling ref
  const pollingRef = useRef(null);
  
  // P0 FIX: Refs to allow parent to trigger save on modal close
  const prehireWizardRef = useRef(null);
  const posthireWizardRef = useRef(null);
  
  // P0 FIX: Track if we've ever successfully loaded booking data
  const hasLoadedRef = useRef(false);

  // ============================================================================
  // DETERMINE USER ROLE - Based on booking data only
  // ============================================================================
  
  const isHost = booking?.owner_id === user?.user_id;
  const isRenter = booking?.customer_id === user?.user_id;
  
  // P0: Check if user is platform owner
  const isPlatformOwner = user?.roles?.some(r => 
    (typeof r === 'string' && r === 'platform_owner') || 
    (typeof r === 'object' && r.role === 'platform_owner' && r.is_active)
  );

  // ============================================================================
  // NORMALIZE STATUS - Map legacy to canonical
  // ============================================================================
  
  const normalizeStatus = (status) => {
    return LEGACY_STATUS_MAP[status] || status;
  };

  const currentStatus = booking ? normalizeStatus(booking.status) : null;

  // ============================================================================
  // DATA FETCHING - Always refetch after actions
  // ============================================================================
  
  const fetchBooking = useCallback(async () => {
    try {
      const response = await api.get(`/bookings/${bookingId}`);
      setBooking(response.data);
      hasLoadedRef.current = true;  // Mark as successfully loaded
      
      // Fetch vehicle data for summary card
      if (response.data.vehicle_id) {
        try {
          const vehicleRes = await api.get(`/vehicles/${response.data.vehicle_id}`);
          setVehicle(vehicleRes.data);
        } catch (vErr) {
        }
      }
      
      return response.data;
    } catch (error) {
      // P0 FIX: Only redirect on definitive 404 or 403, not on transient errors
      const status = error.response?.status;
      
      // Determine correct dashboard based on user role
      const userRoles = user?.roles || [];
      const isHostUser = userRoles.includes('owner') || userRoles.includes('host');
      const fallbackDashboard = isHostUser ? '/host' : '/dashboard';
      
      if (status === 404) {
        // Booking genuinely doesn't exist
        toast.error('Booking not found');
        navigate(fallbackDashboard, { replace: true });
      } else if (status === 403) {
        // User doesn't have access to this booking
        toast.error('You do not have access to this booking');
        navigate(fallbackDashboard, { replace: true });
      } else {
        // Transient error (network, 500, etc.) - show error but don't redirect
        // User can still see cached booking data or retry
        console.error('[BookingDetails] Fetch error (non-fatal):', error.message);
        // Show error toast only on initial load (haven't successfully loaded yet)
        if (!hasLoadedRef.current) {
          toast.error('Failed to load booking. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [api, bookingId, navigate]);

  const fetchLifecycle = useCallback(async () => {
    try {
      const response = await api.get(`/bookings/${bookingId}/lifecycle`);
      setLifecycle(response.data);
      
      // Also fetch checklist data if available
      if (response.data.pre_hire?.exists) {
        const prehire = await api.get(`/bookings/${bookingId}/prehire`);
        setPrehireData(prehire.data.checklist);
      }
      if (response.data.post_hire?.exists) {
        const posthire = await api.get(`/bookings/${bookingId}/posthire`);
        setPosthireData(posthire.data.report);
      }
    } catch (error) {
      // Lifecycle endpoint may not exist for old bookings
    }
  }, [api, bookingId]);

  // P0 UI Polish: Fetch checklist photos for viewer
  const fetchChecklistPhotos = useCallback(async (checklistType) => {
    try {
      const response = await api.get(`/uploads/checklists/${bookingId}/photos`, {
        params: { checklist_type: checklistType }
      });
      setChecklistPhotos(prev => ({
        ...prev,
        [checklistType]: response.data.photos || []
      }));
    } catch (error) {
    }
  }, [api, bookingId]);

  // P0 LEGAL RECORD: Fetch evidence pack status
  const fetchEvidencePackStatus = useCallback(async (type) => {
    try {
      const response = await api.get(`/bookings/${bookingId}/evidence-pack?type=${type}`);
      setEvidencePackStatus(prev => ({
        ...prev,
        [type]: response.data
      }));
      return response.data;
    } catch (error) {
      return null;
    }
  }, [api, bookingId]);

  // P0 LEGAL RECORD: Download evidence pack artifact
  const downloadEvidencePackArtifact = async (type, artifact) => {
    try {
      setEvidencePackLoading(true);
      const response = await api.get(`/bookings/${bookingId}/evidence-pack/download?type=${type}&artifact=${artifact}`);
      if (response.data?.url) {
        window.open(response.data.url, '_blank');
        toast.success(`${artifact.toUpperCase()} download started`);
      }
    } catch (error) {
      toast.error('Failed to download evidence pack');
      console.error('Evidence pack download failed:', error);
    } finally {
      setEvidencePackLoading(false);
    }
  };

  // P0 LEGAL RECORD: Generate evidence pack
  const generateEvidencePack = async (type) => {
    try {
      setEvidencePackLoading(true);
      await api.post(`/bookings/${bookingId}/evidence-pack/generate?type=${type}`);
      toast.success('Evidence pack generated successfully');
      // Refresh status
      await fetchEvidencePackStatus(type);
    } catch (error) {
      toast.error('Failed to generate evidence pack');
      console.error('Evidence pack generation failed:', error);
    } finally {
      setEvidencePackLoading(false);
    }
  };

  // ============================================================================
  // P0 CLAIMS ENGINE: Fetch and manage claims
  // ============================================================================
  
  const fetchClaims = useCallback(async () => {
    if (!bookingId || !user) return;
    try {
      setClaimsLoading(true);
      // Use appropriate endpoint based on user role
      const endpoint = isHost 
        ? `/host/bookings/${bookingId}/claims`
        : `/renter/bookings/${bookingId}/claims`;
      const response = await api.get(endpoint);
      setClaims(response.data?.claims || []);
    } catch (error) {
      setClaims([]);
    } finally {
      setClaimsLoading(false);
    }
  }, [api, bookingId, user, isHost]);

  // ============================================================================
  // P1 EXTENSION SYSTEM: Fetch and manage extensions
  // ============================================================================
  
  const fetchExtensions = useCallback(async () => {
    if (!bookingId || !user) return;
    try {
      setExtensionsLoading(true);
      const response = await api.get(`/bookings/${bookingId}/extensions`);
      setExtensions(response.data?.extensions || []);
    } catch (error) {
      setExtensions([]);
    } finally {
      setExtensionsLoading(false);
    }
  }, [api, bookingId, user]);
  
  // P0: Fetch all booking documents (original agreement + extension addendums)
  const fetchDocuments = useCallback(async () => {
    if (!bookingId || !user) return;
    try {
      setDocumentsLoading(true);
      const response = await api.get(`/bookings/${bookingId}/documents`);
      setBookingDocuments(response.data?.documents || []);
    } catch (error) {
      setBookingDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  }, [api, bookingId, user]);
  
  // P0 FIX: Fetch extension availability when modal opens
  // P0 FIX: Stable reference to fetch extension availability - only depends on primitive values
  const fetchExtensionAvailability = useCallback(async (vehicleId, endDate, currentBookingId) => {
    if (!vehicleId || !endDate) return;
    
    try {
      setExtensionAvailabilityLoading(true);
      setExtensionNoAvailableDates(false);
      
      // Fetch availability for vehicle
      const response = await api.get(`/vehicles/${vehicleId}/availability`);
      const allAvailability = response.data?.availability || [];
      
      // Also fetch existing bookings for this vehicle to block those dates
      const bookingsResponse = await api.get(`/vehicles/${vehicleId}/bookings`);
      const vehicleBookings = bookingsResponse.data?.bookings || [];
      
      // Get current booking end date (extension starts day after)
      const currentEndDate = new Date(endDate);
      currentEndDate.setHours(0, 0, 0, 0);
      
      // Build list of blocked dates from:
      // 1. Availability marked as unavailable
      // 2. Other bookings (excluding current booking)
      const blockedDates = new Set();
      
      // Add unavailable dates from availability
      allAvailability
        .filter(a => !a.is_available)
        .forEach(a => blockedDates.add(a.date));
      
      // Add dates from other bookings
      vehicleBookings
        .filter(b => b.booking_id !== currentBookingId && !['cancelled', 'rejected', 'completed'].includes(b.status))
        .forEach(b => {
          const start = new Date(b.start_date);
          const end = new Date(b.end_date);
          let current = new Date(start);
          while (current <= end) {
            blockedDates.add(format(current, 'yyyy-MM-dd'));
            current.setDate(current.getDate() + 1);
          }
        });
      
      // Check if there are any available dates after current end
      // Look ahead 90 days
      let hasAvailableDates = false;
      const lookAheadDays = 90;
      let checkDate = new Date(currentEndDate);
      checkDate.setDate(checkDate.getDate() + 1); // Start from day after current end
      
      for (let i = 0; i < lookAheadDays; i++) {
        const dateStr = format(checkDate, 'yyyy-MM-dd');
        if (!blockedDates.has(dateStr)) {
          hasAvailableDates = true;
          break;
        }
        checkDate.setDate(checkDate.getDate() + 1);
      }
      
      setExtensionNoAvailableDates(!hasAvailableDates);
      
      // Store availability for the date picker
      setExtensionAvailability(Array.from(blockedDates).map(date => ({
        date,
        is_available: false
      })));
      
    } catch (error) {
      console.error('Failed to fetch extension availability:', error);
      // On error, allow manual selection (backend will validate)
      setExtensionAvailability([]);
    } finally {
      setExtensionAvailabilityLoading(false);
    }
  }, [api]); // P0 FIX: Only depends on `api` which is stable
  
  // P0 FIX: Fetch availability ONCE when extension modal opens, using ref guard
  useEffect(() => {
    // Reset the fetch flag when modal closes
    if (!showExtensionModal) {
      extensionAvailabilityFetchedRef.current = false;
      return;
    }
    
    // Only fetch if modal is open AND we haven't fetched yet for this session
    if (showExtensionModal && booking?.vehicle_id && !extensionAvailabilityFetchedRef.current) {
      extensionAvailabilityFetchedRef.current = true; // Mark as fetched BEFORE calling to prevent race conditions
      fetchExtensionAvailability(booking.vehicle_id, booking.end_date, bookingId);
    }
  }, [showExtensionModal, booking?.vehicle_id, booking?.end_date, bookingId, fetchExtensionAvailability]);
  
  // P0 FIX: Check if a date is blocked for extension
  const isExtensionDateBlocked = useCallback((date) => {
    if (!booking?.end_date) return true;
    
    const dateStr = format(date, 'yyyy-MM-dd');
    const currentEnd = new Date(booking.end_date);
    currentEnd.setHours(0, 0, 0, 0);
    
    // Block dates on or before current end date
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    if (compareDate <= currentEnd) return true;
    
    // Block dates marked as unavailable
    return extensionAvailability.some(a => a.date === dateStr && !a.is_available);
  }, [booking?.end_date, extensionAvailability]);
  
  // Calculate extension pricing when date changes
  const calculateExtensionPricing = useCallback(() => {
    if (!extensionDate || !booking) {
      setExtensionPricing(null);
      return;
    }
    
    const currentEnd = new Date(booking.end_date);
    const requestedEnd = new Date(extensionDate);
    
    if (requestedEnd <= currentEnd) {
      setExtensionPricing(null);
      return;
    }
    
    const extraDays = Math.ceil((requestedEnd - currentEnd) / (1000 * 60 * 60 * 24));
    const dailyRate = booking.daily_rate || 0;
    const subtotal = dailyRate * extraDays;
    
    // P0 UNIFIED PRICING: Use same fee split as backend
    const renterFeePercent = booking.renter_fee_percent || 12;
    const hostFeePercent = booking.host_fee_percent || 5;
    
    const renterFee = Math.round(subtotal * (renterFeePercent / 100) * 100) / 100;
    const hostFee = Math.round(subtotal * (hostFeePercent / 100) * 100) / 100;
    
    // Platform-controlled processing fee: 2.5% of base charge + $0.50
    const baseCharge = subtotal + renterFee;
    const processingFee = Math.round((baseCharge * 0.025 + 0.50) * 100) / 100;
    
    const total = Math.round((subtotal + renterFee + processingFee) * 100) / 100;
    const ownerPayout = Math.round((subtotal - hostFee) * 100) / 100;
    
    setExtensionPricing({
      extra_days: extraDays,
      rate_per_day: dailyRate,
      subtotal: subtotal,
      renter_fee: renterFee,
      processing_fee: processingFee,
      host_fee: hostFee,
      platform_fee: renterFee + hostFee,
      owner_payout: ownerPayout,
      total: total
    });
  }, [extensionDate, booking]);
  
  useEffect(() => {
    calculateExtensionPricing();
  }, [calculateExtensionPricing]);
  
  // Submit extension request
  const handleSubmitExtension = async () => {
    // P0 EXTENSION WORKFLOW: Validate all required fields (tolls removed per design decision)
    if (!extensionDate) {
      toast.error('Please select a new return date');
      return;
    }
    if (!extensionOdometer || parseInt(extensionOdometer) <= 0) {
      toast.error('Please enter the current odometer reading');
      return;
    }
    if (!extensionOdometerPhotoUrl) {
      toast.error('Please upload a photo of the odometer');
      return;
    }
    if (!extensionLicenceValid) {
      toast.error('Please confirm your licence is still valid');
      return;
    }
    if (!extensionConfirmed) {
      toast.error('Please confirm the payment terms');
      return;
    }
    
    try {
      setExtensionLoading(true);
      const response = await api.post(`/bookings/${bookingId}/extension/request`, {
        requested_end_at: extensionDate,
        odometer_reading: parseInt(extensionOdometer),
        odometer_photo_url: extensionOdometerPhotoUrl,
        licence_valid: extensionLicenceValid,
        confirmed_charge: extensionConfirmed
      });
      
      toast.success(response.data.message || 'Extension request submitted!');
      setShowExtensionModal(false);
      // Reset all fields
      setExtensionDate('');
      setExtensionOdometer('');
      setExtensionOdometerPhoto(null);
      setExtensionOdometerPhotoUrl('');
      setExtensionLicenceValid(false);
      setExtensionConfirmed(false);
      setExtensionPricing(null);
      await fetchExtensions();
      await fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit extension request');
    } finally {
      setExtensionLoading(false);
    }
  };
  
  // P0 EXTENSION PAYMENT: Handle renter payment for approved extension (Stripe Checkout)
  const handlePayExtension = async (extensionId) => {
    try {
      setExtensionPaymentLoading(true);
      // P0 FIX: Correct endpoint path - singular "extension" not "extensions"
      const response = await api.post(`/bookings/${bookingId}/extension/${extensionId}/pay`);
      
      // Response contains checkout_url for payment checkout redirect
      if (response.data?.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create payment session. Please try again.');
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Payment failed. Please try again.';
      toast.error(errorDetail);
      setExtensionPaymentLoading(false);
    }
    // Note: Don't set loading to false on success - page will redirect
  };
  
  // P0 EXTENSION BOND: Handle bond authorization for extension after payment
  const handleExtensionBondAuth = async (extensionId) => {
    try {
      setExtensionPaymentLoading(true);
      
      // Call the extension bond authorization endpoint
      const response = await api.post(`/bookings/${bookingId}/extension/${extensionId}/bond`, {
        origin_url: window.location.origin
      });
      
      // Response contains checkout_url for payment checkout redirect
      if (response.data?.checkout_url) {
        // Use replace to prevent history pollution
        window.location.replace(response.data.checkout_url);
      } else {
        toast.error('Failed to create bond authorization session. Please try again.');
        setExtensionPaymentLoading(false);
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Bond authorization failed. Please try again.';
      toast.error(errorDetail);
      setExtensionPaymentLoading(false);
    }
    // Note: Don't set loading to false on success - page will redirect
  };
  
  // P0 FIX: Reuse existing uploadFileToS3 for extension odometer photo (same as prehire)
  const handleExtensionOdometerPhotoUpload = async (file) => {
    if (!file || file.size === 0) {
      toast.error('Please select a valid photo');
      return;
    }
    
    try {
      setExtensionPhotoUploading(true);
      
      // Use the same upload function as prehire photos
      const formData = new FormData();
      formData.append('file', file);
      formData.append('booking_id', bookingId);
      formData.append('checklist_type', 'extension');
      formData.append('photo_type', 'extension_odometer');
      
      const uploadResponse = await api.post('/uploads/checklists/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      
      const { s3_key, photo_id } = uploadResponse.data;
      
      // Fetch signed URL for preview
      let photoUrl = null;
      try {
        const urlResponse = await api.get(`/uploads/checklists/${bookingId}/${encodeURIComponent(s3_key)}`);
        photoUrl = urlResponse.data?.url || urlResponse.data?.signed_url;
      } catch (urlErr) {
        photoUrl = URL.createObjectURL(file);
      }
      
      setExtensionOdometerPhotoUrl(s3_key); // Store s3_key for backend
      setExtensionOdometerPhoto(photoUrl);  // Store URL for preview
      toast.success('Odometer photo uploaded');
    } catch (error) {
      console.error('[Extension Upload] Error:', error.response?.data || error.message);
      const detail = error.response?.data?.detail || error.message;
      if (detail.includes('Empty file')) {
        toast.error('Photo capture failed - please try again');
      } else if (detail.includes('HEIC') || detail.includes('heif')) {
        toast.error('iPhone photo format not supported - try PNG or JPG');
      } else if (detail.includes('too large')) {
        toast.error('Photo too large (max 50MB)');
      } else {
        toast.error('Failed to upload photo');
      }
    } finally {
      setExtensionPhotoUploading(false);
    }
  };
  
  // Cancel pending extension
  const handleCancelExtension = async (extensionId) => {
    try {
      await api.post(`/renter/extensions/${extensionId}/cancel`);
      toast.success('Extension request cancelled');
      await fetchExtensions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel extension');
    }
  };

  // P0: Renter disputes a claim
  const handleDisputeClaim = async (claimId) => {
    if (!disputeReasonInput.trim()) {
      toast.error('Please provide a reason for disputing');
      return;
    }
    try {
      setClaimDisputing(true);
      await api.post(`/renter/claims/${claimId}/dispute`, {
        reason: disputeReasonInput
      });
      toast.success('Claim disputed successfully');
      setShowClaimDetail(null);
      setDisputeReasonInput('');
      await fetchClaims();
      await fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dispute claim');
    } finally {
      setClaimDisputing(false);
    }
  };

  const refreshAll = useCallback(async () => {
    await fetchBooking();
    await fetchLifecycle();
    await fetchClaims();
    await fetchExtensions();
    await fetchDocuments();
  }, [fetchBooking, fetchLifecycle, fetchClaims, fetchExtensions, fetchDocuments]);

  // ============================================================================
  // POLLING - Live status updates without refresh
  // ============================================================================
  
  useEffect(() => {
    fetchBooking();
    fetchLifecycle();
    fetchClaims();
    fetchExtensions();
    fetchDocuments();

    // PHASE B: Read platform payment mode once per booking view
    (async () => {
      try {
        const res = await api.get('/platform/payment-mode');
        if (res?.data?.mode) setPaymentMode(res.data.mode);
      } catch (err) {
        // Fall back to "platform" silently — default is already set.
      }
    })();
    
    // Start polling every 4 seconds
    pollingRef.current = setInterval(() => {
      fetchBooking();
      fetchLifecycle();
    }, 4000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchBooking, fetchLifecycle, fetchClaims, fetchExtensions, fetchDocuments, api]);

  // Stop polling when completed or cancelled
  useEffect(() => {
    if (currentStatus === 'completed' || currentStatus === 'cancelled') {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
  }, [currentStatus]);
  
  // P0 FIX: Handle return from Stripe checkout (main payment AND extra charges)
  // Uses replaceState to prevent back-button loop to Stripe intermediate page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Handle main payment success (from initial rental payment)
    const paymentSuccess = urlParams.get('payment_success');
    const sessionId = urlParams.get('session_id');
    const airwallexIntentId = urlParams.get('id'); // Airwallex appends ?id=int_xxx
    
    if (paymentSuccess === 'true') {
      // Airwallex returns ?payment_success=true&id=int_xxx (no session_id)
      // Stripe returns ?payment_success=true&session_id=cs_xxx
      // Accept either — then reconcile with backend for real status
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, document.title, cleanPath);
      
      // Reconcile with backend to confirm payment and update booking
      const reconcile = async () => {
        try {
          const res = await api.post(`/payments/reconcile/${bookingId}`, {
            session_id: sessionId || undefined,
            intent_id: airwallexIntentId || undefined
          });
          
          if (res.data.status === 'reconciled' || res.data.status === 'already_paid' || res.data.status === 'already_processed') {
            toast.success('Payment successful! Your booking is confirmed.');
          } else if (res.data.status === 'not_paid') {
            toast.info('Payment is being processed. Status will update shortly.');
          } else {
            toast.success('Payment submitted. Refreshing status...');
          }
        } catch (err) {
          console.error('[PaymentReturn] Reconciliation error:', err);
          toast.success('Payment submitted! Refreshing booking status...');
        }
        // Refresh booking data regardless
        navigate(cleanPath, { replace: true });
      };
      reconcile();
      return; // Skip other param checks
    }
    
    // Handle payment cancelled (user explicitly cancelled or backed out)
    const paymentCancelled = urlParams.get('payment_cancelled');
    if (paymentCancelled === 'true') {
      toast.error('Payment was cancelled. You can try again when ready.');
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, document.title, cleanPath);
      navigate(cleanPath, { replace: true });
    }
    
    // Handle extra charges payment (existing logic)
    const extraChargesPaid = urlParams.get('extra_charges');
    if (extraChargesPaid === 'paid') {
      toast.success('Payment received! Your booking is now complete.');
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, document.title, cleanPath);
      navigate(cleanPath, { replace: true });
    } else if (extraChargesPaid === 'cancelled') {
      toast.error('Payment was cancelled. You can try again when ready.');
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, document.title, cleanPath);
      navigate(cleanPath, { replace: true });
    }
    
    // Handle bond payment success — reconcile with backend like main payment
    const bondPaymentSuccess = urlParams.get('bond_payment_success');
    const bondAirwallexId = urlParams.get('id'); // Airwallex appends ?id=int_xxx
    if (bondPaymentSuccess === 'true') {
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, document.title, cleanPath);
      
      const reconcileBond = async () => {
        try {
          const res = await api.get(`/payments/bond/status/${bookingId}`);
          
          if (res.data.bond_status === 'authorized' || res.data.bond_status === 'captured') {
            toast.success('Bond authorization successful! Your security deposit has been held.');
          } else {
            toast.info('Bond payment is being processed. Status will update shortly.');
          }
        } catch (err) {
          console.error('[BondReturn] Status check error:', err);
          toast.success('Bond payment submitted! Refreshing status...');
        }
        navigate(cleanPath, { replace: true });
      };
      reconcileBond();
      return;
    }
    
    // Handle bond payment cancelled
    const bondPaymentCancelled = urlParams.get('bond_payment_cancelled');
    if (bondPaymentCancelled === 'true') {
      toast.error('Bond authorization was cancelled. You can retry from the booking page.');
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, document.title, cleanPath);
      navigate(cleanPath, { replace: true });
    }
    
    // P0 EXTENSION: Handle extension payment success from Stripe Checkout return
    const extensionPaymentSuccess = urlParams.get('extension_payment_success');
    if (extensionPaymentSuccess === 'true') {
      const extensionId = urlParams.get('extension_id');
      toast.success('Extension payment successful! Your booking has been extended.');
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, document.title, cleanPath);
      navigate(cleanPath, { replace: true });
    }
    
    // P0 EXTENSION: Handle extension payment cancelled
    const extensionPaymentCancelled = urlParams.get('extension_payment_cancelled');
    if (extensionPaymentCancelled === 'true') {
      toast.info('Extension payment was cancelled. You can try again.');
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, document.title, cleanPath);
      navigate(cleanPath, { replace: true });
    }
    
    // P0 EXTENSION: Handle extension bond authorization success
    const extensionBondSuccess = urlParams.get('extension_bond_success');
    if (extensionBondSuccess === 'true') {
      toast.success('Extension bond authorized! Your booking has been extended.');
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, document.title, cleanPath);
      navigate(cleanPath, { replace: true });
    }
    
    // P0 EXTENSION: Handle extension bond cancelled
    const extensionBondCancelled = urlParams.get('extension_bond_cancelled');
    if (extensionBondCancelled === 'true') {
      toast.info('Extension bond authorization was cancelled. You can try again.');
      const cleanPath = window.location.pathname;
      window.history.replaceState({}, document.title, cleanPath);
      navigate(cleanPath, { replace: true });
    }
  }, [navigate]);

  // Deep link focus: Auto-scroll to section when specified via query param
  useEffect(() => {
    if (!focusSection || loading) return;
    
    // Wait for DOM to render
    const timeoutId = setTimeout(() => {
      let targetRef = null;
      
      switch (focusSection) {
        case 'prehire':
          targetRef = prehireSectionRef;
          break;
        case 'posthire':
          targetRef = posthireSectionRef;
          break;
        case 'extra_charges':
        case 'payment':
          targetRef = extraChargesSectionRef;
          break;
        case 'messages':
          targetRef = messagesSectionRef;
          break;
        default:
          break;
      }
      
      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Brief highlight animation
        targetRef.current.classList.add('ring-2', 'ring-[#004B8D]', 'ring-offset-2', 'transition-all');
        setTimeout(() => {
          targetRef.current?.classList.remove('ring-2', 'ring-[#004B8D]', 'ring-offset-2');
        }, 2000);
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [focusSection, loading]);

  // ============================================================================
  // ACTION HANDLERS - Always refetch after success
  // ============================================================================
  
  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      switch (action) {
        case 'approve':
          await api.post(`/bookings/${bookingId}/accept`);
          toast.success('Booking approved! The renter has been notified to complete payment.');
          break;
          
        case 'decline':
          await api.post(`/bookings/${bookingId}/decline`, { reason: declineReason });
          toast.success('Booking declined');
          setShowDeclineModal(false);
          break;

        case 'mark_payment_received':
          // PHASE B: Host-direct mode — opens confirmation modal instead of calling immediately.
          setShowMarkPaidModal(true);
          setActionLoading(false);
          return;
          
        case 'pay':
          const payResponse = await api.post(`/payments/authorize/${bookingId}`, {
            origin_url: window.location.origin
          });

          if (payResponse.data.payment_provider === 'airwallex') {
            const { init } = await import('@airwallex/components-sdk');
            const { payments } = await init({
              env: payResponse.data.airwallex_env || 'demo',
              enabledElements: ['payments'],
            });
            await payments.redirectToCheckout({
              intent_id: payResponse.data.airwallex_intent_id,
              client_secret: payResponse.data.airwallex_client_secret,
              currency: payResponse.data.airwallex_currency || 'AUD',
              country_code: 'AU',
              successUrl: `${window.location.origin}/bookings/${bookingId}?payment_success=true`,
              cancelUrl: `${window.location.origin}/bookings/${bookingId}?payment_cancelled=true`,
            });
          } else {
            // Stripe legacy: direct URL redirect
            window.location.replace(payResponse.data.checkout_url);
          }
          return; // Don't refetch - user is redirecting
        
        case 'pay_bond':
          // BOND v1: Pay/authorize bond separately from rental
          setBondLoading(true);
          try {
            const bondResponse = await api.post(`/payments/bond/authorize/${bookingId}`, {
              origin_url: window.location.origin
            });

            if (bondResponse.data.payment_provider === 'airwallex') {
              const { init } = await import('@airwallex/components-sdk');
              const { payments } = await init({
                env: bondResponse.data.airwallex_env || 'demo',
                enabledElements: ['payments'],
              });
              const bondRedirectOptions = {
                intent_id: bondResponse.data.airwallex_intent_id,
                client_secret: bondResponse.data.airwallex_client_secret,
                currency: bondResponse.data.airwallex_currency || 'AUD',
                country_code: 'AU',
                successUrl: `${window.location.origin}/bookings/${bookingId}?bond_payment_success=true`,
                cancelUrl: `${window.location.origin}/bookings/${bookingId}?bond_payment_cancelled=true`,
              };
              // Bond authorize mode: hold only, capture later
              if (bondResponse.data.bond_mode === 'authorize') {
                bondRedirectOptions.autoCapture = false;
                bondRedirectOptions.authorizationType = 'pre_auth';
              }
              await payments.redirectToCheckout(bondRedirectOptions);
            } else {
              // Stripe legacy: direct URL redirect
              window.location.replace(bondResponse.data.checkout_url);
            }
            return; // User redirecting to payment
          } catch (bondErr) {
            setBondLoading(false);
            toast.error(bondErr.response?.data?.detail || 'Failed to start bond payment');
            return;
          }
          
        case 'cancel':
          // Show confirmation modal instead of cancelling directly
          setShowCancelModal(true);
          return; // Don't refetch yet - wait for modal confirmation
          
        case 'start_prehire':
          await api.post(`/bookings/${bookingId}/prehire/start`);
          toast.success('Pre-hire checklist started. Complete all steps before handover.');
          setShowPrehireWizard(true);
          break;
          
        case 'start_trip':
          // Renter must confirm signed declarations via TripConfirmationModal
          // before the start-trip API is called. The modal's onConfirm submits.
          setShowTripConfirmModal(true);
          return; // Don't refetch yet - wait for modal confirmation
          
        case 'start_posthire':
          await api.post(`/bookings/${bookingId}/posthire/start`);
          toast.success('Post-hire report started. Document the vehicle return condition.');
          setShowPosthireWizard(true);
          break;
          
        case 'accept_charges':
          // P0 CRITICAL: Settle charges from held bond, NOT create new payment
          try {
            // First get settlement preview to check if bond covers charges
            const previewRes = await api.get(`/bookings/${bookingId}/extra-charges/settlement-preview`);
            const preview = previewRes.data;
            
            if (preview.covered_by_bond) {
              // Bond covers everything - settle directly
              const settleRes = await api.post(`/bookings/${bookingId}/extra-charges/settle-from-bond`);
              toast.success(`Charges settled! $${preview.bond_return.toFixed(2)} will be returned to you.`);
            } else {
              // Charges exceed bond - need to pay excess via payment checkout
              // First capture full bond, then create checkout for remaining
              const checkoutRes = await api.post(`/bookings/${bookingId}/extra-charges/create-checkout`, {
                excess_only: true,
                excess_amount_cents: preview.excess_due_cents
              });
              if (checkoutRes.data.checkout_url) {
                toast.success('Redirecting to pay remaining balance...');
                window.location.replace(checkoutRes.data.checkout_url);
                return;
              }
            }
          } catch (error) {
            if (error.response?.data?.status === 'exceeds_bond') {
              // Handle excess scenario
              toast.info('Charges exceed your bond. Please pay the remaining balance.');
              const checkoutRes = await api.post(`/bookings/${bookingId}/extra-charges/create-checkout`);
              if (checkoutRes.data.checkout_url) {
                window.location.replace(checkoutRes.data.checkout_url);
                return;
              }
            } else {
              throw error;
            }
          }
          break;
          
        case 'dispute_charges':
          // P0/P1: Open dispute modal
          setShowDisputeModal(true);
          setActionLoading(false);
          return;
        
        case 'cancel_unpaid_hold':
          // P2 FIX: Show confirmation modal for host to cancel unpaid approved booking
          setShowCancelUnpaidModal(true);
          setActionLoading(false);
          return;
          
        case 'leave_review':
          setShowReviewModal(true);
          setActionLoading(false);
          return;
          
        default:
      }
      
      // Always refetch after successful action
      await refreshAll();
    } catch (error) {
      // Display backend error directly to user
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  // Renter start-trip confirmation handler — invoked by TripConfirmationModal
  // on confirm. POSTs the signed confirmation_data alongside the existing
  // start-trip call (lifecycle gates remain enforced server-side).
  const handleConfirmStartTrip = async (confirmation_data) => {
    setTripConfirmLoading(true);
    try {
      await api.post(`/bookings/${bookingId}/start-trip`, { confirmation_data });
      toast.success('Trip started! Enjoy your rental.');
      setShowTripConfirmModal(false);
      await fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trip');
    } finally {
      setTripConfirmLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    setActionLoading(true);
    try {
      await api.post('/reviews', {
        booking_id: bookingId,
        rating: reviewRating,
        comment: reviewComment.trim()
      });
      toast.success('Review submitted!');
      setShowReviewModal(false);
      await refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setActionLoading(false);
    }
  };

  // PHASE B: Host-direct mode — confirm "Mark Payment Received"
  const handleConfirmMarkPaymentReceived = async () => {
    setMarkPaidLoading(true);
    try {
      await api.post(`/bookings/${bookingId}/mark-payment-received`, {
        method: markPaidMethod.trim() || undefined
      });
      toast.success('Payment confirmed. Booking is now paid — you can start the pre-hire checklist.');
      setShowMarkPaidModal(false);
      setMarkPaidMethod('');
      await refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark payment received');
    } finally {
      setMarkPaidLoading(false);
    }
  };

  // PHASE B4: Host-direct mode — open "Record Bond" modal with prefilled amount
  const openRecordBondModal = () => {
    const prefillCents = (
      typeof booking?.bond_recorded_amount_cents === 'number'
        ? booking.bond_recorded_amount_cents
        : (typeof booking?.bond_required_cents === 'number'
            ? booking.bond_required_cents
            : Math.round((booking?.bond_amount || 0) * 100))
    );
    const prefillDollars = (prefillCents / 100).toFixed(2);
    setRecordBondAmount(prefillDollars);
    setRecordBondMethod('payid');
    setShowRecordBondModal(true);
  };

  const handleConfirmRecordBond = async () => {
    const parsed = parseFloat(recordBondAmount);
    if (isNaN(parsed) || parsed <= 0) {
      toast.error('Please enter a valid bond amount.');
      return;
    }
    setRecordBondLoading(true);
    try {
      await api.post(`/bookings/${bookingId}/bond/record-manual`, {
        amount_cents: Math.round(parsed * 100),
        method: recordBondMethod
      });
      toast.success('Bond recorded manually.');
      setShowRecordBondModal(false);
      await refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record bond');
    } finally {
      setRecordBondLoading(false);
    }
  };

  // Handle confirmed cancellation - sends confirmed: true to backend
  const handleConfirmCancel = async () => {
    setCancelLoading(true);
    try {
      await api.post(`/bookings/${bookingId}/cancel`, { 
        reason: 'User cancelled',
        confirmed: true  // Required by backend
      });
      toast.success('Booking cancelled');
      setShowCancelModal(false);
      await refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelLoading(false);
    }
  };
  
  // P2 FIX: Handle host cancelling unpaid approved booking
  const handleConfirmCancelUnpaid = async () => {
    setCancelUnpaidLoading(true);
    try {
      await api.post(`/bookings/${bookingId}/decline`, { 
        reason: cancelUnpaidReason || 'Host released unpaid hold'
      });
      toast.success('Booking cancelled - dates have been released');
      setShowCancelUnpaidModal(false);
      setCancelUnpaidReason('');
      await refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelUnpaidLoading(false);
    }
  };

  // P0/P1: Handle dispute submission
  const handleSubmitDispute = async () => {
    if (!disputeReason.trim() || disputeReason.trim().length < 10) {
      toast.error('Please provide a reason for the dispute (minimum 10 characters)');
      return;
    }
    if (!disputeConfirmed) {
      toast.error('Please confirm that the information is true');
      return;
    }
    
    setDisputeLoading(true);
    try {
      // Get photo IDs from uploaded evidence
      const evidencePhotoIds = disputeEvidence
        .filter(e => e.uploaded && e.s3_key)
        .map(e => e.photo_id || e.s3_key);
      
      const response = await api.post(`/bookings/${bookingId}/extra-charges/mark-disputed`, {
        reason: disputeReason.trim(),
        reason_category: disputeCategory,
        evidence_photos: evidencePhotoIds
      });
      toast.success(response.data.message || 'Dispute submitted. Our team will review the evidence and contact you.');
      setShowDisputeModal(false);
      setDisputeReason('');
      setDisputeCategory('fuel');
      setDisputeEvidence([]);
      setDisputeConfirmed(false);
      await refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to file dispute');
    } finally {
      setDisputeLoading(false);
    }
  };
  
  // P1: Handle evidence photo upload for dispute
  const handleDisputeEvidenceUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    for (const file of files) {
      const tempId = `evidence_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const preview = URL.createObjectURL(file);
      
      // Add to state immediately with preview
      setDisputeEvidence(prev => [...prev, {
        id: tempId,
        file,
        preview,
        uploading: true,
        uploaded: false
      }]);
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('booking_id', bookingId);
        formData.append('checklist_type', 'dispute');
        formData.append('photo_type', 'evidence');
        
        const response = await api.post('/uploads/checklists/photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setDisputeEvidence(prev => prev.map(e => 
          e.id === tempId 
            ? { ...e, uploading: false, uploaded: true, photo_id: response.data.photo_id, s3_key: response.data.s3_key, url: response.data.url }
            : e
        ));
      } catch (error) {
        console.error('Evidence upload failed:', error);
        setDisputeEvidence(prev => prev.map(e => 
          e.id === tempId ? { ...e, uploading: false, error: true } : e
        ));
        toast.error('Failed to upload evidence photo');
      }
    }
  };
  
  // P1: Remove evidence photo
  const removeDisputeEvidence = (id) => {
    setDisputeEvidence(prev => {
      const item = prev.find(e => e.id === id);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter(e => e.id !== id);
    });
  };
  
  // P1: View existing dispute
  const handleViewDispute = async () => {
    try {
      const response = await api.get(`/bookings/${bookingId}`);
      const disputeId = response.data.extra_charges_dispute_id;
      
      if (disputeId) {
        // Load dispute details
        const disputeResp = await api.get(`/bookings/${bookingId}/dispute`);
        if (disputeResp.data) {
          setExistingDispute(disputeResp.data);
          setDisputeViewMode(true);
          setShowDisputeModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to load dispute:', error);
      toast.error('Failed to load dispute details');
    }
  };

  const handleOpenMessages = async () => {
    try {
      const response = await api.post('/messages/threads', {
        thread_type: 'booking',
        booking_id: bookingId
      });
      navigate(`/messages/${response.data.thread_id}`);
    } catch (error) {
      toast.error('Failed to open messages');
    }
  };

  // P0: Platform Owner - Reset Pre-Hire for testing
  const handleResetPrehire = async () => {
    setResetPrehireLoading(true);
    try {
      const response = await api.post(`/platform-owner/bookings/${bookingId}/reset-prehire`);
      const result = response.data;
      
      // Show detailed result in toast
      toast.success(
        `Pre-hire reset complete!\n` +
        `Status: ${result.previous_status} → ${result.new_status}\n` +
        `Deleted: ${result.deleted_photo_count} photos, ${result.deleted_s3_count} S3 objects\n` +
        `Checklist record: ${result.deleted_prehire_record ? 'deleted' : 'none found'}`,
        { duration: 8000 }
      );
      
      // Refetch booking data
      await fetchBooking();
      await fetchLifecycle();
      setShowResetPrehireModal(false);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Reset failed';
      toast.error(detail);
    } finally {
      setResetPrehireLoading(false);
    }
  };

  // PART B: Platform Owner - Test Advance to Post-Hire for testing
  const handleAdvanceToPosthire = async () => {
    setAdvanceToPosthireLoading(true);
    try {
      const response = await api.post(`/platform-owner/bookings/${bookingId}/test-advance-to-posthire`);
      const result = response.data;
      
      toast.success(
        `Booking advanced! Post-hire checklist is now available.\n` +
        `Status: ${result.previous_status} → ${result.new_status}`,
        { duration: 5000 }
      );
      
      // Refetch booking data
      await fetchBooking();
      await fetchLifecycle();
      setShowAdvanceToPosthireModal(false);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Advance failed';
      toast.error(detail);
    } finally {
      setAdvanceToPosthireLoading(false);
    }
  };

  // P0: Platform Owner Fast-Track handler
  const handleFastTrack = async (target) => {
    setFastTrackLoading(true);
    try {
      const response = await api.post(`/admin/bookings/${bookingId}/fast-track`, { target });
      const result = response.data;
      
      if (result.idempotent) {
        toast.info(result.message);
      } else {
        toast.success(
          `Fast-tracked: ${result.before_status} → ${result.after_status}`,
          { duration: 5000 }
        );
      }
      
      // Refetch booking data
      await fetchBooking();
      await fetchLifecycle();
      setShowFastTrackModal(false);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Fast-track failed';
      toast.error(detail);
    } finally {
      setFastTrackLoading(false);
    }
  };

  // Download Booking Agreement PDF
  const handleDownloadAgreement = async () => {
    setAgreementLoading(true);
    try {
      // First check if PDF exists, if not generate it
      let pdfResponse = await api.get(`/bookings/${bookingId}/agreement-pdf`);
      
      if (!pdfResponse.data.exists) {
        // Generate PDF first
        await api.post(`/bookings/${bookingId}/generate-agreement-pdf`);
        // Fetch again
        pdfResponse = await api.get(`/bookings/${bookingId}/agreement-pdf`);
      }
      
      if (pdfResponse.data.download_url) {
        // Open presigned URL in new tab for download
        window.open(pdfResponse.data.download_url, '_blank');
        toast.success('Agreement PDF downloading');
      } else {
        // Fallback to text version
        const response = await api.get(`/bookings/${bookingId}/agreement/download`, {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `agreement_${bookingId.slice(0, 12)}.txt`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success('Agreement downloaded');
      }
    } catch (error) {
      console.error('Agreement download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download agreement');
    } finally {
      setAgreementLoading(false);
    }
  };

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  
  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    try {
      return format(parseISO(ts), 'd MMM yyyy, h:mm a');
    } catch {
      return ts;
    }
  };

  const getStatusConfig = (status) => {
    return CANONICAL_STATUSES[status] || CANONICAL_STATUSES.requested;
  };

  const getAvailableActions = () => {
    if (!currentStatus) return [];
    return getStatusActions(currentStatus, isHost, isRenter, paymentMode);
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#004B8D]"></div>
      </div>
    );
  }

  if (!booking) return null;

  const statusConfig = getStatusConfig(currentStatus);
  const availableActions = getAvailableActions();
  // PHASE 4: Display-only category copy. For cars this is a no-op identity
  // map, so the standard car flow renders byte-for-byte unchanged.
  const categoryCopy = getBookingCategoryCopy(vehicle?.vehicle_category);

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 flex items-center gap-3 sm:gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-lg sm:text-xl font-semibold">Booking Details</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        
        <HostDirectModeBanner variant="subtle" context="booking" />
        
        {/* Status Banner - SINGLE SOURCE OF TRUTH */}
        <Card className="border-slate-100" data-testid="booking-status-card">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="text-xs sm:text-sm text-slate-500 mb-1">
                  Booking #{bookingId.slice(0, 8)}
                </p>
                <Badge className={statusConfig.color} data-testid="booking-status-badge">
                  {applyCategoryCopy(statusConfig.label, categoryCopy)}
                </Badge>
                <p className="text-sm text-slate-600 mt-2">
                  {(() => {
                    // PHASE B: In host_direct mode, override the renter and
                    // host wording on approved_pending_payment because no
                    // funds flow through HireHub — the renter pays the host
                    // directly off-platform.
                    if (
                      paymentMode === 'host_direct' &&
                      currentStatus === 'approved_pending_payment'
                    ) {
                      return isHost
                        ? 'No action needed. Waiting for the renter to arrange payment with you (PayID, bank transfer, etc).'
                        : 'Approved! Arrange payment with your host. Your host will confirm here once funds are received.';
                    }
                    return isHost
                      ? applyCategoryCopy(statusConfig.hostHelperText || statusConfig.description, categoryCopy)
                      : applyCategoryCopy(statusConfig.renterHelperText || statusConfig.description, categoryCopy);
                  })()}
                </p>
              </div>
              <div className="text-left sm:text-right">
                {isHost ? (
                  // HOST VIEW: Show host earnings, not renter total
                  <>
                    <p className="text-xs sm:text-sm text-slate-500">Your Earnings</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600" data-testid="host-earnings-amount">
                      ${(paymentMode === 'host_direct'
                          ? (booking.subtotal || 0)
                          : (booking.host_payout_cents
                              ? (booking.host_payout_cents / 100)
                              : (booking.subtotal - (booking.host_fee_amount || booking.platform_fee * 0.3 || 0))
                            )
                        )?.toFixed(2)} AUD
                    </p>
                    {paymentMode === 'host_direct' ? (
                      <p className="text-xs italic text-slate-500 mt-1" data-testid="no-platform-fee-host-summary">
                        No platform fee (direct payment mode)
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400 mt-1">
                        Guest paid: ${booking.total_amount?.toFixed(2) || ((booking.subtotal || 0) + (booking.renter_fee_amount || booking.platform_fee * 0.7 || 0))?.toFixed(2)} AUD
                      </p>
                    )}
                  </>
                ) : (
                  // RENTER VIEW: Show their total
                  <>
                    <p className="text-xs sm:text-sm text-slate-500">Total</p>
                    <p className="text-xl sm:text-2xl font-bold text-[#004B8D]" data-testid="renter-total-amount">
                      ${(paymentMode === 'host_direct'
                          ? (booking.subtotal || 0)
                          : booking.total_amount
                        )?.toFixed(2)} AUD
                    </p>
                    {paymentMode === 'host_direct' && (
                      <p className="text-xs italic text-slate-500 mt-1" data-testid="no-platform-fee-renter-summary">
                        No platform fee (direct payment mode)
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ════════════════════════════════════════════════════════════════════ */}
        {/* "WHAT HAPPENS NEXT" STRIP - UX Clarity Enhancement                   */}
        {/* Shows who needs to act and what happens next in plain English        */}
        {/* ════════════════════════════════════════════════════════════════════ */}
        {currentStatus && !['completed', 'cancelled', 'approval_expired'].includes(currentStatus) && (() => {
          const nextInfo = WHAT_HAPPENS_NEXT[currentStatus] || {};
          let actionText = isHost ? nextInfo.hostAction : nextInfo.renterAction;
          // PHASE 4: Apply category-aware wording (no-op for cars).
          actionText = applyCategoryCopy(actionText, categoryCopy);
          // PHASE B: In host_direct mode, the renter does not "complete payment"
          // on-platform — they arrange it directly with the host. Override the
          // approved_pending_payment text for both roles to keep messaging
          // consistent with the host-direct banner / status helper text.
          if (paymentMode === 'host_direct' && currentStatus === 'approved_pending_payment') {
            actionText = isHost
              ? 'Waiting for the renter to arrange payment with you. Tap "Mark Payment Received" once funds are received.'
              : 'Approved! Arrange payment with your host. Your host will confirm here once funds are received.';
          }
          const isMyTurn = (isHost && nextInfo.whoActsNext?.toLowerCase().includes('host')) || 
                          (isRenter && nextInfo.whoActsNext?.toLowerCase().includes('renter'));
          
          // Determine color based on who needs to act
          const stripColor = isMyTurn 
            ? 'bg-blue-50 border-blue-200' 
            : 'bg-slate-50 border-slate-200';
          const iconColor = isMyTurn ? 'text-blue-600' : 'text-slate-500';
          const labelColor = isMyTurn ? 'text-blue-800' : 'text-slate-700';
          const textColor = isMyTurn ? 'text-blue-700' : 'text-slate-600';
          
          return (
            <div className={`rounded-lg border p-3 sm:p-4 ${stripColor}`} data-testid="what-happens-next-strip">
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 mt-0.5 ${iconColor}`}>
                  {isMyTurn ? (
                    <ArrowRight className="w-5 h-5" />
                  ) : (
                    <Clock className="w-5 h-5" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${labelColor}`}>
                    {isMyTurn ? 'Your turn' : 'Waiting'}
                  </p>
                  <p className={`text-sm ${textColor} mt-0.5`}>
                    {actionText}
                  </p>
                  {booking?.cooldown_ends_at && ['cooldown_window_open', 'posthire_submitted'].includes(currentStatus) && (
                    <p className="text-xs text-slate-500 mt-1">
                      Review period ends {new Date(booking.cooldown_ends_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* BOND AUTHORIZATION BANNER - Show prominently when bond pending during payment/pre-hire */}
        {paymentMode !== 'host_direct' && isRenter && booking.bond_mode && booking.bond_mode !== 'none' && 
         ['paid', 'prehire_in_progress', 'prehire_submitted'].includes(booking.status) && ['pending', 'requires_action'].includes(booking.bond_status) && (
          <Card className="border-amber-300 bg-amber-50 shadow-md" data-testid="bond-authorization-banner">
            <CardContent className="p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900 text-base">
                      Next Step: Authorise Security Bond
                    </h3>
                    <p className="text-sm text-amber-700 mt-1">
                      Your booking payment is complete. Please authorise the ${((booking.bond_required_cents || booking.bond_amount * 100 || 0) / 100).toFixed(2)} AUD security bond hold to confirm your booking.
                    </p>
                    <p className="text-xs text-amber-600 mt-2">
                      {booking.bond_mode === 'authorize' 
                        ? 'This is a temporary hold — no charge unless damages occur.' 
                        : 'This will be refunded after the trip if no issues.'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => handleAction('pay_bond')}
                  disabled={bondLoading}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2.5 sm:flex-shrink-0"
                  data-testid="authorize-bond-btn-banner"
                >
                  {bondLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Shield className="w-4 h-4 mr-2" />
                      {booking.bond_mode === 'authorize' ? 'Authorise Bond Hold' : 'Pay Security Bond'}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* VEHICLE + BOOKING SUMMARY CARD - P0 UI Polish */}
        <Card className="border-slate-200" data-testid="vehicle-summary-card">
          <CardContent className="p-4 sm:p-6">
            <div className="flex gap-4">
              {/* Vehicle Photo Thumbnail - using canonical helper */}
              <div className="w-20 h-16 sm:w-24 sm:h-18 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100">
                {getVehiclePrimaryImage(vehicle) ? (
                  <img 
                    src={getVehiclePrimaryImage(vehicle)} 
                    alt={vehicle?.title || 'Vehicle'} 
                    className="w-full h-full object-cover"
                    onError={(e) => handleVehicleImageError(e, vehicle?.vehicle_id)}
                  />
                ) : getVehiclePrimaryImage(booking) ? (
                  <img 
                    src={getVehiclePrimaryImage(booking)} 
                    alt="Vehicle" 
                    className="w-full h-full object-cover"
                    onError={(e) => handleVehicleImageError(e, booking?.vehicle_id)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Camera className="w-6 h-6 text-slate-300" />
                  </div>
                )}
              </div>
              
              {/* Vehicle & Booking Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm sm:text-base truncate">
                  {vehicle?.title || (vehicle?.make && vehicle?.model ? `${vehicle.make} ${vehicle.model}` : null) || `${booking.vehicle_make || ''} ${booking.vehicle_model || ''}`.trim() || 'Vehicle'}
                </h3>
                {/* Vehicle not found warning */}
                {booking.vehicle_missing && (
                  <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5">
                    <AlertCircle className="w-3 h-3" />
                    Vehicle not found
                  </p>
                )}
                {!booking.vehicle_missing && (vehicle?.location || booking.location) && (
                  <p className="text-xs text-slate-500 truncate mt-0.5">
                    {typeof vehicle?.location === 'string' ? vehicle.location : (vehicle?.location?.suburb ? `${vehicle.location.suburb}${vehicle?.location?.state ? ', ' + vehicle.location.state : ''}` : '')} {!vehicle?.location && booking.location ? booking.location : ''}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-600">
                  <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                  <span>{booking.start_date} – {booking.end_date}</span>
                </div>
                {booking.total_days && (
                  <p className="text-xs text-slate-500 mt-1">
                    {booking.total_days} day{booking.total_days > 1 ? 's' : ''} • ${booking.daily_rate?.toFixed(0)}/day
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Display-only "What was confirmed at booking" — surfaces existing
            audit fields (licence_declaration / category_declarations /
            supply_confirmation) without issuing requests, mutating state, or
            exposing IP/UA. Renders nothing when no fields exist. */}
        <WhatWasConfirmedCard booking={booking} />

        {/* ACTION PANEL - Only shows buttons for allowed actions */}
        {availableActions.length > 0 && (
          <Card className="border-[#004B8D] bg-blue-50" data-testid="actions-panel" ref={extraChargesSectionRef}>
            <CardContent className="p-4 sm:p-6">
              {/* PHASE B: Host-direct payment banner (only on approved_pending_payment) */}
              {paymentMode === 'host_direct' && currentStatus === 'approved_pending_payment' && (
                <div
                  className="mb-4 p-3 rounded-lg border border-emerald-200 bg-emerald-50 text-sm text-emerald-900"
                  data-testid="host-direct-payment-banner"
                >
                  {isHost ? (
                    <>
                      <p className="font-semibold mb-1">Direct-payment mode</p>
                      <p>
                        Arrange payment directly with the renter (PayID, bank transfer, etc).
                        Once funds are received, confirm below to continue.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold mb-1">Contact your host to arrange payment</p>
                      <p>
                        Arrange payment directly with your host (PayID, bank transfer, etc).
                        Your host will confirm below once funds are received — you'll get a notification.
                      </p>
                    </>
                  )}
                </div>
              )}
              {/* P1: Payment Required Banner (after dispute resolved with require_payment) */}
              {booking?.extra_charges_resolution === 'require_payment' && 
               booking?.extra_charges_status !== 'paid' && 
               !isHost && (
                <div className="mb-4 p-3 bg-amber-100 border border-amber-300 rounded-lg flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-amber-800 font-medium text-sm">Payment Required</p>
                    <p className="text-amber-700 text-xs">Your dispute was reviewed and the charges have been upheld. Please pay ${posthireData?.total_extra_charges?.toFixed(2) || '0.00'} to complete the booking.</p>
                  </div>
                </div>
              )}
              
              <h3 className="font-semibold mb-3 text-sm sm:text-base flex items-center gap-2">
                <Play className="w-5 h-5 text-[#004B8D]" />
                {isHost ? 'Host Actions' : 'Your Actions'}
              </h3>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                {availableActions.includes('approve') && (
                  <Button 
                    onClick={() => handleAction('approve')}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={actionLoading}
                    data-testid="approve-booking-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve Booking
                  </Button>
                )}
                
                {availableActions.includes('decline') && (
                  <Button 
                    onClick={() => setShowDeclineModal(true)}
                    variant="destructive"
                    className="flex-1"
                    disabled={actionLoading}
                    data-testid="decline-booking-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Decline
                  </Button>
                )}
                
                {/* P2 FIX: Host can cancel unpaid approved bookings */}
                {availableActions.includes('cancel_unpaid_hold') && (
                  <Button 
                    onClick={() => handleAction('cancel_unpaid_hold')}
                    variant="outline"
                    className="flex-1 border-amber-500 text-amber-700 hover:bg-amber-50"
                    disabled={actionLoading}
                    data-testid="cancel-unpaid-hold-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Release Unpaid Hold
                  </Button>
                )}
                
                {availableActions.includes('pay') && (
                  <Button 
                    onClick={() => handleAction('pay')}
                    className="flex-1 bg-[#004B8D] hover:bg-[#003865]"
                    disabled={actionLoading}
                    data-testid="pay-now-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay ${booking.total_amount?.toFixed(2)} AUD
                  </Button>
                )}

                {/* PHASE B: Host-direct — host confirms receipt of off-platform payment */}
                {availableActions.includes('mark_payment_received') && (
                  <Button
                    onClick={() => handleAction('mark_payment_received')}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={actionLoading}
                    data-testid="mark-payment-received-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark Payment Received
                  </Button>
                )}
                
                {availableActions.includes('cancel') && (
                  <Button 
                    onClick={() => handleAction('cancel')}
                    variant="outline"
                    className="flex-1 border-red-300 text-red-600"
                    disabled={actionLoading}
                    data-testid="cancel-booking-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel Booking
                  </Button>
                )}
                
                {availableActions.includes('start_prehire') && (
                  <Button 
                    onClick={() => handleAction('start_prehire')}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={actionLoading}
                    data-testid="start-prehire-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Start Pre-Hire Checklist
                  </Button>
                )}
                
                {(availableActions.includes('continue_prehire') || availableActions.includes('submit_prehire')) && (
                  <Button 
                    onClick={() => setShowPrehireWizard(true)}
                    className="flex-1 bg-amber-600 hover:bg-amber-700"
                    disabled={actionLoading}
                    data-testid="continue-prehire-btn"
                  >
                    <FileCheck className="w-4 h-4 mr-2" />
                    Continue Pre-Hire Checklist
                  </Button>
                )}
                
                {availableActions.includes('start_trip') && (
                  <Button 
                    onClick={() => handleAction('start_trip')}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    disabled={actionLoading}
                    data-testid="start-trip-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {categoryCopy.startActionLabel}
                  </Button>
                )}
                
                {/* Return Checklist - GATED by can_start_return_checklist */}
                {availableActions.includes('start_posthire') && booking?.can_start_return_checklist && (
                  <Button 
                    onClick={() => handleAction('start_posthire')}
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                    disabled={actionLoading}
                    data-testid="start-posthire-btn"
                  >
                    <Clipboard className="w-4 h-4 mr-2" />
                    Start Return Checklist
                  </Button>
                )}
                
                {/* Show disabled message when return is not yet available */}
                {availableActions.includes('start_posthire') && !booking?.can_start_return_checklist && (
                  <div className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-lg text-center">
                    <p className="text-sm text-slate-600">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Return checklist available after {categoryCopy.activity} end date
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {categoryCopy.activityCap} ends: {booking?.end_date}
                    </p>
                  </div>
                )}
                
                {(availableActions.includes('continue_posthire') || availableActions.includes('submit_posthire')) && (
                  <Button 
                    onClick={() => setShowPosthireWizard(true)}
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                    disabled={actionLoading}
                    data-testid="continue-posthire-btn"
                  >
                    <FileCheck className="w-4 h-4 mr-2" />
                    Continue Return Checklist
                  </Button>
                )}
                
                {availableActions.includes('accept_charges') && (
                  <Button 
                    onClick={() => handleAction('accept_charges')}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={actionLoading}
                    data-testid="accept-charges-btn"
                  >
                    {/* P0 FIX: Bond settlement - no "Pay" button if bond covers charges */}
                    {posthireData?.total_extra_charges > 0 ? (
                      ((posthireData.total_extra_charges + (posthireData.total_extra_charges * 0.029) + 0.30) <= (booking?.bond_amount || 500)) ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Accept Charges
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Accept & Pay Remaining
                        </>
                      )
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Accept & Complete
                      </>
                    )}
                  </Button>
                )}
                
                {availableActions.includes('dispute_charges') && (
                  <Button 
                    onClick={() => handleAction('dispute_charges')}
                    variant="outline"
                    className="flex-1 border-red-300 text-red-600"
                    disabled={actionLoading}
                    data-testid="dispute-charges-btn"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Dispute Charges
                  </Button>
                )}
                
                {/* View Dispute button when booking is disputed */}
                {currentStatus === 'disputed' && (
                  <Button 
                    onClick={handleViewDispute}
                    variant="outline"
                    className="flex-1 border-amber-300 text-amber-600"
                    data-testid="view-dispute-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Dispute
                  </Button>
                )}
                
                {/* P1: Pay Now button when dispute resolved with require_payment */}
                {(currentStatus === 'disputed' || currentStatus === 'posthire_submitted') && 
                 booking?.extra_charges_resolution === 'require_payment' && 
                 booking?.extra_charges_status !== 'paid' && (
                  <Button 
                    onClick={() => handleAction('accept_charges')}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={actionLoading}
                    data-testid="pay-required-charges-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay ${posthireData?.total_extra_charges?.toFixed(2) || '0.00'} Now
                  </Button>
                )}
                
                {availableActions.includes('leave_review') && (
                  <Button 
                    onClick={() => handleAction('leave_review')}
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600"
                    disabled={actionLoading}
                    data-testid="leave-review-btn"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Leave Review
                  </Button>
                )}
                
                {/* P0 Claims Engine: Host can report issues after post-hire */}
                {isHost && ['posthire_submitted', 'cooldown_window_open', 'completed', 'disputed'].includes(currentStatus) && (
                  <Button 
                    onClick={() => setShowClaimWizard(true)}
                    variant="outline"
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="report-issue-btn"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Report Issue
                  </Button>
                )}
                
                {/* P0 Extension System: Renter can request or pay for extension during active trip */}
                {isRenter && ['active', 'trip_active', 'prehire_submitted'].includes(currentStatus) && (
                  (() => {
                    // P0 FIX: Check for extension statuses to show appropriate UI
                    const pendingExtension = extensions.find(ext => ext.status === 'PENDING');
                    const awaitingPaymentExtension = extensions.find(ext => ext.status === 'APPROVED_AWAITING_PAYMENT');
                    const awaitingBondExtension = extensions.find(ext => ext.status === 'APPROVED_AWAITING_BOND');
                    
                    // P0 FIX: Show bond authorization prompt if extension payment done but bond required
                    if (awaitingBondExtension) {
                      return (
                        <div className="flex-1 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="flex items-center gap-2 text-purple-700 mb-2">
                            <Shield className="w-4 h-4" />
                            <span className="text-sm font-medium">Security Bond Required</span>
                          </div>
                          <p className="text-xs text-purple-600 mb-2">
                            Extension payment received. Please authorise the security bond hold to activate your extension.
                          </p>
                          <Button 
                            size="sm"
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                            onClick={() => handleExtensionBondAuth(awaitingBondExtension.extension_id)}
                            disabled={extensionPaymentLoading}
                            data-testid="extension-bond-auth-btn"
                          >
                            {extensionPaymentLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <Shield className="w-4 h-4 mr-2" />
                                Authorise Bond Hold
                              </>
                            )}
                          </Button>
                        </div>
                      );
                    }
                    
                    // Show payment prompt if host approved
                    if (awaitingPaymentExtension) {
                      return (
                        <div className="flex-1 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-2 text-green-700 mb-2">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">Extension Approved!</span>
                          </div>
                          <p className="text-xs text-green-600 mb-2">Complete payment to extend your booking.</p>
                          <Button 
                            size="sm"
                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handlePayExtension(awaitingPaymentExtension.extension_id)}
                            disabled={extensionPaymentLoading}
                            data-testid="pay-extension-action-btn"
                          >
                            {extensionPaymentLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <CreditCard className="w-4 h-4 mr-2" />
                                Pay ${awaitingPaymentExtension.pricing_breakdown?.total?.toFixed(2)}
                              </>
                            )}
                          </Button>
                        </div>
                      );
                    }
                    
                    // Show pending status
                    if (pendingExtension) {
                      return (
                        <div className="flex-1 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center gap-2 text-blue-700">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm font-medium">Extension Request Pending</span>
                          </div>
                          <p className="text-xs text-blue-600 mt-1">Awaiting host approval</p>
                        </div>
                      );
                    }
                    
                    // Show request button if no pending/awaiting extensions
                    return (
                      <Button 
                        onClick={() => setShowExtensionModal(true)}
                        variant="outline"
                        className="flex-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                        data-testid="request-extension-btn"
                      >
                        <CalendarPlus className="w-4 h-4 mr-2" />
                        Request Extension
                      </Button>
                    );
                  })()
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* WAITING STATE / HELPER PANEL - Role-aware status messages */}
        {!availableActions.length && !['completed', 'cancelled'].includes(currentStatus) && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600 animate-pulse" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm sm:text-base text-amber-800">
                    {/* P0 FIX: Role-aware helper panel titles */}
                    {currentStatus === 'requested' && (isHost ? 'Action Required' : 'Waiting for Host')}
                    {currentStatus === 'approved_pending_payment' && (isHost ? 'Waiting for Renter Payment' : 'Payment Required')}
                    {currentStatus === 'paid' && 'Ready for Pre-Hire Inspection'}
                    {currentStatus === 'prehire_in_progress' && 'Pre-Hire Inspection in Progress'}
                    {currentStatus === 'prehire_submitted' && 'Pre-Hire Complete'}
                    {(currentStatus === 'active' || currentStatus === 'trip_active') && `${categoryCopy.activityCap} in Progress`}
                    {currentStatus === 'posthire_in_progress' && 'Return Inspection in Progress'}
                    {currentStatus === 'posthire_submitted' && 'Review Period Active'}
                    {currentStatus === 'cooldown_window_open' && 'Review Period Active'}
                    {currentStatus === 'claim_submitted' && (isHost ? 'Awaiting Renter Response' : 'Claim Submitted')}
                    {currentStatus === 'disputed' && 'Under Review'}
                    {!['requested', 'approved_pending_payment', 'paid', 'prehire_in_progress', 'prehire_submitted', 'active', 'trip_active', 'posthire_in_progress', 'posthire_submitted', 'cooldown_window_open', 'claim_submitted', 'disputed'].includes(currentStatus) && `Waiting for ${isHost ? 'Renter' : 'Host'}`}
                  </h3>
                  <p className="text-xs sm:text-sm text-amber-700">
                    {/* P0 FIX: Role-aware helper panel descriptions */}
                    {currentStatus === 'requested' && (isHost ? 'Approve or decline this booking request' : 'Waiting for host to approve or decline')}
                    {currentStatus === 'approved_pending_payment' && (
                      paymentMode === 'host_direct'
                        ? (isHost ? 'Waiting for renter to arrange payment with you' : 'Arrange payment with your host to confirm this booking')
                        : (isHost ? 'Booking will confirm once payment is completed' : 'Complete payment to confirm this booking')
                    )}
                    {currentStatus === 'paid' && (isHost ? 'Start the pre-hire inspection when ready' : 'Waiting for host to begin pre-hire inspection')}
                    {currentStatus === 'prehire_in_progress' && applyCategoryCopy('Host is completing the vehicle inspection', categoryCopy)}
                    {currentStatus === 'prehire_submitted' && (isRenter ? applyCategoryCopy('You can now start your trip', categoryCopy) : applyCategoryCopy('Waiting for renter to start the trip', categoryCopy))}
                    {(currentStatus === 'active' || currentStatus === 'trip_active') && applyCategoryCopy('Vehicle currently on hire', categoryCopy)}
                    {currentStatus === 'posthire_in_progress' && applyCategoryCopy('Host is reviewing the vehicle condition', categoryCopy)}
                    {currentStatus === 'posthire_submitted' && 'Claims or disputes may be submitted until the review period ends'}
                    {currentStatus === 'cooldown_window_open' && 'Claims or disputes may be submitted until the review period ends'}
                    {currentStatus === 'claim_submitted' && (isHost ? 'Waiting for renter to respond to the claim' : 'Host has submitted additional charges')}
                    {currentStatus === 'disputed' && 'Admin is reviewing the dispute'}
                    {!['requested', 'approved_pending_payment', 'paid', 'prehire_in_progress', 'prehire_submitted', 'active', 'trip_active', 'posthire_in_progress', 'posthire_submitted', 'cooldown_window_open', 'claim_submitted', 'disputed'].includes(currentStatus) && applyCategoryCopy(statusConfig.description, categoryCopy)}
                  </p>
                </div>
              </div>
              
              {/* P1 FIX: Payment Deadline Countdown for approved_pending_payment.
                  PHASE B: Suppressed in host_direct mode — the host-direct
                  banner above already explains the off-platform workflow,
                  and the long deadline (72h) makes a ticking countdown
                  misleading and contradictory ("complete payment" vs
                  "arrange payment with your host"). */}
              {currentStatus === 'approved_pending_payment' && booking?.payment_deadline_info && paymentMode !== 'host_direct' && (
                <PaymentCountdown 
                  paymentDeadlineInfo={booking.payment_deadline_info}
                  isHost={isHost}
                  onExpired={() => {
                    // Refetch booking to get updated status
                    fetchBooking();
                  }}
                  className="mt-3"
                  data-testid="booking-payment-countdown"
                />
              )}
              
              {/* Show expired message for approval_expired status */}
              {currentStatus === 'approval_expired' && (
                <div className="mt-3 p-4 rounded-lg border-2 bg-slate-100 border-slate-300" data-testid="booking-expired-message">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-slate-200">
                      <Clock className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-700">Booking Approval Expired</h4>
                      <p className="text-sm mt-1 text-slate-600">
                        {isHost 
                          ? 'The renter did not complete payment within the deadline. The dates have been released and are available for new bookings.'
                          : 'Payment was not completed within the deadline. The dates have been released. You can request to book again if the dates are still available.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* P0: PLATFORM OWNER DEV TOOLS - Reset Pre-Hire for Testing */}
        {isPlatformOwner && currentStatus !== 'cancelled' && (
          <Card className="border-purple-400 bg-purple-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-purple-800">Platform Owner Tools</h4>
                    <p className="text-xs text-purple-600">Admin actions for testing & support</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Link to={`/admin/bookings/${bookingId}/evidence`}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-purple-300 text-purple-700 hover:bg-purple-100"
                      data-testid="view-evidence-btn"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View Evidence
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowResetPrehireModal(true)}
                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                    data-testid="reset-prehire-btn"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Reset Pre-Hire
                  </Button>
                  {/* PART B: Advance to Post-Hire button */}
                  {(booking?.is_test === true || booking?.test_tag) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAdvanceToPosthireModal(true)}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      data-testid="advance-posthire-btn"
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      Advance to Post-Hire
                    </Button>
                  )}
                  {/* P0: Fast-Track Button - shows when status allows */}
                  {currentStatus === 'active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFastTrackTarget('posthire_ready');
                        setShowFastTrackModal(true);
                      }}
                      className="border-orange-300 text-orange-700 hover:bg-orange-100"
                      data-testid="fast-track-posthire-btn"
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      Fast-Track to Return
                    </Button>
                  )}
                  {currentStatus === 'posthire_submitted' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFastTrackTarget('completed');
                        setShowFastTrackModal(true);
                      }}
                      className="border-green-300 text-green-700 hover:bg-green-100"
                      data-testid="fast-track-complete-btn"
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      Fast-Track Complete
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* P0: Reset Pre-Hire Confirmation Modal */}
        {showResetPrehireModal && (
          <Dialog open={showResetPrehireModal} onOpenChange={setShowResetPrehireModal}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-purple-800">
                  <RefreshCw className="w-5 h-5" />
                  Reset Pre-Hire Checklist
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800 font-medium">⚠️ This will:</p>
                  <ul className="text-xs text-amber-700 mt-1 space-y-1 list-disc list-inside">
                    <li>Delete all pre-hire checklist photos (DB + S3)</li>
                    <li>Delete pre-hire checklist record</li>
                    <li>Reset booking status to "paid"</li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-600">Booking ID:</p>
                  <p className="font-mono text-sm font-medium">{bookingId}</p>
                </div>
                <p className="text-sm text-slate-600">
                  This action is only available for test bookings and cannot be undone.
                </p>
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowResetPrehireModal(false)}
                  disabled={resetPrehireLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleResetPrehire}
                  disabled={resetPrehireLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="confirm-reset-prehire-btn"
                >
                  {resetPrehireLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Confirm Reset
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* PART B: Advance to Post-Hire Confirmation Modal */}
        {showAdvanceToPosthireModal && (
          <Dialog open={showAdvanceToPosthireModal} onOpenChange={setShowAdvanceToPosthireModal}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-blue-800">
                  <Clock className="w-5 h-5" />
                  Advance to Post-Hire
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800 font-medium">ℹ️ This will:</p>
                  <ul className="text-xs text-blue-700 mt-1 space-y-1 list-disc list-inside">
                    <li>Set booking status to "posthire_in_progress"</li>
                    <li>Set trip_started_at to 1 hour ago (if not set)</li>
                    <li>Set trip_ended_at to now</li>
                    <li>Unlock the Post-Hire checklist wizard</li>
                  </ul>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-700">
                    <strong>⚠️ TEST ONLY:</strong> This action simulates time passing so you can test the post-hire flow immediately.
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-600">Booking ID:</p>
                  <p className="font-mono text-sm font-medium">{bookingId}</p>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAdvanceToPosthireModal(false)}
                  disabled={advanceToPosthireLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAdvanceToPosthire}
                  disabled={advanceToPosthireLoading}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-advance-posthire-btn"
                >
                  {advanceToPosthireLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Advancing...
                    </>
                  ) : (
                    <>
                      <Clock className="w-4 h-4 mr-2" />
                      Confirm Advance
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* P0: Fast-Track Confirmation Modal */}
        {showFastTrackModal && (
          <Dialog open={showFastTrackModal} onOpenChange={setShowFastTrackModal}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-orange-800">
                  <Zap className="w-5 h-5" />
                  Fast-Track Booking
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className={`${fastTrackTarget === 'posthire_ready' ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'} border rounded-lg p-3`}>
                  <p className={`text-sm font-medium ${fastTrackTarget === 'posthire_ready' ? 'text-orange-800' : 'text-green-800'}`}>
                    ⚡ This will {fastTrackTarget === 'posthire_ready' ? 'skip to return phase' : 'mark booking as complete'}:
                  </p>
                  <ul className={`text-xs mt-1 space-y-1 list-disc list-inside ${fastTrackTarget === 'posthire_ready' ? 'text-orange-700' : 'text-green-700'}`}>
                    {fastTrackTarget === 'posthire_ready' ? (
                      <>
                        <li>Set status to "posthire_in_progress"</li>
                        <li>Set trip_ended_at to now</li>
                        <li>Enable "Start Return Checklist" immediately</li>
                      </>
                    ) : (
                      <>
                        <li>Set status to "completed"</li>
                        <li>Set completed_at to now</li>
                        <li>Mark booking as finished</li>
                      </>
                    )}
                  </ul>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs text-purple-700">
                    <strong>🔒 Platform Owner Only:</strong> This action is logged for audit purposes.
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-600">Current Status:</p>
                  <p className="font-mono text-sm font-medium">{currentStatus} → {fastTrackTarget === 'posthire_ready' ? 'posthire_in_progress' : 'completed'}</p>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowFastTrackModal(false)}
                  disabled={fastTrackLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleFastTrack(fastTrackTarget)}
                  disabled={fastTrackLoading}
                  className={fastTrackTarget === 'posthire_ready' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}
                  data-testid="confirm-fast-track-btn"
                >
                  {fastTrackLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Fast-Track Now
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* TRIP ACTIVE INFO */}
        {currentStatus === 'active' && (
          <Card className="border-green-300 bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-4 sm:p-6">
              <div className="text-center mb-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg text-green-800">Trip In Progress</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3 text-center border border-green-200">
                  <p className="text-xs text-slate-500 mb-1">Started</p>
                  <p className="font-semibold text-sm">
                    {booking.trip_started_at ? formatTimestamp(booking.trip_started_at) : booking.start_date}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border border-green-200">
                  <p className="text-xs text-slate-500 mb-1">Return By</p>
                  <p className="font-semibold text-sm">
                    {booking.trip_end_time ? formatTimestamp(booking.trip_end_time) : booking.end_date}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* POST-HIRE CHARGES PREVIEW */}
        {currentStatus === 'posthire_submitted' && posthireData && (
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="p-4 sm:p-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-purple-600" />
                Return Inspection Summary
              </h3>
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="space-y-2 text-sm">
                  {posthireData.excess_km_fee > 0 && (
                    <div className="flex justify-between">
                      <span>Excess KMs ({posthireData.excess_kms} km)</span>
                      <span className="text-amber-700">+${posthireData.excess_km_fee.toFixed(2)}</span>
                    </div>
                  )}
                  {posthireData.fuel_shortfall_fee > 0 && (
                    <div className="flex justify-between">
                      <span>Fuel Shortfall</span>
                      <span className="text-amber-700">+${posthireData.fuel_shortfall_fee.toFixed(2)}</span>
                    </div>
                  )}
                  {posthireData.cleaning_fee > 0 && (
                    <div className="flex justify-between">
                      <span>Cleaning Fee</span>
                      <span className="text-amber-700">+${posthireData.cleaning_fee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between font-semibold">
                      <span>Total Extra Charges</span>
                      <span className={posthireData.total_extra_charges > 0 ? 'text-red-600' : 'text-green-600'}>
                        ${posthireData.total_extra_charges?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* REVIEW PERIOD MESSAGE - Human-readable status for post-hire completion */}
        {(currentStatus === 'posthire_submitted' || currentStatus === 'cooldown_window_open') && 
         lifecycle?.post_hire?.status === 'submitted' && (
          <Card className="border-blue-200 bg-blue-50" data-testid="review-period-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-blue-900 mb-2">
                    Return inspection complete.
                  </h3>
                  <div className="space-y-2 text-sm text-blue-800">
                    <p>
                      Any additional charges may be disputed within 48 hours.
                    </p>
                    <p className="text-blue-600">
                      This booking will automatically complete after the 48-hour review period.
                    </p>
                  </div>
                  {booking?.cooldown_expires_at && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <div className="text-xs text-blue-600 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Review period ends: {new Date(booking.cooldown_expires_at).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* P0 FIX: PROMINENT CHARGES ALERT FOR RENTER - Auto-generated from post-hire checklist */}
        {isRenter && (currentStatus === 'posthire_submitted' || currentStatus === 'cooldown_window_open') && 
         posthireData?.total_extra_charges > 0 && (
          <Card className="border-amber-300 bg-amber-50" data-testid="renter-charges-alert">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-amber-900 mb-2">
                    Post-Hire Charges: ${posthireData.total_extra_charges?.toFixed(2)}
                  </h3>
                  <p className="text-sm text-amber-800 mb-4">
                    The host has submitted additional charges from the return inspection. 
                    These will be deducted from your security bond.
                  </p>
                  
                  {/* Charges Breakdown */}
                  <div className="bg-white rounded-lg p-4 border border-amber-200 space-y-2 text-sm">
                    <div className="font-medium text-slate-700 mb-2">Charge Breakdown:</div>
                    {posthireData.toll_charges > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Toll Charges</span>
                        <span className="font-medium">${posthireData.toll_charges.toFixed(2)}</span>
                      </div>
                    )}
                    {posthireData.late_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Late Return Fee</span>
                        <span className="font-medium">${posthireData.late_fee.toFixed(2)}</span>
                      </div>
                    )}
                    {posthireData.excess_km_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Excess Kilometres ({posthireData.excess_kms?.toFixed(0) || 0} km)</span>
                        <span className="font-medium">${posthireData.excess_km_fee.toFixed(2)}</span>
                      </div>
                    )}
                    {posthireData.fuel_shortfall_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Fuel Shortfall</span>
                        <span className="font-medium">${posthireData.fuel_shortfall_fee.toFixed(2)}</span>
                      </div>
                    )}
                    {posthireData.cleaning_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Cleaning Fee</span>
                        <span className="font-medium">${posthireData.cleaning_fee.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 mt-2 flex justify-between font-medium">
                      <span>Subtotal</span>
                      <span>${posthireData.total_extra_charges.toFixed(2)}</span>
                    </div>
                    
                    {/* Settlement from Bond */}
                    <div className="border-t pt-3 mt-3 space-y-2 bg-slate-50 -mx-4 px-4 py-3 rounded-b">
                      <div className="font-medium text-slate-700">Settlement from Bond:</div>
                      <div className="flex justify-between text-slate-600">
                        <span>Bond Held</span>
                        <span className="font-medium">${booking?.bond_amount?.toFixed(2) || '500.00'}</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>Charges</span>
                        <span className="text-red-600">-${posthireData.total_extra_charges.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>Processing Fee (payment cost recovery)</span>
                        <span className="text-red-600">-${((posthireData.total_extra_charges * 0.029) + 0.30).toFixed(2)}</span>
                      </div>
                      <div className="border-t pt-2 flex justify-between font-semibold text-green-700">
                        <span>Bond Returned to You</span>
                        <span>${Math.max(0, (booking?.bond_amount || 500) - posthireData.total_extra_charges - ((posthireData.total_extra_charges * 0.029) + 0.30)).toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-2">
                        <span>Host receives 100% of charges (no platform fee on reimbursements)</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons - Corrected for bond settlement */}
                  <div className="flex flex-col sm:flex-row gap-2 mt-4">
                    {/* Check if charges + fee are covered by bond */}
                    {(posthireData.total_extra_charges + (posthireData.total_extra_charges * 0.029) + 0.30) <= (booking?.bond_amount || 500) ? (
                      <Button 
                        onClick={() => handleAction('accept_charges')}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        disabled={actionLoading}
                        data-testid="accept-charges-btn"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Accept Charges
                      </Button>
                    ) : (
                      <Button 
                        onClick={() => handleAction('accept_charges')}
                        className="flex-1 bg-amber-600 hover:bg-amber-700"
                        disabled={actionLoading}
                        data-testid="pay-excess-btn"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Accept & Pay Remaining Balance
                      </Button>
                    )}
                    <Button 
                      onClick={() => handleAction('dispute_charges')}
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      disabled={actionLoading}
                      data-testid="dispute-charges-alert-btn"
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Dispute Charges
                    </Button>
                  </div>
                  
                  <p className="text-xs text-amber-600 mt-3">
                    If you don't respond within the review period, charges will be automatically deducted from your security bond.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* COMPLETED STATE */}
        {currentStatus === 'completed' && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4 sm:p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-lg text-green-800">Trip Completed</h3>
              <p className="text-sm text-green-700">This booking has been successfully completed.</p>
            </CardContent>
          </Card>
        )}

        {/* CANCELLED STATE */}
        {currentStatus === 'cancelled' && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 sm:p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-lg text-red-800">Booking Cancelled</h3>
              <p className="text-sm text-red-700">This booking was cancelled.</p>
            </CardContent>
          </Card>
        )}

        {/* CHECKLISTS SECTION - P0 LEGAL RECORD: View checklists + Evidence Packs */}
        {isHost && (
          <Card className="border-slate-200" data-testid="checklists-section" ref={prehireSectionRef}>
            <CardContent className="p-4 sm:p-6">
              <h3 className="font-semibold mb-3 text-sm flex items-center gap-2">
                <Clipboard className="w-5 h-5 text-[#004B8D]" />
                Checklists & Evidence Packs
              </h3>
              <div className="space-y-4">
                {/* Pre-Hire Checklist Section */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => {
                      fetchChecklistPhotos('prehire');
                      setShowPrehireViewer(true);
                    }}
                    className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition text-left"
                    data-testid="view-prehire-checklist-btn"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <FileCheck className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Pre-Hire Checklist</p>
                        <p className="text-xs text-slate-500">Vehicle handover inspection</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={
                        lifecycle?.pre_hire?.status === 'submitted' ? 'bg-green-100 text-green-700' :
                        lifecycle?.pre_hire?.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                        prehireData ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                      }>
                        {lifecycle?.pre_hire?.status === 'submitted' ? 'Complete' :
                         lifecycle?.pre_hire?.status === 'in_progress' ? 'In Progress' :
                         prehireData ? 'Draft' : 'Not Started'}
                      </Badge>
                      <ChevronLeft className="w-4 h-4 text-slate-400 rotate-180" />
                    </div>
                  </button>
                  
                  {/* Pre-Hire Evidence Pack - Only shown when checklist submitted */}
                  {lifecycle?.pre_hire?.status === 'submitted' && (
                    <div className="border-t border-slate-100 bg-slate-50 p-3">
                      <button
                        onClick={() => {
                          fetchEvidencePackStatus('prehire');
                          setShowEvidencePackModal({ open: true, type: 'prehire' });
                        }}
                        className="w-full flex items-center justify-between text-left"
                        data-testid="prehire-evidence-pack-btn"
                      >
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-[#004B8D]" />
                          <span className="text-sm text-[#004B8D]">Pre-Hire Evidence Pack</span>
                        </div>
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          {evidencePackStatus.prehire?.available ? 'Ready' : 'Available'}
                        </Badge>
                      </button>
                    </div>
                  )}
                </div>

                {/* Post-Hire Checklist Section */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => {
                      fetchChecklistPhotos('posthire');
                      setShowPosthireViewer(true);
                    }}
                    className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition text-left"
                    data-testid="view-posthire-checklist-btn"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                        <FileCheck className="w-4 h-4 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Post-Hire Checklist</p>
                        <p className="text-xs text-slate-500">Vehicle return inspection</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={
                        lifecycle?.post_hire?.status === 'submitted' ? 'bg-green-100 text-green-700' :
                        lifecycle?.post_hire?.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                        posthireData ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                      }>
                        {lifecycle?.post_hire?.status === 'submitted' ? 'Complete' :
                         lifecycle?.post_hire?.status === 'in_progress' ? 'In Progress' :
                         posthireData ? 'Draft' : 'Not Started'}
                      </Badge>
                      <ChevronLeft className="w-4 h-4 text-slate-400 rotate-180" />
                    </div>
                  </button>
                  
                  {/* Post-Hire Evidence Pack - Only shown when checklist submitted */}
                  {lifecycle?.post_hire?.status === 'submitted' && (
                    <div className="border-t border-slate-100 bg-slate-50 p-3">
                      <button
                        onClick={() => {
                          fetchEvidencePackStatus('posthire');
                          setShowEvidencePackModal({ open: true, type: 'posthire' });
                        }}
                        className="w-full flex items-center justify-between text-left"
                        data-testid="posthire-evidence-pack-btn"
                      >
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-orange-600" />
                          <span className="text-sm text-orange-700">Post-Hire Evidence Pack</span>
                        </div>
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          {evidencePackStatus.posthire?.available ? 'Ready' : 'Available'}
                        </Badge>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Communication Section - Always visible */}
        <Card className="border-slate-200">
          <CardContent className="p-4 sm:p-6">
            <h3 className="font-semibold mb-3 text-sm flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#004B8D]" />
              Communication
            </h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button 
                onClick={handleOpenMessages}
                variant="outline"
                className="flex-1 h-10 text-sm"
                data-testid="open-messages-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {isRenter ? 'Message Host' : 'Message Renter'}
              </Button>
              <Button 
                onClick={() => navigate('/support')}
                variant="outline"
                className="flex-1 h-10 text-sm"
                data-testid="contact-support-btn"
              >
                <Headphones className="w-4 h-4 mr-2" />
                Get Help
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Booking Agreement Download - Available after payment */}
        {['paid', 'prehire_in_progress', 'prehire_submitted', 'active', 'trip_active',
          'posthire_in_progress', 'posthire_submitted', 'cooldown_window_open', 
          'claim_submitted', 'disputed', 'completed'].includes(currentStatus) && (
          <Card className="border-slate-200" data-testid="agreement-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Booking Documents</h3>
                  <p className="text-xs text-slate-500">
                    {documentsLoading 
                      ? 'Loading documents...' 
                      : `${bookingDocuments.length} document${bookingDocuments.length !== 1 ? 's' : ''} available`
                    }
                  </p>
                </div>
              </div>
              
              {documentsLoading ? (
                <div className="flex items-center justify-center py-4 text-slate-500 text-sm">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading documents...
                </div>
              ) : bookingDocuments.length === 0 ? (
                <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                  <span className="text-sm text-slate-600">Rental Agreement</span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleDownloadAgreement}
                    disabled={agreementLoading}
                    data-testid="download-agreement-btn"
                  >
                    {agreementLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Generate & Download
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {bookingDocuments.map((doc, index) => (
                    <div 
                      key={`${doc.type}-${doc.extension_id || index}`} 
                      className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                        doc.type === 'booking_agreement' 
                          ? 'bg-blue-50 border border-blue-100' 
                          : 'bg-green-50 border border-green-100'
                      }`}
                      data-testid={`document-${doc.type}-${index}`}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className={`w-4 h-4 ${
                          doc.type === 'booking_agreement' ? 'text-blue-600' : 'text-green-600'
                        }`} />
                        <div>
                          <span className="text-sm font-medium">{doc.title}</span>
                          {doc.generated_at && (
                            <p className="text-xs text-slate-500">
                              Generated {format(parseISO(doc.generated_at), 'MMM d, yyyy')}
                            </p>
                          )}
                        </div>
                      </div>
                      {doc.download_url ? (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-8"
                          onClick={() => window.open(doc.download_url, '_blank')}
                          data-testid={`download-${doc.type}-btn`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      ) : doc.pending_pdf_generation ? (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Generating...
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">
                          Unavailable
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Platform agreements — informational links */}
              <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
                <Shield className="w-3.5 h-3.5" />
                <span>Platform agreements:</span>
                <Link to="/renter-agreement" target="_blank" className="text-blue-600 hover:underline" data-testid="view-renter-agreement-link">
                  Renter Agreement
                </Link>
                <span className="text-slate-300">·</span>
                <Link to="/trust-safety" target="_blank" className="text-blue-600 hover:underline" data-testid="view-trust-safety-link">
                  Trust & Safety
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* P1 Extension System: Extensions Section - Show for active trips */}
        {['active', 'trip_active', 'prehire_submitted'].includes(currentStatus) && (
          <Card className="border-blue-200 bg-blue-50/30" data-testid="extensions-section">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CalendarPlus className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-sm">Booking Extensions</h3>
                  {extensions.length > 0 && (
                    <Badge className="bg-blue-100 text-blue-700">{extensions.length}</Badge>
                  )}
                </div>
                {/* P0 FIX: Hide Request Extension if PENDING or APPROVED_AWAITING_PAYMENT */}
                {isRenter && !extensions.some(ext => ['PENDING', 'APPROVED_AWAITING_PAYMENT', 'APPROVED_AWAITING_BOND'].includes(ext.status)) && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-blue-300 text-blue-600 hover:bg-blue-100"
                    onClick={() => setShowExtensionModal(true)}
                    data-testid="new-extension-btn"
                  >
                    + Request Extension
                  </Button>
                )}
              </div>
              
              {extensionsLoading ? (
                <div className="text-center py-4 text-slate-500">Loading extensions...</div>
              ) : extensions.length === 0 ? (
                <div className="text-center py-4 text-slate-500 text-sm">
                  {isRenter 
                    ? 'No extensions requested. Click "+ Request Extension" to extend your booking.' 
                    : 'No extension requests for this booking.'}
                </div>
              ) : (
                <div className="space-y-3">
                  {extensions.map((ext) => (
                    <div 
                      key={ext.extension_id}
                      className={`p-3 bg-white rounded-lg border ${
                        ext.status === 'APPROVED_AWAITING_PAYMENT' 
                          ? 'border-blue-300 bg-blue-50/30' 
                          : 'border-slate-200'
                      }`}
                      data-testid={`extension-item-${ext.extension_id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs ${
                            ext.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                            ext.status === 'APPROVED_AWAITING_PAYMENT' ? 'bg-blue-100 text-blue-700' :
                            ext.status === 'APPROVED_AWAITING_BOND' ? 'bg-purple-100 text-purple-700' :
                            ext.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                            ext.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                            ext.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                            ext.status === 'PAYMENT_FAILED' ? 'bg-red-100 text-red-700' :
                            ext.status === 'CANCELLED' ? 'bg-slate-100 text-slate-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {ext.status === 'APPROVED_AWAITING_PAYMENT' ? 'Awaiting Payment' :
                             ext.status === 'APPROVED_AWAITING_BOND' ? 'Bond Required' :
                             ext.status === 'ACTIVE' ? 'Active' :
                             ext.status?.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            Requested: {new Date(ext.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <span className="font-semibold text-blue-600">
                          +{ext.pricing_breakdown?.extra_days} day{ext.pricing_breakdown?.extra_days > 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between text-slate-600">
                          <span>New Return Date:</span>
                          <span className="font-medium">{ext.requested_end_at}</span>
                        </div>
                        <div className="flex justify-between text-slate-600">
                          <span>Total Cost:</span>
                          <span className="font-medium">${ext.pricing_breakdown?.total?.toFixed(2)}</span>
                        </div>
                      </div>
                      
                      {/* P0 EXTENSION PAYMENT: Show payment button for approved extensions */}
                      {isRenter && ext.status === 'APPROVED_AWAITING_PAYMENT' && (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                            <div className="flex items-center gap-2 text-blue-800 mb-1">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-sm font-medium">Host Approved!</span>
                            </div>
                            <p className="text-xs text-blue-700">
                              Complete payment to activate your extension. Your booking dates will update immediately.
                            </p>
                          </div>
                          <Button
                            size="sm"
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handlePayExtension(ext.extension_id)}
                            disabled={extensionPaymentLoading}
                            data-testid={`pay-extension-${ext.extension_id}`}
                          >
                            {extensionPaymentLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <CreditCard className="w-4 h-4 mr-2" />
                                Pay ${ext.pricing_breakdown?.total?.toFixed(2)} to Extend
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                      
                      {/* Show decision info if rejected */}
                      {ext.status === 'REJECTED' && ext.decision_reason && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
                          Reason: {ext.decision_reason}
                        </div>
                      )}
                      
                      {ext.status === 'PAYMENT_FAILED' && ext.last_payment_error_message && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
                          Payment Error: {ext.last_payment_error_message}
                        </div>
                      )}
                      
                      {(ext.status === 'APPROVED' || ext.status === 'ACTIVE') && ext.payment_receipt_url && (
                        <div className="mt-2">
                          <a 
                            href={ext.payment_receipt_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            View Receipt →
                          </a>
                        </div>
                      )}
                      
                      {/* Cancel button for pending extensions (renter only) */}
                      {isRenter && ext.status === 'PENDING' && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => handleCancelExtension(ext.extension_id)}
                            data-testid={`cancel-extension-${ext.extension_id}`}
                          >
                            Cancel Request
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* P0 Claims Engine: Claims Section - Show for both host and renter */}
        {['posthire_submitted', 'cooldown_window_open', 'completed', 'disputed'].includes(currentStatus) && (claims.length > 0 || isHost) && (
          <Card className="border-red-200 bg-red-50/30" data-testid="claims-section">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h3 className="font-semibold text-sm">Claims & Issues</h3>
                  {claims.length > 0 && (
                    <Badge className="bg-red-100 text-red-700">{claims.length}</Badge>
                  )}
                </div>
                {isHost && ['posthire_submitted', 'cooldown_window_open'].includes(currentStatus) && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-red-300 text-red-600 hover:bg-red-100"
                    onClick={() => setShowClaimWizard(true)}
                    data-testid="new-claim-btn"
                  >
                    + New Claim
                  </Button>
                )}
              </div>
              
              {/* Review Period Explanation */}
              {['posthire_submitted', 'cooldown_window_open'].includes(currentStatus) && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-purple-800">Review Period Active</p>
                      <p className="text-xs text-purple-600 mt-1">
                        {isHost 
                          ? 'Claims or disputes may be submitted during this window. If you found damage or issues, submit a claim before the review period ends.'
                          : 'The host may submit claims during this period. If you disagree with any claims, you can dispute them.'}
                      </p>
                      {booking?.cooldown_ends_at && (
                        <p className="text-xs text-purple-700 mt-2 font-medium">
                          Ends: {new Date(booking.cooldown_ends_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Finalised Notice - ENHANCED WITH BOND RELEASE INFO */}
              {currentStatus === 'completed' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">Booking Finalised</p>
                      <p className="text-xs text-emerald-600 mt-1">
                        This booking has been completed successfully. No further claims or disputes can be submitted.
                      </p>
                      {isRenter && booking?.bond_mode && booking.bond_mode !== 'none' && (
                        <p className="text-xs text-emerald-700 mt-2 font-medium">
                          {booking.bond_status === 'released' 
                            ? 'Your security bond has been released.'
                            : 'Your security bond will be released if no claims were approved.'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* FAIRNESS MESSAGING - Show in claims section */}
              {['posthire_submitted', 'cooldown_window_open', 'disputed'].includes(currentStatus) && claims.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <Scale className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-slate-600">
                        HireHub aims to ensure fair outcomes for both hosts and renters. All claims are based on submitted evidence. Disputes may be reviewed where required.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {claimsLoading ? (
                <div className="text-center py-4 text-slate-500">Loading claims...</div>
              ) : claims.length === 0 ? (
                <div className="text-center py-4 text-slate-500 text-sm">
                  {isHost ? 'No claims filed yet. Click "New Claim" to report an issue.' : 'No claims against this booking.'}
                </div>
              ) : (
                <div className="space-y-3">
                  {claims.map((claim) => {
                    // Human-readable claim status labels
                    const claimStatusLabels = {
                      draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700' },
                      submitted: { label: 'Claim Submitted', className: 'bg-yellow-100 text-yellow-700' },
                      under_review: { label: 'Under Review', className: 'bg-blue-100 text-blue-700' },
                      approved: { label: 'Approved', className: 'bg-green-100 text-green-700' },
                      rejected: { label: 'Not Approved', className: 'bg-red-100 text-red-700' },
                      paid: { label: 'Paid', className: 'bg-emerald-100 text-emerald-700' },
                      disputed: { label: 'Dispute In Progress', className: 'bg-purple-100 text-purple-700' },
                      resolved: { label: 'Resolved', className: 'bg-slate-100 text-slate-700' },
                      cancelled: { label: 'Cancelled', className: 'bg-slate-100 text-slate-600' }
                    };
                    const statusConfig = claimStatusLabels[claim.status] || { label: claim.status, className: 'bg-slate-100 text-slate-700' };
                    
                    return (
                    <div 
                      key={claim.claim_id}
                      className="p-3 bg-white rounded-lg border border-slate-200 hover:border-red-200 hover:shadow-md hover:-translate-y-0.5 cursor-pointer transition-all duration-150"
                      onClick={() => setShowClaimDetail(claim)}
                      data-testid={`claim-item-${claim.claim_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {claim.claim_type?.replace('_', ' ').toUpperCase()}
                          </Badge>
                          <Badge className={`text-xs ${statusConfig.className}`}>
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <span className="font-semibold text-red-600">
                          ${(claim.amount_cents / 100).toFixed(2)}
                        </span>
                      </div>
                      {claim.description && (
                        <p className="text-xs text-slate-500 mt-2 truncate">{claim.description}</p>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Booking Details */}
        <Card className="border-slate-200">
          <CardContent className="p-4 sm:p-6">
            <h3 className="font-semibold mb-3 text-sm">Booking Information</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Dates</span>
                <span>{booking.start_date} - {booking.end_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Days</span>
                <span>{booking.total_days}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Daily Rate</span>
                <span>${booking.daily_rate?.toFixed(2)}</span>
              </div>
              
              {/* Financial breakdown - different for Host vs Renter */}
              <div className="border-t pt-2 mt-2 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Vehicle Subtotal</span>
                  <span>${booking.subtotal?.toFixed(2)}</span>
                </div>
                
                {isHost ? (
                  // HOST VIEW: Show host service fee and payout
                  // PART B FIX: in Host Direct mode HireHub does not deduct a
                  // service fee — the renter pays the host directly off-platform.
                  paymentMode === 'host_direct' ? (
                    <>
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>Payment Method</span>
                        <span>Direct payment between host & renter</span>
                      </div>
                      <div className="flex justify-between text-slate-500">
                        <span>Platform Fee</span>
                        <span className="text-slate-400">$0.00 — not charged in Host Direct mode</span>
                      </div>
                      <div className="flex justify-between font-semibold text-green-600 bg-green-50 -mx-2 px-2 py-1 rounded">
                        <span>Host Receives</span>
                        <span data-testid="host-payout-detail">
                          ${(booking.subtotal || 0)?.toFixed(2)}
                        </span>
                      </div>
                    </>
                  ) : (
                  <>
                    <div className="flex justify-between text-slate-500">
                      <span>Host Service Fee ({booking.host_fee_percent || 5}%)</span>
                      <span className="text-red-500">
                        -${(booking.host_fee_amount || booking.subtotal * 0.05)?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-green-600 bg-green-50 -mx-2 px-2 py-1 rounded">
                      <span>Your Earnings</span>
                      <span data-testid="host-payout-detail">
                        ${(booking.host_payout_cents 
                          ? (booking.host_payout_cents / 100) 
                          : (booking.owner_payout || (booking.subtotal - (booking.host_fee_amount || booking.subtotal * 0.05)))
                        )?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 pt-1">
                      <span>Guest Total Paid</span>
                      <span>${booking.total_amount?.toFixed(2)}</span>
                    </div>
                  </>
                  )
                ) : (
                  // RENTER VIEW: Show renter service fee, Stripe fee, and total
                  <>
                    <div className="flex justify-between text-slate-500">
                      <span>Service Fee ({booking.renter_fee_percent || 12}%)</span>
                      <span>
                        +${(booking.renter_fee_amount || booking.subtotal * 0.12)?.toFixed(2)}
                      </span>
                    </div>
                    {(booking.processing_fee || booking.stripe_fee) > 0 && (
                      <div className="flex justify-between text-slate-500">
                        <span>Processing Fee</span>
                        <span>+${(booking.processing_fee || booking.stripe_fee)?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t pt-2 mt-2">
                      <span>Total Paid</span>
                      <span data-testid="renter-total-detail">${booking.total_amount?.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
              
              {/* BOND v1: Show bond info if required */}
              {booking.bond_mode && booking.bond_mode !== 'none' && (
                <div className="flex justify-between text-amber-600 pt-2 border-t mt-2">
                  <span className="text-slate-500">Security Bond</span>
                  <span>
                    ${((booking.bond_required_cents || booking.bond_amount * 100 || 0) / 100).toFixed(2)}
                    <span className="text-xs ml-1">
                      ({booking.bond_mode === 'authorize' ? 'hold' : 'refundable'})
                    </span>
                  </span>
                </div>
              )}
            </div>
            
            {/* BOND v1: Bond Status Card - ENHANCED WITH TRUST MESSAGING */}
            {booking.bond_mode && booking.bond_mode !== 'none' && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-sm flex items-center gap-2">
                      <Shield className="w-4 h-4 text-amber-500" />
                      Security Bond
                    </h4>
                    <p className="text-xs text-slate-500 mt-1">
                      {booking.bond_mode === 'authorize' 
                        ? 'Temporary hold — not a charge'
                        : 'Refundable deposit'}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-amber-600">
                      ${((booking.bond_required_cents || booking.bond_amount * 100 || 0) / 100).toFixed(2)}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      booking.bond_status === 'authorized' ? 'bg-green-100 text-green-700' :
                      booking.bond_status === 'captured' ? 'bg-green-100 text-green-700' :
                      booking.bond_status === 'partially_captured' ? 'bg-orange-100 text-orange-700' :
                      booking.bond_status === 'released' ? 'bg-slate-100 text-slate-600' :
                      booking.bond_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      booking.bond_status === 'requires_action' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {booking.bond_status === 'authorized' ? 'Held' :
                       booking.bond_status === 'captured' ? 'Used' :
                       booking.bond_status === 'partially_captured' ? 'Partially Used' :
                       booking.bond_status === 'released' ? 'Released' :
                       booking.bond_status === 'pending' ? 'Required' :
                       booking.bond_status === 'requires_action' ? 'Action Required' :
                       booking.bond_status || 'Required'}
                    </span>
                  </div>
                </div>
                
                {/* TRUST MESSAGING: Bond Explanation for Renters */}
                {isRenter && (
                  <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-blue-700 space-y-1">
                        {booking.bond_status === 'authorized' || booking.bond_status === 'captured' ? (
                          <>
                            <p><strong>This is a temporary hold, not a charge.</strong></p>
                            <p>Released automatically after the trip if no claims are made. If a claim is submitted and approved, the bond may be used to cover costs.</p>
                            {['completed', 'posthire_submitted', 'cooldown_window_open'].includes(currentStatus) && (
                              <p className="text-blue-600">No action is required if the trip is completed without issues.</p>
                            )}
                          </>
                        ) : booking.bond_status === 'released' ? (
                          <p>Your bond has been released. No further action required.</p>
                        ) : booking.bond_status === 'pending' || booking.bond_status === 'requires_action' ? (
                          <>
                            <p><strong>This is a temporary hold, not a charge.</strong></p>
                            <p>The bond will be released automatically after the trip if no claims are made.</p>
                          </>
                        ) : (
                          <p>Bond status will update as your booking progresses.</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Pay Bond Button - only for renter when bond is pending or requires_action */}
                {paymentMode !== 'host_direct' && isRenter && ['pending', 'requires_action'].includes(booking.bond_status) && ['paid', 'prehire_in_progress', 'prehire_submitted'].includes(booking.status) && (
                  <Button
                    onClick={() => handleAction('pay_bond')}
                    disabled={bondLoading || actionLoading}
                    className="w-full mt-3 bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="pay-bond-btn"
                  >
                    {bondLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4 mr-2" />
                        {booking.bond_mode === 'authorize' ? 'Authorize Bond Hold' : 'Pay Security Bond'}
                      </>
                    )}
                  </Button>
                )}
                
                {/* Bond satisfied message */}
                {(booking.bond_status === 'authorized' || booking.bond_status === 'captured') && (
                  <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Bond {booking.bond_status === 'authorized' ? 'held' : 'paid'} successfully
                  </div>
                )}

                {/* PHASE B4: Bond recorded manually (host_direct mode) */}
                {booking.bond_status === 'recorded_manual' && (
                  <div
                    className="mt-3 p-2 rounded-md border border-emerald-200 bg-emerald-50 text-xs text-emerald-800 flex items-center gap-2"
                    data-testid="bond-recorded-manual-status"
                  >
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>
                      Bond recorded manually
                      {typeof booking.bond_recorded_amount_cents === 'number' &&
                        ` — $${(booking.bond_recorded_amount_cents / 100).toFixed(2)}`}
                      {booking.bond_recorded_method && ` via ${booking.bond_recorded_method}`}
                    </span>
                  </div>
                )}

                {/* PHASE B4: "Record Bond" button — host only, host_direct mode, bond required & not yet recorded */}
                {paymentMode === 'host_direct'
                  && isHost
                  && booking.bond_mode && booking.bond_mode !== 'none'
                  && booking.bond_status !== 'recorded_manual'
                  && ['paid', 'prehire_in_progress', 'prehire_submitted'].includes(booking.status)
                  && (
                    <Button
                      onClick={openRecordBondModal}
                      disabled={recordBondLoading || actionLoading}
                      className="w-full mt-3 bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="record-bond-btn"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Record Bond
                    </Button>
                  )
                }
                
                {/* Bond required warning - show to host too (Phase A only) */}
                {paymentMode !== 'host_direct' && isHost && ['pending', 'requires_action'].includes(booking.bond_status) && (
                  <div className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Waiting for renter to complete bond payment before pre-hire can start
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* P2: FINANCIAL TIMELINE (Platform Owner / Admin Only) */}
        {(isPlatformOwner || isAdmin() || isSuperAdmin()) && booking && (
          <FinancialTimelineSection bookingId={bookingId} api={api} />
        )}

        {/* Lifecycle Debug (DEV only or Platform Owner) */}
        {lifecycle && (process.env.NODE_ENV === 'development' || isPlatformOwner) && (
          <Card className="border-slate-200 bg-slate-100">
            <CardContent className="p-4">
              <h3 className="font-mono text-xs mb-2 flex items-center gap-2">
                <span className="bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded text-[10px] font-semibold">DEV</span>
                Lifecycle Debug
              </h3>
              <pre className="text-xs overflow-auto max-h-40">
                {JSON.stringify(lifecycle, null, 2)}
              </pre>
            </CardContent>
          </Card>
        )}
      </div>

      {/* DECLINE MODAL */}
      <Dialog open={showDeclineModal} onOpenChange={setShowDeclineModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Booking</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium mb-2">Reason (optional)</label>
            <Textarea
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              placeholder="Let the renter know why..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeclineModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleAction('decline')}
              disabled={actionLoading}
            >
              {actionLoading ? 'Declining...' : 'Decline Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PHASE B: Mark Payment Received confirmation modal (host-direct mode) */}
      <Dialog open={showMarkPaidModal} onOpenChange={setShowMarkPaidModal}>
        <DialogContent data-testid="mark-payment-received-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle className="w-5 h-5" />
              Confirm payment received?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3 text-sm text-slate-700">
            <p>
              You're confirming that the renter has paid you directly (off-platform).
              Once confirmed, the booking status becomes <strong>Paid</strong> and the
              pre-hire checklist unlocks. This action is logged for audit purposes.
            </p>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Payment method (optional note)
              </label>
              <input
                type="text"
                value={markPaidMethod}
                onChange={e => setMarkPaidMethod(e.target.value)}
                placeholder="e.g. PayID, bank transfer, cash"
                maxLength={200}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="mark-payment-received-method-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowMarkPaidModal(false)}
              disabled={markPaidLoading}
              data-testid="mark-payment-received-cancel-btn"
            >
              Not yet
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleConfirmMarkPaymentReceived}
              disabled={markPaidLoading}
              data-testid="mark-payment-received-confirm-btn"
            >
              {markPaidLoading ? 'Confirming…' : 'Yes, mark as paid'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PHASE B4: Record Bond modal (host-direct mode) */}
      <Dialog open={showRecordBondModal} onOpenChange={setShowRecordBondModal}>
        <DialogContent data-testid="record-bond-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Shield className="w-5 h-5" />
              Record Bond
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4 text-sm text-slate-700">
            <p>
              Confirm you've collected a security bond from the renter. The amount below
              is prefilled from this booking's configured bond — adjust only if you
              collected a different amount.
            </p>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Bond amount (AUD)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={recordBondAmount}
                onChange={e => setRecordBondAmount(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="record-bond-amount-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2">
                Collection method
              </label>
              <div className="grid grid-cols-2 gap-2" data-testid="record-bond-method-group">
                {[
                  { value: 'payid', label: 'PayID' },
                  { value: 'bank', label: 'Bank transfer' },
                  { value: 'cash', label: 'Cash' },
                  { value: 'other', label: 'Other' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setRecordBondMethod(opt.value)}
                    className={`border rounded-md px-3 py-2 text-sm transition-colors ${
                      recordBondMethod === opt.value
                        ? 'border-amber-500 bg-amber-50 text-amber-900 font-medium'
                        : 'border-slate-300 bg-white text-slate-700 hover:border-amber-300'
                    }`}
                    data-testid={`record-bond-method-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRecordBondModal(false)}
              disabled={recordBondLoading}
              data-testid="record-bond-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleConfirmRecordBond}
              disabled={recordBondLoading}
              data-testid="record-bond-confirm-btn"
            >
              {recordBondLoading ? 'Recording…' : 'Record Bond'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CANCEL BOOKING CONFIRMATION MODAL */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Cancel Booking?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              This action cannot be undone. Any payments will be refunded according to the cancellation policy.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowCancelModal(false)}
              disabled={cancelLoading}
              data-testid="cancel-modal-keep-btn"
            >
              Keep Booking
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmCancel}
              disabled={cancelLoading}
              data-testid="cancel-modal-confirm-btn"
            >
              {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* P2 FIX: Host Cancel Unpaid Approved Booking Modal */}
      <Dialog open={showCancelUnpaidModal} onOpenChange={setShowCancelUnpaidModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Release Unpaid Hold?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                This renter has <strong>not paid yet</strong>. Releasing this hold will:
              </p>
              <ul className="text-sm text-amber-700 mt-2 list-disc list-inside space-y-1">
                <li>Cancel the approved booking</li>
                <li>Release the held dates immediately</li>
                <li>Allow other renters to book these dates</li>
                <li>Notify the renter that the hold was cancelled</li>
              </ul>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Reason (optional)</label>
              <Textarea
                value={cancelUnpaidReason}
                onChange={(e) => setCancelUnpaidReason(e.target.value)}
                placeholder="e.g., Needed to accommodate another booking..."
                rows={2}
                data-testid="cancel-unpaid-reason"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCancelUnpaidModal(false);
                setCancelUnpaidReason('');
              }}
              disabled={cancelUnpaidLoading}
            >
              Keep Hold
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleConfirmCancelUnpaid}
              disabled={cancelUnpaidLoading}
              data-testid="confirm-cancel-unpaid-btn"
            >
              {cancelUnpaidLoading ? 'Releasing...' : 'Yes, Release Dates'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PRE-HIRE WIZARD MODAL */}
      {showPrehireWizard && usesNonDefaultTemplate(vehicle?.vehicle_category) ? (
        <SpecialtyInspectionWizard
          booking={{ ...booking, vehicle }}
          phase={INSPECTION_PHASES.PREHIRE}
          token={token}
          onClose={() => setShowPrehireWizard(false)}
          onSubmitted={() => {
            setShowPrehireWizard(false);
            refreshAll();
          }}
        />
      ) : (
      <Dialog open={showPrehireWizard} onOpenChange={(open) => {
        // P0 FIX: If closing (open=false), trigger onCancel which saves draft
        if (!open && prehireWizardRef.current?.saveDraftBeforeClose) {
          prehireWizardRef.current.saveDraftBeforeClose();
        }
        setShowPrehireWizard(open);
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pre-Hire Checklist</DialogTitle>
          </DialogHeader>
          <PreHireChecklistWizard
            ref={prehireWizardRef}
            booking={booking}
            onComplete={() => {
              setShowPrehireWizard(false);
              refreshAll();
            }}
            onCancel={() => setShowPrehireWizard(false)}
          />
        </DialogContent>
      </Dialog>
      )}

      {/* POST-HIRE WIZARD MODAL - P0: Canonical with Pre-Hire (same DialogContent class) */}
      {showPosthireWizard && usesNonDefaultTemplate(vehicle?.vehicle_category) ? (
        <SpecialtyInspectionWizard
          booking={{ ...booking, vehicle }}
          phase={INSPECTION_PHASES.POSTHIRE}
          token={token}
          onClose={() => setShowPosthireWizard(false)}
          onSubmitted={() => {
            setShowPosthireWizard(false);
            refreshAll();
          }}
        />
      ) : (
      <Dialog open={showPosthireWizard} onOpenChange={(open) => {
        // P0 FIX: If closing (open=false), trigger save draft
        if (!open && posthireWizardRef.current?.saveDraftBeforeClose) {
          posthireWizardRef.current.saveDraftBeforeClose();
        }
        setShowPosthireWizard(open);
      }}>
        {/* P0 FIX: Mobile-optimized DialogContent - matches Pre-Hire exactly */}
        <DialogContent className="max-w-lg w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Return Checklist</DialogTitle>
          </DialogHeader>
          <PostHireChecklistWizard
            ref={posthireWizardRef}
            booking={booking}
            prehireData={prehireData}
            onComplete={() => {
              setShowPosthireWizard(false);
              refreshAll();
            }}
            onCancel={() => setShowPosthireWizard(false)}
          />
        </DialogContent>
      </Dialog>
      )}

      {/* Renter Trip Start Confirmation modal — gates the start_trip action
          with signed declarations + signature_name + device_info. */}
      <TripConfirmationModal
        isOpen={showTripConfirmModal}
        onClose={() => !tripConfirmLoading && setShowTripConfirmModal(false)}
        onConfirm={handleConfirmStartTrip}
        booking={booking}
        userRole={isRenter ? 'renter' : 'host'}
        loading={tripConfirmLoading}
      />

      {/* REVIEW MODAL */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave a Review</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Rating</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    onClick={() => setReviewRating(star)}
                    className="focus:outline-none"
                  >
                    <Star 
                      className={`w-8 h-8 ${star <= reviewRating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Comment</label>
              <Textarea
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                placeholder="Share your experience..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitReview}
              disabled={actionLoading}
              className="bg-yellow-500 hover:bg-yellow-600"
            >
              {actionLoading ? 'Submitting...' : 'Submit Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PRE-HIRE CHECKLIST VIEWER MODAL - P0 UI Polish: Read-only view */}
      <Dialog open={showPrehireViewer} onOpenChange={setShowPrehireViewer}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-blue-600" />
              Pre-Hire Checklist
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-1">
            {/* Status Badge */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm text-slate-500">Status:</span>
              <Badge className={
                lifecycle?.pre_hire?.status === 'submitted' ? 'bg-green-100 text-green-700' :
                lifecycle?.pre_hire?.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                prehireData ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
              }>
                {lifecycle?.pre_hire?.status === 'submitted' ? 'Complete' :
                 lifecycle?.pre_hire?.status === 'in_progress' ? 'In Progress' :
                 prehireData ? 'Draft' : 'Not Started'}
              </Badge>
              {lifecycle?.pre_hire?.submitted_at && (
                <span className="text-xs text-slate-400">
                  {formatTimestamp(lifecycle.pre_hire.submitted_at)}
                </span>
              )}
            </div>

            {prehireData || checklistPhotos.prehire?.length > 0 ? (
              <>
                {/* Summary Section */}
                {prehireData && (
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-sm mb-2">Summary</h4>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Odometer:</span>
                        <span>{prehireData.odometer_value || '-'} km</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Fuel Level:</span>
                        <span>{prehireData.fuel_level || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Keys:</span>
                        <span>{prehireData.keys_count || '-'}</span>
                      </div>
                      {prehireData.prehire_meta?.licence_class && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Licence Class:</span>
                          <span>{prehireData.prehire_meta.licence_class}</span>
                        </div>
                      )}
                      {prehireData.prehire_meta?.fuel_type && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Fuel Type:</span>
                          <span>{prehireData.prehire_meta.fuel_type}</span>
                        </div>
                      )}
                      {prehireData.prehire_meta?.fuel_policy && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Fuel Policy:</span>
                          <span className="truncate max-w-[150px]">{prehireData.prehire_meta.fuel_policy}</span>
                        </div>
                      )}
                      {prehireData.notes && (
                        <div className="pt-2 border-t border-slate-200 mt-2">
                          <span className="text-slate-600">Notes:</span>
                          <p className="text-slate-700 mt-1">{prehireData.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Photos Grid */}
                {checklistPhotos.prehire?.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm">Photos ({checklistPhotos.prehire.length})</h4>
                    {/* Group photos by type */}
                    {['exterior', 'interior', 'odometer', 'fuel', 'licence_front', 'licence_back', 'licence_selfie', 'damage'].map(type => {
                      const typePhotos = checklistPhotos.prehire.filter(p => p.photo_type === type);
                      if (typePhotos.length === 0) return null;
                      return (
                        <div key={type}>
                          <p className="text-xs text-slate-500 mb-1 capitalize">{type.replace('_', ' ')} ({typePhotos.length})</p>
                          <div className="grid grid-cols-4 gap-2">
                            {typePhotos.map((photo, idx) => (
                              <div key={photo.photo_id || idx} className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
                                {photo.thumbnail_url ? (
                                  <img 
                                    src={photo.thumbnail_url} 
                                    alt={photo.photo_type}
                                    className="w-full h-full object-cover cursor-pointer hover:opacity-80"
                                    onClick={() => photo.url && window.open(photo.url, '_blank')}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Image className="w-4 h-4 text-slate-300" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <FileCheck className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-sm text-slate-500">No checklist data yet</p>
                <p className="text-xs text-slate-400 mt-1">The pre-hire checklist has not been started.</p>
              </div>
            )}
          </div>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setShowPrehireViewer(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* POST-HIRE CHECKLIST VIEWER MODAL - P0 UI Polish: Read-only view */}
      <Dialog open={showPosthireViewer} onOpenChange={setShowPosthireViewer}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-orange-600" />
              Post-Hire Checklist
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-1">
            {/* Status Badge */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm text-slate-500">Status:</span>
              <Badge className={
                lifecycle?.post_hire?.status === 'submitted' ? 'bg-green-100 text-green-700' :
                lifecycle?.post_hire?.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                posthireData ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
              }>
                {lifecycle?.post_hire?.status === 'submitted' ? 'Complete' :
                 lifecycle?.post_hire?.status === 'in_progress' ? 'In Progress' :
                 posthireData ? 'Draft' : 'Not Started'}
              </Badge>
              {lifecycle?.post_hire?.submitted_at && (
                <span className="text-xs text-slate-400">
                  {formatTimestamp(lifecycle.post_hire.submitted_at)}
                </span>
              )}
            </div>

            {posthireData || checklistPhotos.posthire?.length > 0 ? (
              <>
                {/* Summary Section */}
                {posthireData && (
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-sm mb-2">Return Summary</h4>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Return Odometer:</span>
                        <span>{posthireData.return_odometer_value || '-'} km</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Return Fuel Level:</span>
                        <span>{posthireData.return_fuel_level || '-'}</span>
                      </div>
                      {posthireData.cleaning_required && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Cleaning Required:</span>
                          <span className="text-amber-600">Yes</span>
                        </div>
                      )}
                      {posthireData.notes && (
                        <div className="pt-2 border-t border-slate-200 mt-2">
                          <span className="text-slate-600">Notes:</span>
                          <p className="text-slate-700 mt-1">{posthireData.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Photos Grid */}
                {checklistPhotos.posthire?.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm">Photos ({checklistPhotos.posthire.length})</h4>
                    {['exterior', 'interior', 'odometer', 'fuel', 'damage'].map(type => {
                      const typePhotos = checklistPhotos.posthire.filter(p => p.photo_type === type || p.photo_type === `return_${type}`);
                      if (typePhotos.length === 0) return null;
                      return (
                        <div key={type}>
                          <p className="text-xs text-slate-500 mb-1 capitalize">{type} ({typePhotos.length})</p>
                          <div className="grid grid-cols-4 gap-2">
                            {typePhotos.map((photo, idx) => (
                              <div key={photo.photo_id || idx} className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
                                {photo.thumbnail_url ? (
                                  <img 
                                    src={photo.thumbnail_url} 
                                    alt={photo.photo_type}
                                    className="w-full h-full object-cover cursor-pointer hover:opacity-80"
                                    onClick={() => photo.url && window.open(photo.url, '_blank')}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Image className="w-4 h-4 text-slate-300" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <FileCheck className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-sm text-slate-500">No checklist data yet</p>
                <p className="text-xs text-slate-400 mt-1">The post-hire checklist has not been started.</p>
              </div>
            )}
          </div>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setShowPosthireViewer(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0/P1: DISPUTE EXTRA CHARGES MODAL */}
      <Dialog open={showDisputeModal} onOpenChange={(open) => {
        if (!open) {
          setDisputeViewMode(false);
          setExistingDispute(null);
          setDisputeEvidence([]);
        }
        setShowDisputeModal(open);
      }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              {disputeViewMode ? 'Dispute Details' : 'Dispute Extra Charges'}
            </DialogTitle>
          </DialogHeader>
          
          {disputeViewMode && existingDispute ? (
            /* VIEW MODE - Read-only dispute details */
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Status</div>
                <div className={`text-sm font-medium ${
                  existingDispute.status === 'open' ? 'text-amber-600' :
                  existingDispute.status === 'resolved' ? 'text-green-600' : 'text-slate-700'
                }`}>
                  {existingDispute.status?.toUpperCase()}
                </div>
              </div>
              
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Category</div>
                <div className="text-sm font-medium text-slate-700">
                  {existingDispute.category || existingDispute.reason_category || 'Other'}
                </div>
              </div>
              
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Reason</div>
                <div className="text-sm text-slate-700">{existingDispute.reason}</div>
              </div>
              
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Filed On</div>
                <div className="text-sm text-slate-700">
                  {existingDispute.created_at ? new Date(existingDispute.created_at).toLocaleString() : 'N/A'}
                </div>
              </div>
              
              {existingDispute.evidence_urls?.length > 0 && (
                <div>
                  <div className="text-xs text-slate-500 mb-2">Evidence Photos</div>
                  <div className="grid grid-cols-3 gap-2">
                    {existingDispute.evidence_urls.map((evidence, idx) => (
                      <img 
                        key={idx} 
                        src={evidence.url} 
                        alt={`Evidence ${idx + 1}`}
                        className="w-full h-20 object-cover rounded border"
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {existingDispute.resolution && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="text-xs text-green-600 mb-1">Resolution</div>
                  <div className="text-sm font-medium text-green-700">{existingDispute.resolution}</div>
                  {existingDispute.resolution_notes && (
                    <div className="text-xs text-green-600 mt-1">{existingDispute.resolution_notes}</div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* CREATE MODE - Submit new dispute */
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                If you believe the extra charges are incorrect, you can file a dispute. 
                The host and platform admin will review your case.
              </p>
              
              {posthireData?.total_extra_charges > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="text-sm font-medium text-amber-800">
                    Charges being disputed: ${posthireData.total_extra_charges?.toFixed(2)}
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-2">Dispute Category *</label>
                <select
                  value={disputeCategory}
                  onChange={e => setDisputeCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  data-testid="dispute-category-select"
                >
                  <option value="fuel">Fuel</option>
                  <option value="tolls">Tolls</option>
                  <option value="cleaning">Cleaning</option>
                  <option value="damage">Damage</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Reason for Dispute * (min 10 characters)</label>
                <Textarea
                  value={disputeReason}
                  onChange={e => setDisputeReason(e.target.value)}
                  rows={4}
                  placeholder="Please explain why you are disputing these charges..."
                  className="w-full"
                  data-testid="dispute-reason-textarea"
                />
                <div className="text-xs text-slate-500 mt-1">
                  {disputeReason.length}/10 characters minimum
                </div>
              </div>
              
              {/* Evidence Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">Evidence (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleDisputeEvidenceUpload}
                  className="hidden"
                  id="dispute-evidence-input"
                  data-testid="dispute-evidence-input"
                />
                <label 
                  htmlFor="dispute-evidence-input"
                  className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-amber-500 transition-colors"
                >
                  <Camera className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-600">Add Photos</span>
                </label>
                
                {disputeEvidence.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {disputeEvidence.map((evidence) => (
                      <div key={evidence.id} className="relative">
                        <img 
                          src={evidence.preview || evidence.url} 
                          alt="Evidence"
                          className={`w-full h-20 object-cover rounded border ${evidence.uploading ? 'opacity-50' : ''}`}
                        />
                        {evidence.uploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                        {evidence.error && (
                          <div className="absolute inset-0 flex items-center justify-center bg-red-500/30 rounded">
                            <X className="w-4 h-4 text-red-600" />
                          </div>
                        )}
                        {!evidence.uploading && (
                          <button
                            onClick={() => removeDisputeEvidence(evidence.id)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Confirmation Checkbox */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="dispute-confirm"
                  checked={disputeConfirmed}
                  onChange={e => setDisputeConfirmed(e.target.checked)}
                  className="mt-1"
                  data-testid="dispute-confirm-checkbox"
                />
                <label htmlFor="dispute-confirm" className="text-sm text-slate-600">
                  I confirm this information is true and accurate
                </label>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDisputeModal(false);
                setDisputeViewMode(false);
                setExistingDispute(null);
              }}
              disabled={disputeLoading}
            >
              {disputeViewMode ? 'Close' : 'Cancel'}
            </Button>
            {!disputeViewMode && (
              <Button 
                onClick={handleSubmitDispute}
                disabled={disputeLoading || disputeReason.trim().length < 10 || !disputeConfirmed}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="submit-dispute-btn"
              >
                {disputeLoading ? 'Submitting...' : 'File Dispute'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0 LEGAL RECORD: Evidence Pack Modal */}
      <Dialog open={showEvidencePackModal.open} onOpenChange={(open) => setShowEvidencePackModal({ ...showEvidencePackModal, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#004B8D]" />
              {showEvidencePackModal.type === 'prehire' ? 'Pre-Hire' : 'Post-Hire'} Evidence Pack
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {evidencePackStatus[showEvidencePackModal.type]?.available ? (
              <>
                <p className="text-sm text-slate-600">
                  Evidence pack generated on {evidencePackStatus[showEvidencePackModal.type]?.artifacts?.generated_at ? 
                    format(parseISO(evidencePackStatus[showEvidencePackModal.type].artifacts.generated_at), 'dd MMM yyyy, HH:mm') : 'N/A'}
                </p>
                
                <div className="space-y-2">
                  {/* PDF Summary */}
                  {evidencePackStatus[showEvidencePackModal.type]?.artifacts?.pdf_key && (
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2"
                      onClick={() => downloadEvidencePackArtifact(showEvidencePackModal.type, 'pdf')}
                      disabled={evidencePackLoading}
                      data-testid="download-pdf-btn"
                    >
                      <FileText className="w-4 h-4 text-red-500" />
                      <span>Download PDF Summary</span>
                      <Download className="w-4 h-4 ml-auto" />
                    </Button>
                  )}
                  
                  {/* ZIP Photos - Always show, disabled if not available */}
                  <div className="relative group">
                    <Button
                      variant="outline"
                      className={`w-full justify-start gap-2 ${!evidencePackStatus[showEvidencePackModal.type]?.artifacts?.zip_key ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => downloadEvidencePackArtifact(showEvidencePackModal.type, 'zip')}
                      disabled={evidencePackLoading || !evidencePackStatus[showEvidencePackModal.type]?.artifacts?.zip_key}
                      data-testid="download-zip-btn"
                    >
                      <FileArchive className="w-4 h-4 text-amber-500" />
                      <span>Download Photos (ZIP)</span>
                      {evidencePackStatus[showEvidencePackModal.type]?.artifacts?.zip_key ? (
                        <Download className="w-4 h-4 ml-auto" />
                      ) : (
                        <span className="ml-auto text-xs text-slate-400">Not available</span>
                      )}
                    </Button>
                    {!evidencePackStatus[showEvidencePackModal.type]?.artifacts?.zip_key && (
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                        No photos were uploaded to this checklist
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* JSON Snapshot */}
                  {evidencePackStatus[showEvidencePackModal.type]?.artifacts?.json_key && (
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2"
                      onClick={() => downloadEvidencePackArtifact(showEvidencePackModal.type, 'json')}
                      disabled={evidencePackLoading}
                      data-testid="download-json-btn"
                    >
                      <FileJson className="w-4 h-4 text-blue-500" />
                      <span>Download JSON Snapshot</span>
                      <Download className="w-4 h-4 ml-auto" />
                    </Button>
                  )}
                </div>
              </>
            ) : evidencePackStatus[showEvidencePackModal.type]?.status === 'not_generated' ? (
              <div className="text-center py-4">
                <p className="text-sm text-slate-600 mb-4">
                  Evidence pack has not been generated yet. Generate it now to create a legal record of this checklist.
                </p>
                <Button
                  onClick={() => generateEvidencePack(showEvidencePackModal.type)}
                  disabled={evidencePackLoading}
                  className="bg-[#004B8D]"
                  data-testid="generate-evidence-pack-btn"
                >
                  {evidencePackLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Package className="w-4 h-4 mr-2" />
                      Generate Evidence Pack
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center py-4">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#004B8D]" />
                <p className="text-sm text-slate-600 mt-2">Loading...</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEvidencePackModal({ open: false, type: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0 Claims Engine: Claim Wizard Modal */}
      <Dialog open={showClaimWizard} onOpenChange={setShowClaimWizard}>
        <DialogContent className="max-w-lg" aria-describedby="claim-wizard-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Submit a Claim
            </DialogTitle>
            <p id="claim-wizard-description" className="text-sm text-slate-500">
              Report post-rental issues such as fuel shortages, tolls, cleaning, or damage.
            </p>
          </DialogHeader>
          <ClaimWizard 
            bookingId={bookingId}
            api={api}
            reviewPeriodEnds={booking?.cooldown_ends_at}
            onSuccess={() => {
              setShowClaimWizard(false);
              fetchClaims();
            }}
            onCancel={() => setShowClaimWizard(false)}
          />
        </DialogContent>
      </Dialog>

      {/* P0 Claims Engine: Claim Detail Modal */}
      <Dialog open={!!showClaimDetail} onOpenChange={() => setShowClaimDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Claim Details</DialogTitle>
          </DialogHeader>
          {showClaimDetail && (() => {
            // Human-readable claim status for modal
            const claimStatusLabels = {
              draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700', description: 'Claim is being prepared.' },
              submitted: { label: 'Claim Submitted', className: 'bg-yellow-100 text-yellow-700', description: 'Awaiting renter response.' },
              under_review: { label: 'Under Review', className: 'bg-blue-100 text-blue-700', description: 'Our team is reviewing this claim.' },
              approved: { label: 'Approved', className: 'bg-green-100 text-green-700', description: 'Claim has been approved.' },
              rejected: { label: 'Not Approved', className: 'bg-red-100 text-red-700', description: 'Claim was not approved.' },
              paid: { label: 'Paid', className: 'bg-emerald-100 text-emerald-700', description: 'Payment has been processed.' },
              disputed: { label: 'Dispute In Progress', className: 'bg-purple-100 text-purple-700', description: 'The renter has disputed this claim.' },
              resolved: { label: 'Resolved', className: 'bg-slate-100 text-slate-700', description: 'This claim has been resolved.' },
              cancelled: { label: 'Cancelled', className: 'bg-slate-100 text-slate-600', description: 'This claim was cancelled.' }
            };
            const statusConfig = claimStatusLabels[showClaimDetail.status] || { label: showClaimDetail.status, className: 'bg-slate-100 text-slate-700', description: '' };
            
            return (
            <div className="space-y-4">
              {/* Renter explanation banner */}
              {isRenter && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-blue-700">
                        This claim is based on trip evidence submitted by the host. Review the details below.
                      </p>
                      {['submitted', 'under_review'].includes(showClaimDetail.status) && (
                        <p className="text-xs text-blue-600 mt-1 font-medium">
                          You will not be charged without a confirmed outcome.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-500 uppercase">Type</div>
                  <Badge variant="outline" className="capitalize">{showClaimDetail.claim_type?.replace('_', ' ')}</Badge>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase">Amount</div>
                  <div className="text-lg font-bold text-red-600">${(showClaimDetail.amount_cents / 100).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase">Status</div>
                  <Badge className={statusConfig.className}>
                    {statusConfig.label}
                  </Badge>
                  <p className="text-xs text-slate-500 mt-1">{statusConfig.description}</p>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase">Created</div>
                  <div className="text-sm">{new Date(showClaimDetail.created_at).toLocaleDateString()}</div>
                </div>
              </div>

              <div>
                <div className="text-xs text-slate-500 uppercase mb-1">Description</div>
                <div className="text-sm bg-slate-50 p-3 rounded">{showClaimDetail.description || 'No description'}</div>
              </div>

              {/* Renter can dispute during cooldown */}
              {isRenter && ['cooldown_window_open', 'posthire_submitted'].includes(booking?.status) && 
               ['submitted', 'under_review'].includes(showClaimDetail.status) && (
                <div className="border-t pt-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Disputing a Claim</p>
                        <p className="text-xs text-amber-700 mt-1">
                          If you disagree with this claim, you can dispute it. Our team will review the evidence from both parties.
                        </p>
                      </div>
                    </div>
                  </div>
                  <h4 className="text-sm font-medium mb-2">Your Dispute Reason</h4>
                  <Textarea
                    value={disputeReasonInput}
                    onChange={(e) => setDisputeReasonInput(e.target.value)}
                    placeholder="Explain why you believe this claim is incorrect..."
                    className="mb-2"
                    data-testid="dispute-reason-input"
                  />
                  <Button
                    className="w-full bg-red-600 hover:bg-red-700"
                    onClick={() => handleDisputeClaim(showClaimDetail.claim_id)}
                    disabled={claimDisputing || !disputeReasonInput.trim()}
                    data-testid="dispute-claim-btn"
                  >
                    {claimDisputing ? 'Submitting Dispute...' : 'Dispute This Claim'}
                  </Button>
                </div>
              )}

              {/* Show dispute info if disputed */}
              {(showClaimDetail.disputed || showClaimDetail.status === 'disputed') && (
                <div className="bg-purple-50 p-3 rounded border border-purple-200">
                  <div className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-purple-800">Dispute In Progress</div>
                      <p className="text-xs text-purple-600 mt-1">Our team is reviewing the evidence from both parties.</p>
                      {showClaimDetail.disputed_reason && (
                        <div className="text-sm text-purple-700 mt-2 bg-purple-100 p-2 rounded">
                          "{showClaimDetail.disputed_reason}"
                        </div>
                      )}
                      <div className="text-xs text-slate-500 mt-2">
                        {showClaimDetail.disputed_at && `Disputed on ${new Date(showClaimDetail.disputed_at).toLocaleDateString()}`}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClaimDetail(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0 EXTENSION WORKFLOW: Request Extension Modal - Complete Form */}
      <Dialog open={showExtensionModal} onOpenChange={(open) => {
        if (!open) {
          setExtensionDate('');
          setExtensionPricing(null);
          setExtensionConfirmed(false);
          setExtensionOdometer('');
          setExtensionOdometerPhoto(null);
          setExtensionOdometerPhotoUrl('');
          setExtensionLicenceValid(false);
        }
        setShowExtensionModal(open);
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-blue-600" />
              Request Booking Extension
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-1" style={{ maxHeight: 'calc(90vh - 160px)' }}>
            <p className="text-sm text-slate-600">
              Need more time? Complete the form below to request an extension.
            </p>
            
            {/* Current End Date */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-xs text-slate-500 mb-1">Current Return Date</div>
              <div className="text-sm font-medium text-slate-700">
                {booking?.end_date ? format(parseISO(booking.end_date), 'EEEE, dd MMMM yyyy') : 'N/A'}
              </div>
            </div>
            
            {/* Check for pending extension */}
            {extensions.some(ext => ['PENDING', 'APPROVED_AWAITING_PAYMENT', 'APPROVED_AWAITING_BOND'].includes(ext.status)) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-amber-800">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {extensions.find(ext => ext.status === 'APPROVED_AWAITING_PAYMENT') 
                      ? 'Extension Approved – Payment Required'
                      : 'Extension Request Pending'}
                  </span>
                </div>
                <p className="text-xs text-amber-700 mt-1">
                  {extensions.find(ext => ext.status === 'APPROVED_AWAITING_PAYMENT')
                    ? 'Please complete payment to activate your extension.'
                    : 'Awaiting host approval. Cancel to submit a new request.'}
                </p>
              </div>
            )}
            
            {/* P0 FIX: Availability Loading State */}
            {extensionAvailabilityLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin mr-2" />
                <span className="text-sm text-slate-600">Loading available dates...</span>
              </div>
            )}
            
            {/* P0 FIX: No Available Dates Message */}
            {!extensionAvailabilityLoading && extensionNoAvailableDates && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">No extension dates available</p>
                    <p className="text-xs text-red-600 mt-1">
                      This vehicle has no available dates for extension in the next 90 days. 
                      Please contact the host directly if you need more time.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* P0 FIX: New End Date Picker with Calendar and Availability */}
            {!extensionAvailabilityLoading && !extensionNoAvailableDates && (
              <div>
                <label className="block text-sm font-medium mb-2">New Return Date *</label>
                <p className="text-xs text-slate-500 mb-3">
                  Select an available date. Greyed-out dates are unavailable.
                </p>
                
                {/* Calendar Component */}
                <div className="border border-slate-200 rounded-xl p-2 bg-white">
                  <Calendar
                    mode="single"
                    selected={extensionDate ? parseISO(extensionDate) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        setExtensionDate(format(date, 'yyyy-MM-dd'));
                      } else {
                        setExtensionDate('');
                      }
                    }}
                    disabled={(date) => {
                      // Disable past dates and current booking dates
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      if (date < today) return true;
                      
                      // Disable dates on or before current end date
                      if (booking?.end_date) {
                        const currentEnd = new Date(booking.end_date);
                        currentEnd.setHours(0, 0, 0, 0);
                        if (date <= currentEnd) return true;
                      }
                      
                      // Disable blocked dates
                      return isExtensionDateBlocked(date);
                    }}
                    className="rounded-xl w-full"
                    numberOfMonths={1}
                    defaultMonth={booking?.end_date ? new Date(new Date(booking.end_date).getTime() + 86400000) : new Date()}
                  />
                </div>
                
                {/* Selected Date Display */}
                {extensionDate && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-blue-600">Selected New Return Date</div>
                        <div className="text-sm font-semibold text-blue-800">
                          {format(parseISO(extensionDate), 'EEEE, dd MMMM yyyy')}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExtensionDate('')}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Change
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Pricing Preview */}
            {extensionPricing && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-3">Extension Cost</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-600">
                    <span>{extensionPricing.extra_days} extra day{extensionPricing.extra_days > 1 ? 's' : ''}</span>
                    <span>@ ${extensionPricing.rate_per_day?.toFixed(2)}/day</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span>${extensionPricing.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Service Fee (12%)</span>
                    <span>${extensionPricing.renter_fee?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Processing Fee</span>
                    <span>${extensionPricing.processing_fee?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-blue-800 pt-2 border-t border-blue-200">
                    <span>Total to Pay</span>
                    <span>${extensionPricing.total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 pt-1">
                    <span>Host receives</span>
                    <span>${extensionPricing.owner_payout?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Odometer Reading */}
            {extensionPricing && (
              <div>
                <label className="block text-sm font-medium mb-2">Current Odometer Reading (km) *</label>
                <Input
                  type="number"
                  value={extensionOdometer}
                  onChange={(e) => setExtensionOdometer(e.target.value)}
                  placeholder="e.g., 45230"
                  className="w-full"
                  data-testid="extension-odometer-input"
                />
              </div>
            )}
            
            {/* Odometer Photo Upload */}
            {extensionPricing && (
              <div>
                <label className="block text-sm font-medium mb-2">Odometer Photo *</label>
                {extensionOdometerPhoto ? (
                  <div className="relative">
                    <img 
                      src={extensionOdometerPhoto} 
                      alt="Odometer" 
                      className="w-full h-32 object-cover rounded-lg border border-slate-200"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-1 right-1 bg-white/80"
                      onClick={() => {
                        setExtensionOdometerPhoto(null);
                        setExtensionOdometerPhotoUrl('');
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div 
                    className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors"
                    onClick={() => document.getElementById('extension-odometer-photo')?.click()}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleExtensionOdometerPhotoUpload(file);
                        }
                        // Reset input so same file can be selected again
                        e.target.value = '';
                      }}
                      className="hidden"
                      id="extension-odometer-photo"
                      disabled={extensionPhotoUploading}
                    />
                    <div className="flex flex-col items-center gap-2">
                      {extensionPhotoUploading ? (
                        <>
                          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                          <span className="text-sm text-blue-600 font-medium">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Camera className="w-8 h-8 text-slate-400" />
                          <span className="text-sm text-slate-600 font-medium">Tap to take photo</span>
                          <span className="text-xs text-slate-400">or select from gallery</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Licence Declaration */}
            {extensionPricing && (
              <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg p-3">
                <input
                  type="checkbox"
                  id="extension-licence"
                  checked={extensionLicenceValid}
                  onChange={(e) => setExtensionLicenceValid(e.target.checked)}
                  className="mt-0.5"
                  data-testid="extension-licence-checkbox"
                />
                <label htmlFor="extension-licence" className="text-xs text-green-800">
                  I declare my driver licence remains valid, is not suspended, and I am legally permitted to drive this vehicle.
                </label>
              </div>
            )}
            
            {/* P0 FIX: Post-hire notice (replaces toll acknowledgment) */}
            {extensionPricing && (
              <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-600">
                  Tolls, fuel, cleaning, and damages are handled after the trip through the normal post-hire process. Extension only extends your booking time.
                </p>
              </div>
            )}
            
            {/* Payment Confirmation */}
            {extensionPricing && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <input
                  type="checkbox"
                  id="extension-confirm"
                  checked={extensionConfirmed}
                  onChange={(e) => setExtensionConfirmed(e.target.checked)}
                  className="mt-0.5"
                  data-testid="extension-confirm-checkbox"
                />
                <label htmlFor="extension-confirm" className="text-xs text-amber-800">
                  I understand that if the host approves, I must pay <strong>${extensionPricing.total?.toFixed(2)} AUD</strong> to activate the extension.
                </label>
              </div>
            )}
            
            {/* Bond Notice */}
            {extensionPricing && extensionPricing.extra_days >= 7 && booking?.bond_mode && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-purple-800">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm font-medium">Bond Notice</span>
                </div>
                <p className="text-xs text-purple-700 mt-1">
                  This extension makes your total hire 7+ days. Your security bond will be captured rather than held.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0 gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowExtensionModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitExtension}
              disabled={
                extensionLoading || 
                !extensionDate || 
                !extensionPricing ||
                !extensionOdometer ||
                !extensionOdometerPhotoUrl ||
                !extensionLicenceValid ||
                !extensionConfirmed ||
                extensions.some(ext => ['PENDING', 'APPROVED_AWAITING_PAYMENT'].includes(ext.status))
              }
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-extension-btn"
            >
              {extensionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Extension Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingDetails;
